/**
 * Registry for the named head-to-head comparison pages —
 * /clear-talent-vs-<competitor>.
 *
 * Mirrors the conventions of `in-practice.ts` and `solution-pages.ts`: one typed
 * entry per page, schema emitted through `getSchemaForHeadToHead()`, rendering
 * handled by a single template. Adding an entry here is step one of four — it
 * also needs a route in `MarketingRoutes.tsx` and `MarketingRoutes.server.tsx`,
 * a path in `prerenderRoutes`, and SEO resolution in `seo/siteSeo.ts`.
 *
 * WHY THESE PAGES EXIST
 * ---------------------
 * Buyers compare named products: "CLEAR Talent vs Lattice", not "best
 * performance management software". Search results for those named queries are
 * currently entirely third-party listicles and competitor-owned comparison hubs.
 * /best-performance-management-software is a generic buyer's guide against a
 * different vendor set (Workday, SAP, BambooHR, Darwinbox, Sprout) and does not
 * answer a named head-to-head, so these pages take that intent and link back to
 * it as their hub.
 *
 * HOW THESE DIFFER FROM /lattice-alternative
 * ------------------------------------------
 * /lattice-alternative is a paid campaign destination targeting "alternative"
 * intent, orphaned by design and self-canonical. These pages target "vs" intent,
 * are editorial, and are linked from the buyer's guide. They deliberately avoid
 * the word "alternative" in titles and headings so the two do not compete, and
 * they do not link to the campaign page — campaign landing pages stay orphaned.
 * It is also not a redirect candidate: it reads click IDs and UTM parameters for
 * live ads, so it keeps its URL until marketing repoints those destinations.
 *
 * ONE CLUSTER
 * -----------
 * Every page links to the hub and to every other head-to-head in its "Keep
 * reading" block, so a buyer weighing several vendors can move between them
 * without going back to the guide. A new entry goes into each sibling's
 * `internalLinks` as well as its own; `seo/siteSeo.test.ts` fails otherwise.
 *
 * THE HRIS MODULE PAGES
 * ---------------------
 * /clear-talent-vs-personio, /clear-talent-vs-hibob and /clear-talent-vs-bamboohr
 * compare CLEAR Talent with the performance module inside an HRIS the buyer
 * usually already runs. The honest question there is not "which product" but
 * "is the module we own enough, or do we add a layer beside it", so they set
 * `shortAnswer` to that framing, mark the competitor's core HR `strong` (it is
 * the product), and carry an `integrationNote` that says plainly there is no
 * prebuilt connector — the same statement `constants/integrations.ts` makes.
 *
 * THE PEOPLE-PLATFORM PAGES
 * --------------------------
 * /clear-talent-vs-leapsome and /clear-talent-vs-culture-amp compare CLEAR
 * Talent with broad people platforms that put engagement surveys beside
 * performance. Their competitor cells restate only the positioning the segment
 * guide and the OKR and 360 roundups already publish for the same vendors, and
 * each page states CLEAR Talent's survey boundary in a row of its own.
 *
 * THE CLEARCOMPANY PAGE
 * ---------------------
 * /clear-talent-vs-clearcompany is a comparison and a disambiguation at once:
 * the two names are close enough that a search for one returns the other. Its
 * `notice` says plainly that the companies are separate before the comparison
 * starts. Name ClearCompany only. "ClearCo" / "Clearco" is never presented as
 * another name for it — the page says only that CLEAR Talent has no connection
 * to any business using that name, which is true whoever uses it — and nothing
 * is asserted about ClearCompany's size, ownership or branding.
 *
 * WHAT MAY BE ASSERTED HERE
 * -------------------------
 * The CLEAR Talent column states what the platform does today and is shared
 * across every page from `CLEAR_CAPABILITY`, so the pages cannot contradict
 * each other about our own product. The competitor column never claims a gap:
 * `strong` is used only where a capability is unambiguously part of that
 * vendor's own public positioning, and `confirm` everywhere else, with the
 * specific question to put to them. No competitor is marked `limited` or `none`,
 * no competitor pricing figure is quoted, and no `Review` or `AggregateRating`
 * markup is emitted for anyone.
 *
 * THE SEGMENT GUIDE
 * -----------------
 * `SEGMENT_GUIDE` at the foot of this file is the cluster's one page by company
 * size rather than by named rival — /best-performance-management-software-for-small-business.
 * It lives here rather than in a registry of its own because it has to say the
 * same things about our own product and our own price as the head-to-heads, and
 * the same rules bind it: competitor cells are `strong` or `confirm` only, no
 * competitor price, no rating markup, and no link to a campaign page. It hangs
 * off the same hub, links to every head-to-head, and each head-to-head links to
 * it.
 *
 * THE SEGMENT SIBLINGS
 * --------------------
 * `SEGMENT_SIBLING_GUIDES` — /best-performance-management-software-for-startups,
 * -for-enterprise and -for-remote-teams — copy the small-business guide's shape,
 * page and rules for three other kinds of company, each with criteria of its own.
 * They link to each other, to the small-business guide and to the hub, and the
 * hub and the small-business guide link back.
 *
 * THE CATEGORY ROUNDUPS
 * ---------------------
 * `CATEGORY_GUIDES` — /best-okr-software and /best-360-feedback-tools — reuse the
 * segment guide's shape and page for one capability each, under the same rules.
 * Every guide is listed in `SEGMENT_GUIDES`, which is what both route tables, the
 * prerender list, the sitemap dates and `seo/siteSeo.ts` read.
 */

import type { ComparisonStatus } from './comparison-status';
import {
  ANNUAL_DISCOUNT_PERCENT,
  BASIC_TIER,
  ENTERPRISE_ONLY_MODULES,
  PRO_TIER,
  PUBLISHED_STAFF_CAP,
  STARTING_PRICE_SENTENCE,
  TRIAL_POLICY,
  monthlyListPrice,
  type PublishedTier,
} from './pricing';
import { breadcrumbListSchema, type BreadcrumbItem } from '../seo/breadcrumbs';

const SITE_ORIGIN = 'https://www.clear-talent.com';
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const SOFTWARE_ID = `${SITE_ORIGIN}/#software`;

/** The buyer's guide these pages hang off, in the breadcrumb and the link block. */
export const HEAD_TO_HEAD_HUB_PATH = '/best-performance-management-software';

/** The by-company-size guide — see `SEGMENT_GUIDE` below. */
export const SEGMENT_GUIDE_PATH = '/best-performance-management-software-for-small-business';

/** Its siblings for other kinds of company — see `SEGMENT_SIBLING_GUIDES` below. */
export const STARTUP_GUIDE_PATH = '/best-performance-management-software-for-startups';
export const ENTERPRISE_GUIDE_PATH = '/best-performance-management-software-for-enterprise';
export const REMOTE_TEAMS_GUIDE_PATH = '/best-performance-management-software-for-remote-teams';

/** The by-category roundups — see `CATEGORY_GUIDES` below. */
export const OKR_SOFTWARE_GUIDE_PATH = '/best-okr-software';
export const FEEDBACK_360_GUIDE_PATH = '/best-360-feedback-tools';

export const HEAD_TO_HEAD_REVIEWED_DATE = 'August 27, 2026';
export const HEAD_TO_HEAD_REVIEWED_DATE_ISO = '2026-08-27';

/**
 * Rendered above the capability table on every page. The asymmetry is real and
 * stating it plainly is what makes the rest of the table worth reading.
 */
export const HEAD_TO_HEAD_DISCLAIMER =
  'We can describe CLEAR Talent precisely, because we build it. We cannot describe another vendor’s current packaging with the same authority, and pretending otherwise would make this page useless to you. So the CLEAR Talent column states what the platform does today, and the other column gives you the question to put to them — never a claim about a gap.';

export type CapabilityGroup = 'Performance depth' | 'Reach and governance' | 'Scope boundaries';

export interface CapabilityCell {
  status: ComparisonStatus;
  detail: string;
}

export interface CapabilityRow {
  requirement: string;
  group: CapabilityGroup;
  clear: CapabilityCell;
  rival: CapabilityCell;
}

export interface HeadToHeadFaq {
  q: string;
  a: string;
}

export interface HeadToHead {
  slug: string;
  /** Competitor display name, exactly as they spell it. */
  competitor: string;
  /** <title> and og:title — under 60 characters. */
  seoTitle: string;
  /** 150–160 characters. */
  metaDescription: string;
  primaryKeyword: string;
  keywords: string[];
  eyebrow: string;
  /** Visible H1 — the exact string a buyer searches for. */
  h1: string;
  excerpt: string;
  heroImage: string;
  heroImageAlt: string;
  intro: string[];
  /** How the competitor describes itself, stated neutrally. */
  rivalPositioning: string;
  chooseClear: string[];
  chooseRival: string[];
  rows: CapabilityRow[];
  pricing: {
    clear: string;
    rival: string;
    questions: string[];
  };
  /** What CLEAR Talent deliberately does not do, framed for this comparison. */
  boundaries: string[];
  faqs: HeadToHeadFaq[];
  sources: { label: string; href: string }[];
  internalLinks: { label: string; href: string }[];
  /**
   * A plain statement rendered above the intro, for a comparison where the
   * reader may be on the wrong company's site altogether.
   */
  notice?: { title: string; text: string };
  /**
   * When this page's vendor evidence was last checked, for a page added or
   * revised after `HEAD_TO_HEAD_REVIEWED_DATE`. Read it through
   * `headToHeadReviewed()`.
   */
  reviewed?: { label: string; iso: string };
  /**
   * Replaces the default "Choose CLEAR Talent when / Consider <competitor>
   * when" framing of the short-answer section. Used where the competitor is an
   * HRIS whose performance module the buyer usually already owns, so the real
   * decision is whether to add a layer rather than which product to buy.
   */
  shortAnswer?: { title: string; sub: string; clearLabel: string; rivalLabel: string };
  /**
   * How the two products exchange data, rendered after the capability table.
   * Must restate only what `constants/integrations.ts` says ships.
   */
  integrationNote?: { title: string; paragraphs: string[]; link: { label: string; href: string } };
}

export const headToHeadPath = (entry: HeadToHead) => `/${entry.slug}`;

/**
 * The review date one page shows, marks up and reports to the sitemap. A page
 * carries its own only when it was checked on a different day from the rest;
 * moving the shared date instead would claim a review of pages nobody reread.
 */
export const headToHeadReviewed = (entry: HeadToHead): { label: string; iso: string } =>
  entry.reviewed ?? { label: HEAD_TO_HEAD_REVIEWED_DATE, iso: HEAD_TO_HEAD_REVIEWED_DATE_ISO };

/**
 * The CLEAR Talent side of every shared row, declared once. Three pages
 * describing our own product in three slightly different ways is how a
 * comparison set starts contradicting itself.
 */
type CapabilityKey =
  | 'goals'
  | 'reviews'
  | 'competencies'
  | 'feedback360'
  | 'development'
  | 'ai'
  | 'global'
  | 'coreHr';

const CLEAR_CAPABILITY: Record<
  CapabilityKey,
  { requirement: string; group: CapabilityGroup; clear: CapabilityCell }
> = {
  goals: {
    requirement: 'Organisation goals, OKRs and weighted KPIs',
    group: 'Performance depth',
    clear: {
      status: 'strong',
      detail:
        'Strategic Linked Goals cascades organisation objectives to team and individual level and requires every individual goal to connect to a parent, so orphaned goals cannot quietly accumulate. A KPI library holds reusable, role-aware metric templates with performance bands, weightings and guardrails.',
    },
  },
  reviews: {
    requirement: 'Review cycles and configuration',
    group: 'Performance depth',
    clear: {
      status: 'strong',
      detail:
        'Configurable self, manager, peer and multi-stage cycles, with configurable review templates and rating scales. Each stage carries its own deadline and automated reminders, quarterly and annual cadences can run side by side, and a closed period stays closed.',
    },
  },
  competencies: {
    requirement: 'Competency frameworks and evidence',
    group: 'Performance depth',
    clear: {
      status: 'strong',
      detail:
        'A central library with behavioural indicators at every proficiency level, scales configurable from three to seven levels that can differ by job family, and role profiles carrying required competencies and target levels. Ratings can be configured to require written evidence before submission.',
    },
  },
  feedback360: {
    requirement: '360° multi-rater feedback',
    group: 'Performance depth',
    clear: {
      status: 'strong',
      detail:
        'Rater groups are configurable across managers, peers, direct reports and cross-functional stakeholders, raters are anonymised within their group and minimum-rater thresholds apply. Input is structured against the competencies in the role profile rather than collected as open text.',
    },
  },
  development: {
    requirement: 'Development, calibration and succession',
    group: 'Performance depth',
    clear: {
      status: 'strong',
      detail:
        'Skills-gap analysis feeds individual development plans, calibration runs before ratings go final, and 9-box, readiness and succession workflows sit on the same record. Individual, team and organisation reports export to PDF or Excel in one click.',
    },
  },
  ai: {
    requirement: 'AI assistance and its governance controls',
    group: 'Reach and governance',
    clear: {
      status: 'strong',
      detail:
        'Ava drafts review narrative, summarises multi-rater input into themes, generates competency frameworks and prompts reviewers on possible rating patterns. Output is labelled, editable and approved by a person, every edit is logged, and it can be disabled per cycle, team or module.',
    },
  },
  global: {
    requirement: 'Mobile and multilingual participation',
    group: 'Reach and governance',
    clear: {
      status: 'strong',
      detail:
        'Web plus native iOS and Android apps with biometric sign-in, push notifications and offline support for sites where the signal drops, and 16 languages — with AI-generated summaries and recommendations delivered in each employee’s preferred language.',
    },
  },
  coreHr: {
    requirement: 'Payroll, attendance and applicant tracking',
    group: 'Scope boundaries',
    clear: {
      status: 'none',
      detail:
        'Not a CLEAR Talent capability, deliberately. The platform is built to run alongside the HRIS or payroll system you already have rather than replace it, which is what keeps the implementation narrow.',
    },
  },
};

const sharedRow = (key: CapabilityKey, rival: CapabilityCell): CapabilityRow => ({
  ...CLEAR_CAPABILITY[key],
  rival,
});

const PRICING_QUESTIONS = [
  'Which modules are inside the quoted price, and which are licensed separately?',
  'Is the price per employee, per active user, or per licence — and how are leavers treated mid-term?',
  'What is the minimum seat count, and what happens to it if headcount falls?',
  'What is the implementation fee, what does it cover, and what is billed hourly afterwards?',
  'What is the contract term, and what is the uplift at renewal?',
];

/**
 * What CLEAR Talent costs, declared once — for the same reason `CLEAR_CAPABILITY`
 * is. Three pages describing our own commercials three slightly different ways is
 * how a comparison set starts contradicting itself, and the price is the claim a
 * buyer is most likely to hold us to. The figures come from `constants/pricing.ts`,
 * which is also what the pricing page renders.
 */
const CLEAR_PRICING_STATEMENT =
  `${STARTING_PRICE_SENTENCE} A final quote is confirmed at demo; modules, localisation, implementation scope, taxes and commercial terms all move the number.`;

/** The shared opening of every "how do the two compare on price?" answer. */
const CLEAR_PRICE_FAQ_OPENING =
  `CLEAR Talent publishes a list price you can plan against before the first sales conversation: USD ${BASIC_TIER.monthlyUsd} per month for the Basic plan, covering ${BASIC_TIER.includedAdmins} admin user and ${BASIC_TIER.includedStaff} staff users, with each additional staff user at USD ${BASIC_TIER.extraStaffUsd} per month, and USD ${PRO_TIER.monthlyUsd} per month for Pro. Past what those plans cover, the Enterprise plan is quoted.`;

/**
 * Shared by the HRIS module pages (Personio, HiBob, BambooHR). The buyer usually
 * already runs the HRIS, so the decision is whether to add a layer beside it.
 */
const HRIS_REVIEWED = { label: 'October 5, 2026', iso: '2026-10-05' };

/**
 * Shared by the Leapsome and Culture Amp pages, added after the shared review
 * date. Both are broad people platforms with engagement surveys inside, so each
 * page leads with that boundary rather than burying it in the table.
 */
const PEOPLE_PLATFORM_REVIEWED = { label: 'October 10, 2026', iso: '2026-10-10' };

const hrisShortAnswer = (competitor: string): NonNullable<HeadToHead['shortAnswer']> => ({
  title: `Is the ${competitor} module enough, or do you add a layer?`,
  sub: `Most teams weighing CLEAR Talent against ${competitor} already run ${competitor} as their HRIS. The decision is rarely which one to buy — it is whether the performance module you have is enough, or whether a specialist layer belongs beside it.`,
  clearLabel: 'Add CLEAR Talent when',
  rivalLabel: `The ${competitor} module is enough when`,
});

/** Restates `constants/integrations.ts` — no prebuilt connector, import, API and export. */
const hrisIntegrationNote = (competitor: string): NonNullable<HeadToHead['integrationNote']> => ({
  title: `Running CLEAR Talent beside ${competitor}`,
  paragraphs: [
    `There is no prebuilt connector between CLEAR Talent and ${competitor} today, so nothing syncs automatically in the background. ${competitor} stays the system of record for the employee: people and organisation structure — departments, locations, roles and reporting lines — come into CLEAR Talent through CSV or Excel bulk import, or through API access where a file is not enough.`,
    `In the other direction, performance records come out of CLEAR Talent as CSV, Excel or PDF on every plan, so a calibration summary can go to a remuneration committee as a document and the underlying ratings can go back into ${competitor} as a spreadsheet. When someone changes team, site or manager, a staff transfer moves them without rebuilding the structure, and a fresh import after a reorganisation keeps the two org charts close.`,
    `Before you sign, ask ${competitor} which people and organisation fields its exports or API expose, and agree who re-imports after a reorganisation. If a live connection is a hard requirement, raise it in the first conversation with us: enterprise integrations are scoped per rollout. There is also no SAML single sign-on or SCIM provisioning today, so people sign in with their own CLEAR Talent credentials.`,
  ],
  link: { label: 'See exactly how data gets in and out', href: '/integrations' },
});

const hrisDataRow = (competitor: string): CapabilityRow => ({
  requirement: 'Data exchange with your HRIS',
  group: 'Reach and governance',
  clear: {
    status: 'limited',
    detail:
      'No prebuilt connector to any named HRIS, so nothing syncs automatically. People and structure come in by CSV or Excel bulk import or API access, and performance records go out as CSV, Excel or PDF on every plan.',
  },
  rival: {
    status: 'confirm',
    detail: `Ask which people and organisation fields ${competitor} can export to CSV or Excel or expose through its API, and which performance tools it already exchanges data with.`,
  },
});

const HRIS_BOUNDARIES = [
  'The employee record. CLEAR Talent holds performance and development data; your HRIS remains the system of record.',
  'Payroll, time and attendance, or absence and leave administration.',
  'Recruitment and applicant tracking.',
  'Employee engagement or pulse surveys.',
  'A prebuilt, automatically syncing connector to any named HRIS. Data moves by import, API access and export.',
];

export const HEAD_TO_HEAD_COMPARISONS: HeadToHead[] = [
  // ─────────────────────────────────────────────
  // 1. Lattice
  // ─────────────────────────────────────────────
  {
    slug: 'clear-talent-vs-lattice',
    competitor: 'Lattice',
    seoTitle: 'CLEAR Talent vs Lattice: Performance Software Compared',
    metaDescription:
      'Compare CLEAR Talent and Lattice on competency depth, review configuration, AI governance, mobile reach and published pricing — plus exactly what to ask.',
    primaryKeyword: 'CLEAR Talent vs Lattice',
    keywords: [
      'Lattice comparison',
      'Lattice vs CLEAR Talent',
      'performance management software comparison',
      'competency management software',
      'performance review software comparison',
    ],
    eyebrow: 'Head-to-head',
    h1: 'CLEAR Talent vs Lattice',
    excerpt:
      'Both run reviews, goals and feedback. They diverge on how a rating is evidenced, how far the platform reaches beyond performance, and how much of your existing HR stack you keep.',
    heroImage: '/screenshots/06-competencies-main.webp',
    heroImageAlt: 'Competency library with behavioural indicators in CLEAR Talent',
    intro: [
      'Lattice and CLEAR Talent turn up on the same shortlist because they answer the same first question — how do we run reviews and goals without a spreadsheet — and then answer the second one very differently. Lattice has grown outwards, adding engagement and core HR alongside the performance product. CLEAR Talent has grown downwards, into the evidence behind a rating.',
      'That is the decision this page is really about. If you want one vendor covering performance, engagement and increasingly the employee record, that argues for breadth. If your problem is that ratings are not defensible and the competency model lives in a document nobody is actually assessed against, that argues for depth. Neither is the better product in the abstract, and a demo that only shows review cycles will not tell you which you need.',
    ],
    rivalPositioning:
      'Lattice positions itself as a people platform: performance reviews, goals and OKRs, 1:1s, engagement surveys, career and development tracks and analytics — and it has extended beyond performance into core HR. It is widely adopted by fast-growing companies that want one supplier across those areas. Module packaging changes, so confirm what your quoted tier actually includes directly with Lattice.',
    chooseClear: [
      'A rating has to hold up to a sceptical reader later. Self, assessor and final ratings are stored separately with their own comments, and competency ratings can require written evidence at the point of assessment.',
      'Competencies are the framework, not a tag. Scales are configurable from three to seven levels, can differ by job family, and every competency carries behavioural indicators at each level.',
      'You want the parts of a rating to stay separable — goals and KPIs as one stream, competency assessment as another, with the weighting between them configurable per level.',
      'The workforce is global, deskless or not English-first: 16 languages, native iOS and Android with biometric sign-in, push notifications and offline support.',
      'You are keeping your HRIS and payroll. CLEAR Talent is designed to sit alongside them, which is what keeps the rollout to weeks rather than a programme.',
    ],
    chooseRival: [
      'Employee engagement surveys are part of the purchase. CLEAR Talent has no survey product at all — if you need one, that is a genuine gap and Lattice covers it.',
      'You want the shortest possible vendor list and are willing to consolidate performance, engagement and parts of core HR with one supplier.',
      'Your competency model is deliberately light, and gating ratings behind written evidence would be more process than your organisation wants.',
      'Your managers have used Lattice before. Familiarity is a legitimate adoption advantage, and evaluations that discount it tend to overstate how quickly any new tool lands.',
    ],
    rows: [
      sharedRow('goals', {
        status: 'strong',
        detail:
          'Goals and OKRs are a headline Lattice capability. Ask how an individual goal is tied to a parent objective, whether that link can be enforced rather than encouraged, and how weighted KPIs with performance bands are modelled.',
      }),
      sharedRow('reviews', {
        status: 'strong',
        detail:
          'Review cycles are core to the product. Ask to see a multi-stage cycle built with your own stages, templates and rating scale, and confirm how much of that an administrator can change without vendor support.',
      }),
      sharedRow('competencies', {
        status: 'confirm',
        detail:
          'Ask how a competency is defined, whether behavioural indicators exist at each level, how many levels a scale can carry, whether scales can differ by job family, and whether a rating can be blocked until written evidence is attached.',
      }),
      sharedRow('feedback360', {
        status: 'strong',
        detail:
          'Multi-rater input is part of Lattice review cycles. Ask how rater groups are configured, what the anonymity and minimum-rater rules are, and whether input is structured against a competency model or gathered as open text.',
      }),
      sharedRow('development', {
        status: 'confirm',
        detail:
          'Ask which of development planning, calibration, 9-box and succession are in your tier, which are licensed separately, and what the export looks like for a remuneration or talent committee.',
      }),
      sharedRow('ai', {
        status: 'confirm',
        detail:
          'Ask what the AI writes, what it reads to write it, whether the output is labelled as AI-generated, whether it can be switched off for a cycle or a team, and what is recorded when a manager edits it.',
      }),
      sharedRow('global', {
        status: 'confirm',
        detail:
          'Ask which languages are supported and have them demonstrate one full review completed on a phone, by someone who has no desk and no company laptop.',
      }),
      {
        requirement: 'Employee engagement surveys',
        group: 'Scope boundaries',
        clear: {
          status: 'none',
          detail:
            'Not a CLEAR Talent capability. If engagement or pulse surveys are a required part of this purchase, CLEAR Talent does not cover it and you would need a second tool.',
        },
        rival: {
          status: 'strong',
          detail:
            'Engagement surveys are a core part of the Lattice platform. Confirm question libraries, benchmarking, reporting depth and how survey results connect to the performance record.',
        },
      },
      sharedRow('coreHr', {
        status: 'confirm',
        detail:
          'Lattice has extended beyond performance into core HR. Confirm what its current HRIS scope covers, what it does not, and how that would sit with the system of record you already run.',
      }),
    ],
    pricing: {
      clear: CLEAR_PRICING_STATEMENT,
      rival:
        'Read Lattice’s current pricing page, then confirm exactly which modules the tier you are quoted includes before comparing per-seat figures. An unmatched module list is the usual reason a price comparison misleads.',
      questions: PRICING_QUESTIONS,
    },
    boundaries: [
      'Employee engagement or pulse surveys.',
      'Payroll, time and attendance, or leave administration.',
      'Recruitment and applicant tracking.',
      'A learning management system. Development plans link to learning; delivering courses is not a CLEAR Talent capability.',
    ],
    faqs: [
      {
        q: 'Is CLEAR Talent a direct replacement for Lattice?',
        a: 'Only for the performance side. CLEAR Talent covers goals and KPIs, review cycles, 360° feedback, competency assessment, development, calibration and succession, with analytics across all of it. It does not include employee engagement surveys, and it is not a core HR system — it is built to run alongside the HRIS and payroll platform you already have. Map your current usage against those boundaries before assuming a like-for-like swap.',
      },
      {
        q: 'What is the main functional difference between them?',
        a: 'How a rating is built and evidenced. CLEAR Talent holds goals and KPIs as one stream and competency assessment as another, applies a weighting between them that is configurable per level, and stores self-rating, assessor rating and final rating separately with their own comments. Competency ratings can be configured to require written evidence before submission, so a rating can be decomposed years later rather than read as a single number.',
      },
      {
        q: 'Can we migrate our existing review data?',
        a: 'CLEAR Talent supports API access plus CSV and Excel bulk import for structured data such as goals, ratings, and organisation structure including departments, locations, roles and reporting lines. What transfers cleanly depends on how your current data is shaped, so migration scope is confirmed during discovery rather than promised up front.',
      },
      {
        q: 'How do the two compare on price?',
        a: `${CLEAR_PRICE_FAQ_OPENING} For Lattice, read their current pricing page and then write down exactly what the tier you are quoted includes. Note that the two are not priced the same way — ours is a plan fee with per-user add-ons, theirs is quoted per seat — so convert both to a monthly cost for your own headcount before comparing. Comparing headline prices without matching the module list, the headcount and the term is the most common way a software price comparison goes wrong.`,
      },
      {
        q: 'How should we run the evaluation fairly?',
        a: 'Write your scenarios first — a full review cycle with your stages, a competency assessment with evidence attached, a calibration session, a 360° with your own rater rules — and make every vendor demonstrate the same ones live rather than presenting their standard demo. Score against the outcomes you need instead of feature-list length, and put the managers who will actually complete the cycle in the room.',
      },
    ],
    sources: [
      { label: 'Lattice (vendor site)', href: 'https://lattice.com/' },
      { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
      {
        label: 'CLEAR Talent buyer’s comparison',
        href: 'https://www.clear-talent.com/best-performance-management-software',
      },
    ],
    internalLinks: [
      { label: 'Competency management software', href: '/solutions/competency-management-software' },
      { label: 'Employee performance review software', href: '/solutions/employee-performance-review-software' },
      { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
      { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
      { label: 'CLEAR Talent vs ClearCompany', href: '/clear-talent-vs-clearcompany' },
      { label: 'CLEAR Talent vs Personio', href: '/clear-talent-vs-personio' },
      { label: 'CLEAR Talent vs HiBob', href: '/clear-talent-vs-hibob' },
      { label: 'CLEAR Talent vs BambooHR', href: '/clear-talent-vs-bamboohr' },
      { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
      { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
      { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
      { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
    ],
  },

  // ─────────────────────────────────────────────
  // 2. 15Five
  // ─────────────────────────────────────────────
  {
    slug: 'clear-talent-vs-15five',
    competitor: '15Five',
    seoTitle: 'CLEAR Talent vs 15Five: Performance Software Compared',
    metaDescription:
      'Compare CLEAR Talent and 15Five: weekly check-ins against evidence-backed competency ratings, review configuration, mobile reach, AI governance and price.',
    primaryKeyword: 'CLEAR Talent vs 15Five',
    keywords: [
      '15Five comparison',
      '15Five vs CLEAR Talent',
      'continuous performance management',
      'competency assessment software',
      'performance management software comparison',
    ],
    eyebrow: 'Head-to-head',
    h1: 'CLEAR Talent vs 15Five',
    excerpt:
      '15Five is built around a weekly rhythm and better managers. CLEAR Talent is built around what a rating is made of. Which matters more depends on which problem you actually have.',
    heroImage: '/screenshots/12-feedback-main.webp',
    heroImageAlt: 'Continuous feedback and check-in view in CLEAR Talent',
    intro: [
      '15Five’s design starts from a habit: a short weekly check-in, a regular 1-on-1, and managers trained to run both well. Goals, reviews and engagement hang off that rhythm. It is a coherent answer to a real problem — most performance processes are an annual event attached to nothing that happens in between.',
      'CLEAR Talent starts somewhere else: with the standard people are assessed against, and the evidence behind each rating. It supports continuous feedback and check-ins between formal cycles, but the centre of gravity is the competency framework, the goal and KPI streams, and a review record that can be interrogated afterwards. If your managers already meet their people regularly and the problem is that ratings do not hold up, that is the difference that matters.',
    ],
    rivalPositioning:
      '15Five positions itself around continuous performance and manager enablement: weekly check-ins, 1-on-1s, OKRs, performance reviews, engagement surveys, and manager coaching and training content. A substantial part of its value proposition is behavioural — building a cadence and developing managers — rather than purely functional. Packaging changes, so confirm what your quoted tier includes directly with 15Five.',
    chooseClear: [
      'Competency assessment is the core requirement: behavioural indicators at every level, scales from three to seven points that can differ by job family, and role profiles carrying required competencies and target levels.',
      'Ratings need evidence attached. Competency ratings can require written evidence, and self, assessor and final ratings are held separately with their own comments.',
      'You need a composite rating whose parts stay visible — goals and KPIs as one stream, competencies as another, weighted per level.',
      'The workforce is not desk-based or not English-first: 16 languages, native iOS and Android, offline support and push notifications.',
      'Calibration, 9-box and succession are part of this purchase rather than a later phase.',
    ],
    chooseRival: [
      'The real problem is manager capability. 15Five’s manager training and coaching content is a substantive part of what you are buying, and CLEAR Talent is not a course library.',
      'Employee engagement surveys are in scope. CLEAR Talent has no survey product.',
      'A weekly check-in habit is the outcome you want, and you would rather buy the cadence than build it.',
      'Your competency model is intentionally light, and structured assessment against behavioural indicators would be more process than the organisation will absorb.',
    ],
    rows: [
      sharedRow('goals', {
        status: 'strong',
        detail:
          'OKRs are a core 15Five capability. Ask how an individual objective is tied to a parent, whether that link can be enforced, and whether weighted KPIs with performance bands and guardrails can be modelled.',
      }),
      sharedRow('reviews', {
        status: 'strong',
        detail:
          'Review cycles are part of the product. Ask to see your own cycle stages, templates and rating scale configured live, and confirm how quarterly and annual cadences run together.',
      }),
      sharedRow('competencies', {
        status: 'confirm',
        detail:
          'Ask whether competencies carry behavioural indicators at each proficiency level, how many levels a scale supports, whether scales differ by job family, and whether evidence can be required before a rating is submitted.',
      }),
      sharedRow('feedback360', {
        status: 'confirm',
        detail:
          'Ask how rater groups are configured, what the anonymity and minimum-rater rules are, and whether the input maps to a competency model or is collected as open text.',
      }),
      sharedRow('development', {
        status: 'confirm',
        detail:
          'Ask which of development planning, calibration, 9-box and succession are in your tier and which are separate, and what a completed report looks like at team and organisation level.',
      }),
      sharedRow('ai', {
        status: 'confirm',
        detail:
          'Ask what the AI drafts, whether the output is labelled and editable, whether it can be disabled per team or per cycle, and what is recorded when someone changes it.',
      }),
      sharedRow('global', {
        status: 'confirm',
        detail:
          'Ask which languages are supported, and have them complete both a check-in and a full review on a phone in front of you rather than describing it.',
      }),
      {
        requirement: 'Manager training and coaching content',
        group: 'Scope boundaries',
        clear: {
          status: 'limited',
          detail:
            'CLEAR Talent gives managers coaching context assembled from goals, ratings and structured feedback, and Ava can prompt on possible rating patterns. It is not a course library and does not deliver training content.',
        },
        rival: {
          status: 'strong',
          detail:
            'Manager training and coaching content is a substantive part of what 15Five sells. Confirm whether it is included in your tier or priced separately, who it is licensed for, and how completion is tracked.',
        },
      },
      sharedRow('coreHr', {
        status: 'confirm',
        detail:
          'Confirm what sits inside 15Five and what you would still need from your HRIS or payroll platform, so the two quotes cover the same ground.',
      }),
    ],
    pricing: {
      clear: CLEAR_PRICING_STATEMENT,
      rival:
        'Read 15Five’s current pricing page, then confirm whether manager training and every other capability you need sits inside the tier you are quoted or is priced separately. Compare the module lists before you compare the per-seat figures.',
      questions: PRICING_QUESTIONS,
    },
    boundaries: [
      'Employee engagement or pulse surveys.',
      'Manager training courses or a learning management system. Development plans link to learning; delivering the content is not a CLEAR Talent capability.',
      'Payroll, time and attendance, or leave administration.',
      'Recruitment and applicant tracking.',
    ],
    faqs: [
      {
        q: 'Does CLEAR Talent support weekly check-ins?',
        a: 'Yes. Continuous feedback, check-ins and coaching context run between formal cycles, and quarterly and annual cycles can run side by side with cadences mixed per department or level. What CLEAR Talent does not ship is the manager training programme 15Five wraps around that habit — so if the cadence and the coaching curriculum are what you are buying, weigh that carefully.',
      },
      {
        q: 'We already run 1-on-1s well. What would CLEAR Talent add?',
        a: 'Structure behind the rating rather than around the conversation: competency frameworks with behavioural indicators at each level, role profiles carrying required competencies and target levels, evidence attached to ratings, calibration before ratings go final, and skills-gap analysis feeding development plans. If your managers already meet their people, that is where the remaining value is.',
      },
      {
        q: 'Which is better for a distributed or deskless workforce?',
        a: 'Compare the mobile experience directly, with the people who will use it. CLEAR Talent provides native iOS and Android apps with biometric sign-in, push notifications and offline support for sites where the signal drops, and supports 16 languages, with AI-generated summaries and recommendations delivered in each employee’s preferred language. Ask 15Five to demonstrate the same completion journey on a phone.',
      },
      {
        q: 'Can both platforms run 360° feedback?',
        a: 'Both offer multi-rater feedback; the question is how it is structured. In CLEAR Talent, rater groups are configurable across managers, peers, direct reports and cross-functional stakeholders, raters are anonymised within their group, minimum-rater thresholds apply, and input is collected against the competencies in the role profile rather than as free text. Ask each vendor to show a completed 360° report built from your own competency model.',
      },
      {
        q: 'How do the two compare on price?',
        a: `${CLEAR_PRICE_FAQ_OPENING} For 15Five, read their current pricing page and confirm whether manager training and the other capabilities you need are inside the tier you are quoted. Then compare like for like: convert both to a monthly cost at the same headcount, over the same term, covering the same module list — a plan fee with per-user add-ons and a per-seat quote are not comparable as headline figures.`,
      },
    ],
    sources: [
      { label: '15Five (vendor site)', href: 'https://www.15five.com/' },
      { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
      {
        label: 'CLEAR Talent buyer’s comparison',
        href: 'https://www.clear-talent.com/best-performance-management-software',
      },
    ],
    internalLinks: [
      { label: 'Continuous performance management', href: '/solutions/continuous-performance-management' },
      { label: 'Competency management software', href: '/solutions/competency-management-software' },
      { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
      { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
      { label: 'CLEAR Talent vs ClearCompany', href: '/clear-talent-vs-clearcompany' },
      { label: 'CLEAR Talent vs Personio', href: '/clear-talent-vs-personio' },
      { label: 'CLEAR Talent vs HiBob', href: '/clear-talent-vs-hibob' },
      { label: 'CLEAR Talent vs BambooHR', href: '/clear-talent-vs-bamboohr' },
      { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
      { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
      { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
      { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
    ],
  },

  // ─────────────────────────────────────────────
  // 3. Betterworks
  // ─────────────────────────────────────────────
  {
    slug: 'clear-talent-vs-betterworks',
    competitor: 'Betterworks',
    seoTitle: 'CLEAR Talent vs Betterworks: Software Compared',
    metaDescription:
      'Compare CLEAR Talent and Betterworks: enterprise goal programmes against a composite rating built from goals, KPIs and evidence-backed competency data.',
    primaryKeyword: 'CLEAR Talent vs Betterworks',
    keywords: [
      'Betterworks comparison',
      'Betterworks vs CLEAR Talent',
      'OKR software comparison',
      'enterprise goal management',
      'performance management software comparison',
    ],
    eyebrow: 'Head-to-head',
    h1: 'CLEAR Talent vs Betterworks',
    excerpt:
      'Betterworks came from enterprise goal and OKR programmes. CLEAR Talent treats a goal score as one half of a rating, with competency assessment as the other. That gap is the decision.',
    heroImage: '/screenshots/04-organizational-goals-main.webp',
    heroImageAlt: 'Organisational goal cascade in CLEAR Talent',
    intro: [
      'Betterworks built its reputation on goal and OKR management at enterprise scale — cascading objectives across large, complex organisations and keeping them visible — with conversations, feedback, reviews and calibration layered on top. If what you are running is an alignment programme, that heritage is exactly the point, and you should weight it heavily.',
      'CLEAR Talent takes goals seriously too. Strategic Linked Goals requires every individual goal to connect to a parent objective, and a KPI library holds reusable, role-aware metric templates with performance bands, weightings and guardrails. But it treats a goal score as one stream of a rating rather than the rating. The other stream is competency assessment against behavioural indicators, weighted per level. That is the difference worth putting to both vendors in a demo.',
    ],
    rivalPositioning:
      'Betterworks positions itself around enterprise goal and OKR management, with conversations, continuous feedback, performance reviews and calibration alongside it, and is typically deployed in large organisations running an alignment programme next to an existing HCM. Module scope, packaging and pricing all change — confirm the current position directly with Betterworks.',
    chooseClear: [
      'The rating needs a second stream. Competency assessment against behavioural indicators sits alongside goals and KPIs, with the weighting between them configurable per level.',
      'Goals should not be able to float. Strategic Linked Goals requires every individual goal to connect to a parent objective, so a team cannot spend a quarter optimising for something the organisation stopped asking for.',
      `You want a published price to plan against before the first sales conversation: USD ${BASIC_TIER.monthlyUsd} per month for the Basic plan, covering ${BASIC_TIER.includedAdmins} admin user and ${BASIC_TIER.includedStaff} staff users, with the per-user add-on rates published alongside it.`,
      'Rollout is measured in weeks, with a guided onboarding wizard tailored to organisation size, industry and performance-management maturity.',
      'The workforce is mobile or multilingual: native iOS and Android with offline support, and 16 languages.',
    ],
    chooseRival: [
      'The primary objective is an enterprise goal or OKR programme across a very large, complex organisation, and alignment at that scale is the outcome being bought.',
      'You already run a full HCM and want a goal layer beside it rather than a performance and development platform.',
      'Procurement is comfortable running a full enterprise tender and a structured implementation programme rather than starting from a published price.',
      'Engagement capability from the same vendor is in scope — confirm the current scope with them, because CLEAR Talent has no survey product.',
    ],
    rows: [
      sharedRow('goals', {
        status: 'strong',
        detail:
          'Goal and OKR management is the core of the product. Ask whether the link from an individual goal to its parent can be enforced rather than encouraged, how weighted KPIs with performance bands are modelled, and what happens to a cascade when a parent objective changes mid-cycle.',
      }),
      {
        requirement: 'Enterprise-scale goal programme management',
        group: 'Performance depth',
        clear: {
          status: 'available',
          detail:
            'Departments, locations, roles and reporting lines are first-class concepts, with bulk import, staff transfers, multi-entity support and role-based access with ownership checks. Scope your own scale, integration and governance requirements with us rather than inferring them from this page.',
        },
        rival: {
          status: 'strong',
          detail:
            'This is Betterworks’ heritage and its clearest strength. Ask for evidence of deployments at your headcount and structural complexity, what the implementation involves, and who administers the cascade once it is live.',
        },
      },
      sharedRow('reviews', {
        status: 'strong',
        detail:
          'Reviews and calibration are part of the platform. Ask to see your own cycle stages, templates and rating scale configured live, and what an administrator can change without vendor support.',
      }),
      sharedRow('competencies', {
        status: 'confirm',
        detail:
          'Ask whether competencies carry behavioural indicators at each level, how many levels a scale supports, whether scales can differ by job family, and whether written evidence can be required before submission.',
      }),
      sharedRow('feedback360', {
        status: 'confirm',
        detail:
          'Ask how rater groups, anonymity and minimum-rater thresholds are configured, and whether input is structured against a competency model or gathered as open text.',
      }),
      sharedRow('development', {
        status: 'confirm',
        detail:
          'Ask which of development planning, 9-box and succession sit in Betterworks and which would remain in your HCM, so the two systems do not both half-own the same record.',
      }),
      sharedRow('ai', {
        status: 'confirm',
        detail:
          'Ask what the AI produces, whether output is labelled and editable, whether it can be disabled per team or per cycle, and what is logged when it is changed.',
      }),
      sharedRow('global', {
        status: 'confirm',
        detail:
          'Ask which languages are supported and have them show a full review completed on a phone, not a responsive web page on a tablet.',
      }),
      sharedRow('coreHr', {
        status: 'confirm',
        detail:
          'Betterworks is usually deployed alongside an existing HCM. Confirm which parts of your process would remain in the HCM, and where the boundary sits for reporting.',
      }),
    ],
    pricing: {
      clear: CLEAR_PRICING_STATEMENT,
      rival:
        'Check Betterworks’ current pricing page, then ask for a written quote covering the same module list, the same headcount and the same term as every other vendor on the shortlist, with implementation costed separately and explicitly rather than folded into the subscription line.',
      questions: PRICING_QUESTIONS,
    },
    boundaries: [
      'Employee engagement or pulse surveys.',
      'Payroll, time and attendance, or leave administration.',
      'Recruitment and applicant tracking.',
      'A learning management system. Development plans link to learning; delivering courses is not a CLEAR Talent capability.',
    ],
    faqs: [
      {
        q: 'Is CLEAR Talent an OKR tool?',
        a: 'It manages OKRs, but it is not only an OKR tool. Organisation, team and individual goals cascade through Strategic Linked Goals, which requires every individual goal to connect to a parent objective, and a KPI library holds reusable, role-aware metric templates with performance bands, weightings and guardrails. Those goal scores then form one stream of a composite rating; competency assessment forms the other.',
      },
      {
        q: 'How do goals and competencies combine into one rating?',
        a: 'They stay separate until the composite is formed. Goals and KPIs are scored on one side, competencies assessed against behavioural indicators on the other, and the weighting between the two is configurable per level — so a control function and a revenue-generating role can sit on the same platform without being measured identically. Because both streams remain visible, a calibration session can interrogate one without disturbing the other.',
      },
      {
        q: 'We are a large organisation. Is CLEAR Talent enterprise-ready?',
        a: 'The governance controls a large organisation asks about are in place: role-based access with ownership checks so a manager works only within their own reporting line, an audit record that captures every rating, comment, approval and file access with the values before and after, and multi-entity support for groups that have grown by acquisition as well as by hiring. Reporting runs at individual, team and organisation level with one-click PDF or Excel export. Scale, integration and implementation scope are worth putting to us directly rather than inferring from this page.',
      },
      {
        q: 'What does a security review usually cover?',
        a: 'Google Cloud Platform hosting with continuous threat monitoring through Security Command Center Premium, end-to-end TLS, generated files served through signed URLs that expire after 24 hours, role-based access with ownership checks and logged access, GDPR support including DSAR exports and right-to-be-forgotten workflows, and an A+ Mozilla Observatory rating. CLEAR Talent holds employee performance data rather than payroll or customer data, which narrows the review considerably.',
      },
      {
        q: 'How do the two compare on price?',
        a: `${CLEAR_PRICE_FAQ_OPENING} For Betterworks, check their current pricing page and then ask for a written quote covering the same module list, headcount and term before comparing, with implementation costed separately so it does not disappear into the subscription line. Convert both to a monthly cost at your own headcount first: a plan fee with per-user add-ons and an enterprise quote are not comparable as headline figures.`,
      },
    ],
    sources: [
      { label: 'Betterworks (vendor site)', href: 'https://www.betterworks.com/' },
      { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
      {
        label: 'CLEAR Talent buyer’s comparison',
        href: 'https://www.clear-talent.com/best-performance-management-software',
      },
    ],
    internalLinks: [
      { label: 'OKR & goal setting software', href: '/solutions/okr-goal-setting-software' },
      { label: 'Competency management software', href: '/solutions/competency-management-software' },
      { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
      { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
      { label: 'CLEAR Talent vs ClearCompany', href: '/clear-talent-vs-clearcompany' },
      { label: 'CLEAR Talent vs Personio', href: '/clear-talent-vs-personio' },
      { label: 'CLEAR Talent vs HiBob', href: '/clear-talent-vs-hibob' },
      { label: 'CLEAR Talent vs BambooHR', href: '/clear-talent-vs-bamboohr' },
      { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
      { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
      { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
      { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
    ],
  },

  // ─────────────────────────────────────────────
  // 4. ClearCompany — a comparison and a disambiguation
  // ─────────────────────────────────────────────
  {
    slug: 'clear-talent-vs-clearcompany',
    competitor: 'ClearCompany',
    seoTitle: 'CLEAR Talent vs ClearCompany: Two Separate Products',
    metaDescription:
      'CLEAR Talent and ClearCompany are separate, unaffiliated HR software companies with similar names. See what each one covers and how to tell which you need.',
    primaryKeyword: 'CLEAR Talent vs ClearCompany',
    keywords: [
      'ClearCompany comparison',
      'ClearCompany vs CLEAR Talent',
      'difference between CLEAR Talent and ClearCompany',
      'performance management software comparison',
    ],
    eyebrow: 'Head-to-head · Separate companies',
    h1: 'CLEAR Talent vs ClearCompany',
    excerpt:
      'Two separate, unaffiliated companies with similar names. ClearCompany presents a talent platform that runs from hiring to performance; CLEAR Talent covers performance and development only, in more depth.',
    heroImage: '/screenshots/03-dashboard-main.webp',
    heroImageAlt: 'CLEAR Talent performance management dashboard',
    reviewed: { label: 'October 1, 2026', iso: '2026-10-01' },
    notice: {
      title: 'First, the plain answer: these are two different companies',
      text: 'CLEAR Talent is not ClearCompany. It is not owned by, part of or affiliated with ClearCompany, and the two products share no accounts, sign-in or support. If you came here looking for a ClearCompany login, its support team or its careers pages, you are on a different company’s website and should go to ClearCompany directly. If you are looking for CLEAR Talent — performance management software at www.clear-talent.com — you are in the right place.',
    },
    intro: [
      'CLEAR Talent and ClearCompany both make HR software, and the names share their first word. That is enough for a search for one to return the other, and for the two to be confused on shortlists, in review directories and in conversation. They are separate companies with separate products, so the first job of this page is to say so, and the second is to explain what each one is for.',
      'Once the names are untangled, the choice is fairly simple, because the two products are different shapes. ClearCompany describes a talent management platform that follows the employee from application to performance review. CLEAR Talent does one part of that span — performance and development — and puts its depth there: competency frameworks, evidence behind ratings, goals and KPIs, 360° feedback, calibration and development plans. If hiring is part of what you are buying, that matters a great deal. If it is not, the question becomes how much structure you want behind a rating.',
    ],
    rivalPositioning:
      'ClearCompany positions itself as a talent management platform covering the employee lifecycle: recruiting and applicant tracking, onboarding, employee engagement, performance management, and workforce planning and analytics. Its appeal is one supplier and one record across those stages. Product names, packaging and scope change, so confirm what is included in any quote directly with ClearCompany.',
    chooseClear: [
      'Performance and development is the whole purchase, and you already have an applicant tracking system or do not need one. CLEAR Talent is a performance and development layer built to run alongside the HR systems you keep.',
      'Competencies are the framework, not a tag. Scales are configurable from three to seven levels, can differ by job family, and every competency carries behavioural indicators at each level.',
      'A rating has to hold up to a sceptical reader later. Self, assessor and final ratings are stored separately with their own comments, and competency ratings can require written evidence at the point of assessment.',
      'The workforce is global, deskless or not English-first: 16 languages, native iOS and Android with biometric sign-in, push notifications and offline support.',
      `You want a published price to plan against before the first sales conversation: USD ${BASIC_TIER.monthlyUsd} per month for the Basic plan, covering ${BASIC_TIER.includedAdmins} admin user and ${BASIC_TIER.includedStaff} staff users, with the per-user add-on rates published alongside it.`,
    ],
    chooseRival: [
      'Recruiting is part of the purchase. CLEAR Talent has no recruitment or applicant tracking product at all — if you need one from the same supplier as performance, that is a genuine gap and ClearCompany covers it.',
      'You want one supplier across the employee lifecycle, from the first application through onboarding to performance, and are willing to consolidate on it.',
      'You already use ClearCompany for hiring, and adding its performance product is simpler for your team than introducing a second vendor. Familiarity is a legitimate adoption advantage.',
      'Your competency model is deliberately light, and gating ratings behind written evidence would be more process than your organisation wants.',
    ],
    rows: [
      sharedRow('goals', {
        status: 'confirm',
        detail:
          'Ask how goals are set and tracked inside the performance product, whether an individual goal can be required to link to a parent objective, and whether weighted KPIs with performance bands can be modelled.',
      }),
      sharedRow('reviews', {
        status: 'strong',
        detail:
          'Performance management is one of the products ClearCompany presents. Ask to see a multi-stage cycle built with your own stages, templates and rating scale, and confirm how much of that an administrator can change without vendor support.',
      }),
      sharedRow('competencies', {
        status: 'confirm',
        detail:
          'Ask how a competency is defined, whether behavioural indicators exist at each level, how many levels a scale can carry, whether scales can differ by job family, and whether a rating can be blocked until written evidence is attached.',
      }),
      sharedRow('feedback360', {
        status: 'confirm',
        detail:
          'Ask how rater groups are configured, what the anonymity and minimum-rater rules are, and whether input is structured against a competency model or gathered as open text.',
      }),
      sharedRow('development', {
        status: 'confirm',
        detail:
          'Ask which of development planning, calibration, 9-box and succession are included in your quote and which are separate, and what a completed report looks like at team and organisation level.',
      }),
      sharedRow('ai', {
        status: 'confirm',
        detail:
          'Ask what the AI writes, what it reads to write it, whether the output is labelled as AI-generated, whether it can be switched off for a cycle or a team, and what is recorded when a manager edits it.',
      }),
      sharedRow('global', {
        status: 'confirm',
        detail:
          'Ask which languages are supported and have them demonstrate one full review completed on a phone, by someone who has no desk and no company laptop.',
      }),
      {
        requirement: 'Recruitment and applicant tracking',
        group: 'Scope boundaries',
        clear: {
          status: 'none',
          detail:
            'Not a CLEAR Talent capability. If hiring and applicant tracking are a required part of this purchase, CLEAR Talent does not cover them and you would need a second tool.',
        },
        rival: {
          status: 'strong',
          detail:
            'Recruiting and applicant tracking is a headline part of the ClearCompany platform. Confirm what the hiring workflow covers, how a new hire’s record carries through into performance, and which products your quote includes.',
        },
      },
      {
        requirement: 'Employee engagement surveys',
        group: 'Scope boundaries',
        clear: {
          status: 'none',
          detail:
            'Not a CLEAR Talent capability. If engagement or pulse surveys are a required part of this purchase, CLEAR Talent does not cover it and you would need a second tool.',
        },
        rival: {
          status: 'confirm',
          detail:
            'Ask what the engagement product covers, whether surveys are included in your quote or priced separately, and how survey results connect to the performance record.',
        },
      },
      sharedRow('coreHr', {
        status: 'confirm',
        detail:
          'Applicant tracking is covered in the row above. Confirm what ClearCompany covers for payroll and time and attendance, and what you would still need from your HRIS or payroll platform, so the two quotes cover the same ground.',
      }),
    ],
    pricing: {
      clear: CLEAR_PRICING_STATEMENT,
      rival:
        'Ask ClearCompany for a written quote, and confirm exactly which products it covers before comparing it with a performance-only price. A quote that spans hiring, onboarding and performance and one that covers performance alone are not the same purchase.',
      questions: PRICING_QUESTIONS,
    },
    boundaries: [
      'Recruitment and applicant tracking.',
      'Employee engagement or pulse surveys.',
      'Payroll, time and attendance, or leave administration.',
      'A learning management system. Development plans link to learning; delivering courses is not a CLEAR Talent capability.',
    ],
    faqs: [
      {
        q: 'Is CLEAR Talent the same company as ClearCompany?',
        a: 'No. CLEAR Talent is a separate company with its own product and its own website, www.clear-talent.com. It is not affiliated with ClearCompany, a separate HR software company with a similar name, and it has no connection to any business using the name ClearCo or Clearco. If a search result, review listing or invoice carries one of those names, it does not refer to CLEAR Talent.',
      },
      {
        q: 'Why do searches for CLEAR Talent show results for ClearCompany?',
        a: 'The two names share their first word and both companies make HR software, so search engines and review directories can treat a search for one as a search for the other. The reliable check is the web address. CLEAR Talent is at www.clear-talent.com, and its mobile apps are published under the name CLEAR Talent on the Apple App Store and Google Play. Anything on another domain is a different company.',
      },
      {
        q: 'What is the main difference between the two products?',
        a: 'Breadth against depth. ClearCompany presents a talent management platform that covers recruiting and applicant tracking, onboarding, engagement and performance. CLEAR Talent covers performance and development only: goals and KPIs, review cycles, 360° feedback, competency assessment against behavioural indicators, calibration, development plans and succession. It has no recruitment product and is built to run alongside the HR systems you already have.',
      },
      {
        q: 'Can CLEAR Talent replace ClearCompany?',
        a: 'Only for the performance side. CLEAR Talent covers goals and KPIs, review cycles, 360° feedback, competency assessment, development, calibration and succession, with analytics across all of it. It does not include recruitment or applicant tracking, and it has no employee engagement survey product. If you use ClearCompany for hiring, moving performance to CLEAR Talent would leave hiring where it is, so map your current usage against those boundaries before assuming a like-for-like swap.',
      },
      {
        q: 'Can CLEAR Talent run alongside the applicant tracking system we already use?',
        a: 'Yes. CLEAR Talent is designed to sit beside the HR systems you keep rather than replace them. There is no named connector to any particular applicant tracking system or HRIS; organisation structure — departments, locations, roles and reporting lines — along with goals and ratings comes in through CSV and Excel bulk import or the API. What transfers cleanly depends on how your current data is shaped, so scope is confirmed during discovery rather than promised up front.',
      },
      {
        q: 'How do the two compare on price?',
        a: `${CLEAR_PRICE_FAQ_OPENING} For ClearCompany, ask for a written quote and write down exactly which products it includes. The two are unlikely to be the same purchase — one covers performance and development, the other can span hiring through performance — so compare the performance scope like for like, at the same headcount and over the same term, before comparing figures.`,
      },
    ],
    sources: [
      { label: 'ClearCompany (vendor site)', href: 'https://www.clearcompany.com/' },
      { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
      { label: 'About CLEAR Talent', href: 'https://www.clear-talent.com/about' },
      {
        label: 'CLEAR Talent buyer’s comparison',
        href: 'https://www.clear-talent.com/best-performance-management-software',
      },
    ],
    internalLinks: [
      { label: 'Performance management software', href: '/solutions/performance-management-software' },
      { label: 'Competency management software', href: '/solutions/competency-management-software' },
      { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
      { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
      { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
      { label: 'CLEAR Talent vs Personio', href: '/clear-talent-vs-personio' },
      { label: 'CLEAR Talent vs HiBob', href: '/clear-talent-vs-hibob' },
      { label: 'CLEAR Talent vs BambooHR', href: '/clear-talent-vs-bamboohr' },
      { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
      { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
      { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
      { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
    ],
  },

  // ─────────────────────────────────────────────
  // 5. Personio — an HRIS performance module
  // ─────────────────────────────────────────────
  {
    slug: 'clear-talent-vs-personio',
    competitor: 'Personio',
    seoTitle: 'CLEAR Talent vs Personio: Performance Management Compared',
    metaDescription:
      'Personio’s performance module or a specialist layer? Compare review cycles, 360°, OKRs, AI, calibration and languages, and see when the HRIS module is enough.',
    primaryKeyword: 'CLEAR Talent vs Personio',
    keywords: [
      'Personio performance management',
      'Personio performance reviews',
      'Personio vs CLEAR Talent',
      'HRIS performance module',
      'performance management software comparison',
    ],
    eyebrow: 'Head-to-head · HRIS performance module',
    h1: 'CLEAR Talent vs Personio',
    excerpt:
      'Personio is an HR system of record with performance built in. CLEAR Talent is a performance and development layer that runs beside an HRIS. The real question is whether the module you own is enough.',
    heroImage: '/screenshots/13-reviews-main.webp',
    heroImageAlt: 'Performance review cycle overview in CLEAR Talent',
    reviewed: HRIS_REVIEWED,
    shortAnswer: hrisShortAnswer('Personio'),
    intro: [
      'If Personio is already your HR system of record — the employee file, absences, onboarding — its performance module is the obvious first option, and a search for “Personio performance management” is rarely about replacing Personio. It is about whether the module will carry the review process you actually need to run, or whether that process deserves a specialist tool alongside it.',
      'This page is written for that decision. CLEAR Talent does not replace an HRIS: it has no payroll, absence management or applicant tracking, and it is built to sit beside the system you keep. What it adds is depth in one place — competency frameworks with behavioural indicators, evidence behind ratings, goals and KPIs as a separate stream, structured 360° feedback, calibration and development plans. If a review form, a rating and a goal list are what you need, the module you already own may well be enough, and we would rather say so here than in week three of an implementation.',
    ],
    rivalPositioning:
      'Personio positions itself as an all-in-one HR platform for small and medium-sized companies, with a strong European presence: the employee record, recruiting, onboarding, absence and time tracking, payroll-related workflows, and performance and development on one system. Plans, modules and country availability change, so confirm which plan includes performance and what it covers directly with Personio.',
    chooseClear: [
      'Ratings have to hold up to a sceptical reader later. Competency ratings can be configured to require written evidence before submission, and self, assessor and final ratings are stored separately with their own comments.',
      'Your competency model is the framework, not a tag: behavioural indicators at every level, scales from three to seven levels that can differ by job family, and role profiles carrying required competencies and target levels.',
      'Calibration, 9-box and succession need to sit on the same record as the review, rather than in a spreadsheet assembled after the cycle closes.',
      'You want goals and competencies as separate streams of one rating, with the weighting between them configurable per level.',
      'Reviews run across several countries and languages: 16 languages, with AI-generated summaries and recommendations delivered in each employee’s preferred language, and native iOS and Android apps from the Pro plan.',
    ],
    chooseRival: [
      'A straightforward cycle — a form, a rating and a conversation — is what you run, and having it on the same system as the employee file matters more than depth.',
      'Your competency model is deliberately light, and requiring written evidence behind ratings would be more process than the organisation wants.',
      'The budget covers one HR subscription. Adding CLEAR Talent is an additional purchase beside Personio, never a saving on it.',
      'Administering a second system — a fresh import after every reorganisation, a second sign-in for every employee — is a cost your HR team cannot absorb.',
    ],
    rows: [
      sharedRow('goals', {
        status: 'confirm',
        detail:
          'Ask how goals are set and tracked in the performance module, whether an individual goal can be required to link to a company objective, and whether weighted KPIs with performance bands can be modelled.',
      }),
      sharedRow('reviews', {
        status: 'strong',
        detail:
          'Performance reviews are part of the performance and development offering Personio presents. Ask to see a multi-stage cycle built with your own stages, templates and rating scale, and which plan it requires.',
      }),
      sharedRow('competencies', {
        status: 'confirm',
        detail:
          'Ask how a competency is defined, whether behavioural indicators exist at each level, how many levels a scale can carry, whether scales can differ by job family, and whether a rating can be blocked until written evidence is attached.',
      }),
      sharedRow('feedback360', {
        status: 'confirm',
        detail:
          'Ask whether peer and upward feedback can be collected inside a review cycle, how raters are chosen, and what the anonymity and minimum-rater rules are in a small team.',
      }),
      sharedRow('development', {
        status: 'confirm',
        detail:
          'Ask which of development planning, calibration, 9-box and succession the module covers, and what a calibration session looks like before ratings are released to employees.',
      }),
      sharedRow('ai', {
        status: 'confirm',
        detail:
          'Ask whether any AI assists with reviews, what it writes and reads, whether its output is labelled as AI-generated, whether it can be switched off for a cycle or a team, and what is recorded when a manager edits it.',
      }),
      sharedRow('global', {
        status: 'confirm',
        detail:
          'Ask which languages the review forms and employee screens are available in, and have them show one full review completed on a phone by someone without a company laptop.',
      }),
      hrisDataRow('Personio'),
      sharedRow('coreHr', {
        status: 'strong',
        detail:
          'This is what Personio is for: the employee record, absences, onboarding and payroll-related workflows. Confirm which of those your plan includes and which countries its payroll features cover.',
      }),
    ],
    integrationNote: hrisIntegrationNote('Personio'),
    pricing: {
      clear: CLEAR_PRICING_STATEMENT,
      rival:
        'Read Personio’s current pricing page, then confirm whether performance and development is included in the plan you are quoted or priced as an add-on or upgrade. If you keep Personio and add CLEAR Talent, the comparison is that add-on against a second subscription — not one HRIS price against another.',
      questions: PRICING_QUESTIONS,
    },
    boundaries: HRIS_BOUNDARIES,
    faqs: [
      {
        q: 'Does Personio include performance management?',
        a: 'Yes. Personio presents performance and development as part of its HR platform, so if you run Personio you may already have a review process available. Which plan includes it, and how far its cycles, rating scales and feedback can be configured, are worth confirming with Personio directly — those answers decide whether you need anything else.',
      },
      {
        q: 'When is the Personio performance module enough?',
        a: 'When the review process is simple and stable: a small number of cycles a year, a form and a rating, a conversation, and goals tracked alongside. It is also enough when keeping everything on one system matters more than depth. The signs that it is not tend to arrive together: managers rating against a competency model that lives in a separate document, calibration run in a spreadsheet after the cycle closes, and ratings nobody can explain a year later.',
      },
      {
        q: 'Can CLEAR Talent run alongside Personio?',
        a: 'Yes — it is designed to sit beside the HRIS you keep. There is no prebuilt Personio connector, so nothing syncs automatically. People and organisation structure come into CLEAR Talent through CSV or Excel bulk import or API access, and performance records come back out as CSV, Excel or PDF on every plan. Personio remains the system of record for the employee.',
      },
      {
        q: 'We run reviews in several European countries. What about languages and GDPR?',
        a: 'CLEAR Talent supports 16 languages, with AI-generated summaries and recommendations delivered in each employee’s preferred language. For data protection it provides DSAR exports, right-to-be-forgotten workflows and a data processing agreement you can request for your DPO to review. It is hosted on Google Cloud Platform with regional requirements agreed per deployment, so get the hosting region confirmed in writing alongside the DPA rather than assuming it from a web page.',
      },
      {
        q: 'How do the two compare on price?',
        a: `${CLEAR_PRICE_FAQ_OPENING} For Personio, confirm whether performance and development sits in the plan you are quoted or is an add-on. If you keep Personio and add CLEAR Talent, you are weighing that add-on or upgrade against a second subscription at your own headcount and term, so convert both to a monthly cost before comparing.`,
      },
    ],
    sources: [
      { label: 'Personio (vendor site)', href: 'https://www.personio.com/' },
      { label: 'CLEAR Talent integrations', href: 'https://www.clear-talent.com/integrations' },
      { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
      {
        label: 'CLEAR Talent buyer’s comparison',
        href: 'https://www.clear-talent.com/best-performance-management-software',
      },
    ],
    internalLinks: [
      { label: 'Performance management for Ireland and Europe', href: '/solutions/performance-management-software-europe' },
      { label: 'Spreadsheet, HRIS module or dedicated software', href: '/resources/performance-management-software-vs-spreadsheets' },
      { label: 'CLEAR Talent vs HiBob', href: '/clear-talent-vs-hibob' },
      { label: 'CLEAR Talent vs BambooHR', href: '/clear-talent-vs-bamboohr' },
      { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
      { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
      { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
      { label: 'CLEAR Talent vs ClearCompany', href: '/clear-talent-vs-clearcompany' },
      { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
      { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
      { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
      { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
    ],
  },

  // ─────────────────────────────────────────────
  // 6. HiBob — an HRIS performance module
  // ─────────────────────────────────────────────
  {
    slug: 'clear-talent-vs-hibob',
    competitor: 'HiBob',
    seoTitle: 'CLEAR Talent vs HiBob: Performance Management Compared',
    metaDescription:
      'Running performance in HiBob? Compare its review cycles, 360°, goals, AI and calibration with CLEAR Talent, and see when to add a specialist performance layer.',
    primaryKeyword: 'CLEAR Talent vs HiBob',
    keywords: [
      'HiBob performance management',
      'HiBob performance reviews',
      'HiBob vs CLEAR Talent',
      'HRIS performance module',
      'performance management software comparison',
    ],
    eyebrow: 'Head-to-head · HRIS performance module',
    h1: 'CLEAR Talent vs HiBob',
    excerpt:
      'HiBob’s Bob is an HR platform with performance management inside it. CLEAR Talent is a specialist layer that runs beside an HRIS. Whether you need both depends on what sits behind a rating.',
    heroImage: '/screenshots/14-assessments-main.webp',
    heroImageAlt: 'Competency assessment view in CLEAR Talent',
    reviewed: HRIS_REVIEWED,
    shortAnswer: hrisShortAnswer('HiBob'),
    intro: [
      'HiBob presents its platform, Bob, as an HR system for modern mid-sized and multinational companies, and performance management sits inside it. If Bob is already your system of record, a search for “HiBob performance management” is usually about one thing: whether its review cycles will carry the process you need to run, or whether that process needs a specialist layer beside it.',
      'CLEAR Talent is not an HRIS and is not trying to become one. It has no payroll, time and attendance or employee record, and it is built to run beside the system you keep. Its depth is in what sits behind a rating: competency frameworks with behavioural indicators at every level, evidence attached to ratings, goals and KPIs weighted against competency assessment, structured 360° feedback, calibration and development plans. If a configurable review cycle on the same system as the employee record is what you need, Bob may already cover it.',
    ],
    rivalPositioning:
      'HiBob positions Bob as an HR platform for modern, mid-sized and multinational companies: core HR and the employee record, onboarding, time and attendance, compensation and performance management on one system, with an emphasis on culture and employee experience. Modules and packaging change, so confirm which ones your plan includes, and what the performance module covers, directly with HiBob.',
    chooseClear: [
      'Competency assessment is the core requirement: behavioural indicators at every proficiency level, scales of three to seven levels that can differ by job family, and ratings that can require written evidence before submission.',
      'A rating needs to be decomposable later. Self, assessor and final ratings are held separately with their own comments, and goals and competencies stay separate streams until the composite is formed.',
      'Calibration should happen before ratings go final, on the same record, with 9-box, readiness and succession alongside it.',
      'You need AI governance you can show a works council or DPO: Ava’s output is labelled, editable and approved by a person, every edit is logged, and it can be disabled per cycle, team or module.',
      'Every individual goal must connect to a company objective — Strategic Linked Goals enforces that link rather than encouraging it.',
    ],
    chooseRival: [
      'Your review cycles are well served by Bob, and having performance, compensation and the employee record on one system matters more to you than depth in assessment.',
      'Engagement surveys are part of what you want from the same vendor. Ask HiBob what its survey features cover — CLEAR Talent has no survey product.',
      'Your competency model is deliberately light, and structured assessment against behavioural indicators would be more process than the organisation will absorb.',
      'One subscription is the budget. CLEAR Talent is an addition beside Bob, not a replacement for any part of it.',
    ],
    rows: [
      sharedRow('goals', {
        status: 'confirm',
        detail:
          'Ask how goals and OKRs are set in Bob, whether an individual goal can be required to link to a company objective, and whether weighted KPIs with performance bands and guardrails can be modelled.',
      }),
      sharedRow('reviews', {
        status: 'strong',
        detail:
          'Performance management is part of the platform HiBob presents. Ask to see a cycle built live with your own stages, templates and rating scale, and how much of it an administrator can change without vendor support.',
      }),
      sharedRow('competencies', {
        status: 'confirm',
        detail:
          'Ask whether competencies carry behavioural indicators at each proficiency level, how many levels a scale supports, whether scales differ by job family, and whether evidence can be required before a rating is submitted.',
      }),
      sharedRow('feedback360', {
        status: 'confirm',
        detail:
          'Ask how peer and upward reviewers are chosen, what the anonymity and minimum-rater rules are, and whether input maps to a competency model or is collected as open text.',
      }),
      sharedRow('development', {
        status: 'confirm',
        detail:
          'Ask which of development planning, calibration, 9-box and succession are in your plan, and how a calibration outcome flows into compensation decisions held on the same system.',
      }),
      sharedRow('ai', {
        status: 'confirm',
        detail:
          'Ask what any AI in the performance module drafts, what it reads to do so, whether its output is labelled and editable, whether it can be disabled per team or cycle, and what is logged when someone changes it.',
      }),
      sharedRow('global', {
        status: 'confirm',
        detail:
          'Ask which languages review forms and employee screens support, and have them complete a full review on a phone in front of you rather than describing it.',
      }),
      hrisDataRow('HiBob'),
      sharedRow('coreHr', {
        status: 'strong',
        detail:
          'This is what Bob is for: the employee record, onboarding, time and attendance and compensation. Confirm which of those your plan includes and how payroll is handled in each country you employ in.',
      }),
    ],
    integrationNote: hrisIntegrationNote('HiBob'),
    pricing: {
      clear: CLEAR_PRICING_STATEMENT,
      rival:
        'Ask HiBob for a written quote and confirm whether performance management is included in your package or licensed as a separate module. If you keep Bob and add CLEAR Talent, compare that module’s cost with a second subscription at the same headcount and term.',
      questions: PRICING_QUESTIONS,
    },
    boundaries: HRIS_BOUNDARIES,
    faqs: [
      {
        q: 'Does HiBob have performance management?',
        a: 'Yes. HiBob presents performance management as part of Bob, so if Bob is your HRIS you may already have review cycles available. How far those cycles, rating scales and feedback can be configured — and which package includes them — are the questions to put to HiBob, because they decide whether you need anything beside it.',
      },
      {
        q: 'When is HiBob’s performance module enough?',
        a: 'When your reviews are a configurable cycle, a rating and a conversation, and keeping performance next to the employee record and compensation matters more than depth. It starts to fall short of what you need when the competency model lives in a separate document, ratings cannot be traced back to evidence, and calibration happens in a spreadsheet after the cycle has closed. Those are the requirements CLEAR Talent is built around.',
      },
      {
        q: 'Can CLEAR Talent run alongside HiBob?',
        a: 'Yes. CLEAR Talent is built to sit beside the HRIS you keep. There is no prebuilt HiBob connector, so nothing syncs in the background: people and organisation structure come in through CSV or Excel bulk import or API access, and performance records go out as CSV, Excel or PDF on every plan. Bob remains the system of record for the employee.',
      },
      {
        q: 'How is CLEAR Talent’s AI governed?',
        a: 'Ava drafts review narrative, summarises multi-rater input into themes, generates competency frameworks and prompts reviewers on possible rating patterns. Its output is labelled, editable and approved by a person, every edit is logged, and it can be disabled per cycle, team or module. Employee data is never used to train models for other customers. Put the same questions to HiBob about any AI in its performance module.',
      },
      {
        q: 'How do the two compare on price?',
        a: `${CLEAR_PRICE_FAQ_OPENING} For HiBob, ask for a written quote and confirm whether performance management is part of your package or a separate module. If you keep Bob and add CLEAR Talent, weigh that module against a second subscription at your own headcount and term, converted to a monthly cost on both sides.`,
      },
    ],
    sources: [
      { label: 'HiBob (vendor site)', href: 'https://www.hibob.com/' },
      { label: 'CLEAR Talent integrations', href: 'https://www.clear-talent.com/integrations' },
      { label: 'CLEAR Talent trust centre', href: 'https://www.clear-talent.com/trust' },
      { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
    ],
    internalLinks: [
      { label: 'Competency management software', href: '/solutions/competency-management-software' },
      { label: 'Performance management for Ireland and Europe', href: '/solutions/performance-management-software-europe' },
      { label: 'CLEAR Talent vs Personio', href: '/clear-talent-vs-personio' },
      { label: 'CLEAR Talent vs BambooHR', href: '/clear-talent-vs-bamboohr' },
      { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
      { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
      { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
      { label: 'CLEAR Talent vs ClearCompany', href: '/clear-talent-vs-clearcompany' },
      { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
      { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
      { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
      { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
    ],
  },

  // ─────────────────────────────────────────────
  // 7. BambooHR — an HRIS performance module
  // ─────────────────────────────────────────────
  {
    slug: 'clear-talent-vs-bamboohr',
    competitor: 'BambooHR',
    seoTitle: 'CLEAR Talent vs BambooHR: Performance Management Compared',
    metaDescription:
      'BambooHR performance management compared with CLEAR Talent: review cycles, 360°, OKRs, AI, calibration, languages, and when the built-in module is enough.',
    primaryKeyword: 'CLEAR Talent vs BambooHR',
    keywords: [
      'BambooHR performance management',
      'BambooHR performance reviews',
      'BambooHR vs CLEAR Talent',
      'HRIS performance module',
      'performance management software comparison',
    ],
    eyebrow: 'Head-to-head · HRIS performance module',
    h1: 'CLEAR Talent vs BambooHR',
    excerpt:
      'BambooHR is an HRIS for small and medium-sized businesses with performance management built in. CLEAR Talent adds structure behind the rating. Add it only when the built-in reviews stop keeping up.',
    heroImage: '/screenshots/04-organizational-goals-main.webp',
    heroImageAlt: 'Organisational goal cascade in CLEAR Talent',
    reviewed: HRIS_REVIEWED,
    shortAnswer: hrisShortAnswer('BambooHR'),
    intro: [
      'BambooHR presents itself as an HRIS for small and medium-sized businesses, and performance management is one part of it. If BambooHR already holds your employee records, its performance features are the natural first place to run reviews, and a search for “BambooHR performance management” usually comes from a team asking whether those features will keep up as the process matures.',
      'That is a fair question to ask before you add anything. CLEAR Talent is a performance and development layer, not an HRIS: no employee record, payroll, time off or hiring. It is built to run beside BambooHR rather than replace it, and what it adds is structure behind the rating — competency frameworks with behavioural indicators, evidence attached to ratings, goals linked to company objectives with weighted KPIs, structured 360° feedback, calibration and development plans. If your reviews are simple and working, a second system may not be worth it yet.',
    ],
    rivalPositioning:
      'BambooHR positions itself as an HRIS for small and medium-sized businesses — the employee record, hiring, onboarding and time off — with performance management as one part of the platform. Plans and packaging change, so confirm which plan includes the performance features you need directly with BambooHR.',
    chooseClear: [
      'Reviews have outgrown a form and a rating, and managers need a written standard to rate against: competencies with behavioural indicators at every level, on scales of three to seven levels that can differ by job family.',
      'Individual goals need to roll up. Strategic Linked Goals requires every goal to connect to a parent objective, and a KPI library holds role-aware metric templates with performance bands and weightings.',
      '360° feedback needs anonymity rules that survive small teams: raters anonymised within their group, minimum-rater thresholds, and input structured against the role’s competencies.',
      'Calibration, 9-box and succession need to be part of the cycle rather than a spreadsheet afterwards.',
      'Part of the workforce works away from a desk or not in English: 16 languages, and native iOS and Android apps with offline support from the Pro plan.',
    ],
    chooseRival: [
      'Your review process is simple and it works: a self-assessment, a manager assessment and a conversation, kept on the same system as the employee record.',
      'You are still choosing an HRIS. Buy the system of record first; a specialist layer can be added once the review process outgrows the built-in features.',
      'A second system to administer would stretch an HR team of one or two people further than the extra depth is worth.',
      'The budget is one HR subscription. CLEAR Talent is an addition beside BambooHR, never a substitute for it.',
    ],
    rows: [
      sharedRow('goals', {
        status: 'confirm',
        detail:
          'Ask how goals are set and tracked alongside reviews, whether an individual goal can be required to link to a company objective, and whether weighted KPIs with performance bands can be modelled.',
      }),
      sharedRow('reviews', {
        status: 'strong',
        detail:
          'Performance management is part of the BambooHR platform. Ask to see a review cycle built with your own questions, stages and rating scale, how far an administrator can configure it, and which plan it requires.',
      }),
      sharedRow('competencies', {
        status: 'confirm',
        detail:
          'Ask whether competencies can be defined with behavioural indicators at each level, how many levels a scale supports, whether scales differ by role, and whether a rating can require written evidence.',
      }),
      sharedRow('feedback360', {
        status: 'confirm',
        detail:
          'Ask whether peer feedback can be collected within a cycle, how raters are chosen, and what anonymity and minimum-rater rules apply when a team has only three or four people.',
      }),
      sharedRow('development', {
        status: 'confirm',
        detail:
          'Ask which of development planning, calibration, 9-box and succession the performance features cover, and what a manager or HR lead can report on once a cycle closes.',
      }),
      sharedRow('ai', {
        status: 'confirm',
        detail:
          'Ask whether any AI assists with reviews, what it writes, whether its output is labelled and editable, and whether it can be switched off for a team or a cycle.',
      }),
      sharedRow('global', {
        status: 'confirm',
        detail:
          'Ask which languages employees can complete a review in, and have them show a full review completed on a phone by someone who has no desk.',
      }),
      hrisDataRow('BambooHR'),
      sharedRow('coreHr', {
        status: 'strong',
        detail:
          'This is what BambooHR is for: the employee record, hiring, onboarding and time off. Confirm which plan includes those and which includes the performance features, so the quote covers the ground you need.',
      }),
    ],
    integrationNote: hrisIntegrationNote('BambooHR'),
    pricing: {
      clear: CLEAR_PRICING_STATEMENT,
      rival:
        'Read BambooHR’s current pricing page, then confirm which plan includes the performance features you need. If you keep BambooHR and add CLEAR Talent, the comparison is the cost of that plan or upgrade against a second subscription — at your own headcount and term.',
      questions: PRICING_QUESTIONS,
    },
    boundaries: HRIS_BOUNDARIES,
    faqs: [
      {
        q: 'Does BambooHR have performance management?',
        a: 'Yes. BambooHR presents performance management as one part of its HRIS, so if you run BambooHR you may already have reviews available. Which plan includes the performance features, and how far a review can be configured, are the questions to confirm with BambooHR before deciding whether you need anything else.',
      },
      {
        q: 'When should a BambooHR customer add a dedicated performance tool?',
        a: 'When the review process outgrows a form and a rating. The usual signs are managers rating without a written standard, goals that do not connect to company objectives, 360° feedback that cannot stay anonymous in small teams, and calibration done in a spreadsheet after the cycle closes. If none of those applies, the built-in features are probably enough for now.',
      },
      {
        q: 'Can CLEAR Talent run alongside BambooHR?',
        a: 'Yes. CLEAR Talent is designed to sit beside BambooHR rather than replace it. There is no prebuilt BambooHR connector, so nothing syncs automatically: people and organisation structure come in through CSV or Excel bulk import or API access, and performance records go out as CSV, Excel or PDF on every plan. BambooHR remains the system of record.',
      },
      {
        q: 'Is CLEAR Talent too much for a small company?',
        a: `Not necessarily, but be honest about whether you need the depth yet. The Basic plan is USD ${BASIC_TIER.monthlyUsd} a month for ${BASIC_TIER.includedAdmins} admin user and ${BASIC_TIER.includedStaff} staff users, with each additional staff user at USD ${BASIC_TIER.extraStaffUsd} a month, and there is a ${TRIAL_POLICY.toLowerCase()}. A guided onboarding wizard is tailored to organisation size, industry and performance-management maturity, and goals, ratings and organisation structure can be bulk-imported from CSV or Excel. If your BambooHR reviews are simple and working, trial it with one team before committing.`,
      },
      {
        q: 'How do the two compare on price?',
        a: `${CLEAR_PRICE_FAQ_OPENING} For BambooHR, read their current pricing page and confirm which plan includes the performance features you need. If you keep BambooHR and add CLEAR Talent, you are weighing that plan or upgrade against a second subscription, so compare both at your own headcount and term.`,
      },
    ],
    sources: [
      { label: 'BambooHR (vendor site)', href: 'https://www.bamboohr.com/' },
      { label: 'CLEAR Talent integrations', href: 'https://www.clear-talent.com/integrations' },
      { label: 'CLEAR Talent pricing', href: 'https://www.clear-talent.com/pricing' },
      { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
    ],
    internalLinks: [
      { label: 'Spreadsheet, HRIS module or dedicated software', href: '/resources/performance-management-software-vs-spreadsheets' },
      { label: 'OKR & goal setting software', href: '/solutions/okr-goal-setting-software' },
      { label: 'CLEAR Talent vs Personio', href: '/clear-talent-vs-personio' },
      { label: 'CLEAR Talent vs HiBob', href: '/clear-talent-vs-hibob' },
      { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
      { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
      { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
      { label: 'CLEAR Talent vs ClearCompany', href: '/clear-talent-vs-clearcompany' },
      { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
      { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
      { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
      { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
    ],
  },

  // ─────────────────────────────────────────────
  // 8. Leapsome — a broad people platform
  // ─────────────────────────────────────────────
  {
    slug: 'clear-talent-vs-leapsome',
    competitor: 'Leapsome',
    seoTitle: 'CLEAR Talent vs Leapsome: Performance Software Compared',
    metaDescription:
      'Compare CLEAR Talent and Leapsome on competency depth, 360° feedback, AI governance, multilingual reach and published pricing — plus what to ask Leapsome.',
    primaryKeyword: 'CLEAR Talent vs Leapsome',
    keywords: [
      'Leapsome comparison',
      'Leapsome vs CLEAR Talent',
      'performance management software Europe',
      'competency management software',
      'performance management software comparison',
    ],
    eyebrow: 'Head-to-head',
    h1: 'CLEAR Talent vs Leapsome',
    excerpt:
      'Leapsome brings reviews, goals, engagement surveys, 1:1s and learning into one people platform. CLEAR Talent goes deeper into what a rating is made of. Which fits depends on how much of the people process you want from one supplier.',
    heroImage: '/screenshots/14-assessments-main.webp',
    heroImageAlt: 'Competency assessment view in CLEAR Talent',
    reviewed: PEOPLE_PLATFORM_REVIEWED,
    intro: [
      'Leapsome and CLEAR Talent answer the same first question — how do we run reviews, goals and 360° feedback without a spreadsheet — and then grow in different directions. Leapsome extends sideways, putting engagement surveys, 1:1 meetings, learning and onboarding on the same platform as performance. CLEAR Talent extends downwards, into the competency framework and the evidence behind each rating.',
      'So the decision is less about which product runs a better review cycle and more about shape. If you want one supplier across performance, engagement and learning, breadth is the argument. If ratings have to hold up later — to an employee, a works council or a remuneration committee — and the competency model has to be the standard people are actually assessed against, depth is. If you run reviews across several countries and languages, add a third question: can every employee complete the cycle in their own language, on a phone? A standard demo will not settle any of this, so bring your own scenarios.',
    ],
    rivalPositioning:
      'Leapsome positions itself as a people enablement platform covering performance reviews, goals and OKRs, 360° feedback, engagement surveys, 1:1 meetings, and learning and onboarding. Its appeal is one platform across those areas rather than a separate tool for each. Modules and packaging change, so confirm which ones the plan you are quoted includes directly with Leapsome.',
    chooseClear: [
      'A rating has to hold up to a sceptical reader later. Self, assessor and final ratings are stored separately with their own comments, and competency ratings can be configured to require written evidence before submission.',
      'Competencies are the framework, not a tag: behavioural indicators at every proficiency level, scales from three to seven levels that can differ by job family, and role profiles carrying required competencies and target levels.',
      'Goals should not be able to float. Strategic Linked Goals requires every individual goal to connect to a parent objective, and goal scores form one weighted stream of the rating, with competency assessment as the other.',
      'Reviews run in more than one language: 16 languages, with AI-generated summaries and recommendations delivered in each employee’s preferred language, and native iOS and Android apps with offline support from the Pro plan.',
      'You need AI governance you can show a works council or DPO: Ava’s output is labelled, editable and approved by a person, every edit is logged, and it can be disabled per cycle, team or module.',
    ],
    chooseRival: [
      'Engagement surveys are part of the purchase. CLEAR Talent has no survey product — if you need one from the same supplier as performance, that is a genuine gap, and Leapsome presents surveys as part of its platform.',
      'Learning belongs in the same purchase. CLEAR Talent development plans link to learning, but delivering courses is not a CLEAR Talent capability.',
      'You want the shortest possible vendor list across the people process, and will trade some depth in assessment for one platform.',
      'Your competency model is deliberately light, and gating ratings behind written evidence would be more process than your organisation wants.',
    ],
    rows: [
      sharedRow('goals', {
        status: 'strong',
        detail:
          'Goals and OKRs are part of the platform Leapsome presents. Ask whether an individual goal can be required to link to a parent objective rather than encouraged to, and whether weighted KPIs with performance bands can be modelled.',
      }),
      sharedRow('reviews', {
        status: 'strong',
        detail:
          'Performance reviews are core to the product. Ask to see a multi-stage cycle built with your own stages, templates and rating scale, and how much of it an administrator can change without vendor support.',
      }),
      sharedRow('competencies', {
        status: 'confirm',
        detail:
          'Ask how a competency is defined, whether behavioural indicators exist at each level, how many levels a scale can carry, whether scales can differ by job family, and whether a rating can be blocked until written evidence is attached.',
      }),
      sharedRow('feedback360', {
        status: 'strong',
        detail:
          '360° feedback is part of the platform Leapsome presents. Ask how rater groups are configured, what the anonymity and minimum-rater rules are in a small team, and whether input is structured against your competency model or gathered as open text.',
      }),
      sharedRow('development', {
        status: 'confirm',
        detail:
          'Ask which of development planning, calibration, 9-box and succession are in the plan you are quoted, and how a calibration session works before ratings are released to employees.',
      }),
      sharedRow('ai', {
        status: 'confirm',
        detail:
          'Ask what any AI in the platform writes, what it reads to write it, whether its output is labelled as AI-generated, whether it can be switched off for a cycle or a team, and what is recorded when a manager edits it.',
      }),
      sharedRow('global', {
        status: 'confirm',
        detail:
          'Ask which languages employees can complete a review and a 360° in, and have them show one full review completed on a phone by someone without a company laptop.',
      }),
      {
        requirement: 'Employee engagement surveys',
        group: 'Scope boundaries',
        clear: {
          status: 'none',
          detail:
            'Not a CLEAR Talent capability. If engagement or pulse surveys are a required part of this purchase, CLEAR Talent does not cover them and you would need a second tool.',
        },
        rival: {
          status: 'strong',
          detail:
            'Engagement surveys are part of the platform Leapsome presents. Confirm whether they are in the plan you are quoted, and how survey results connect to the performance record.',
        },
      },
      {
        requirement: 'Learning delivery',
        group: 'Scope boundaries',
        clear: {
          status: 'none',
          detail:
            'Not a CLEAR Talent capability. Development plans link to learning; delivering courses is left to the learning platform you already use.',
        },
        rival: {
          status: 'strong',
          detail:
            'Learning is part of the platform Leapsome presents. Confirm what it covers, whether it is licensed separately, and how completed learning shows up in a development plan.',
        },
      },
      sharedRow('coreHr', {
        status: 'confirm',
        detail:
          'Confirm what sits inside Leapsome and what you would still need from your HRIS or payroll platform, so the two quotes cover the same ground.',
      }),
    ],
    pricing: {
      clear: CLEAR_PRICING_STATEMENT,
      rival:
        'Read Leapsome’s current pricing page, then confirm exactly which modules — reviews, goals, surveys, learning — the plan you are quoted includes before comparing per-seat figures. A bundle that includes surveys and learning and a performance-only price are not the same purchase.',
      questions: PRICING_QUESTIONS,
    },
    boundaries: [
      'Employee engagement or pulse surveys.',
      'A learning management system. Development plans link to learning; delivering courses is not a CLEAR Talent capability.',
      'Payroll, time and attendance, or leave administration.',
      'Recruitment and applicant tracking.',
      'A prebuilt, automatically syncing connector to any named HRIS. Data moves by import, API access and export.',
    ],
    faqs: [
      {
        q: 'Is CLEAR Talent a direct replacement for Leapsome?',
        a: 'Only for the performance side. CLEAR Talent covers goals and KPIs, review cycles, 360° feedback, competency assessment, development, calibration and succession, with analytics across all of it. It has no employee engagement survey product and is not a learning management system, so if you use Leapsome for surveys or learning, those would need another home. Map your current usage against those boundaries before assuming a like-for-like swap.',
      },
      {
        q: 'What is the main functional difference between them?',
        a: 'Breadth against depth. Leapsome presents one platform across reviews, goals, 360° feedback, engagement surveys, 1:1s and learning. CLEAR Talent covers performance and development only, and puts its depth into how a rating is built: goals and KPIs as one stream, competency assessment against behavioural indicators as another, a weighting between them configurable per level, and self, assessor and final ratings stored separately with their own comments.',
      },
      {
        q: 'We run reviews in several European countries. What about languages and GDPR?',
        a: 'CLEAR Talent supports 16 languages, with AI-generated summaries and recommendations delivered in each employee’s preferred language. For data protection it provides DSAR exports, right-to-be-forgotten workflows and a data processing agreement you can request for your DPO to review. It is hosted on Google Cloud Platform with regional requirements agreed per deployment, so get the hosting region confirmed in writing alongside the DPA rather than assuming it from a web page. Put the same questions to Leapsome: which languages employees can complete a review in, and where their data is hosted.',
      },
      {
        q: 'How would we switch from Leapsome to CLEAR Talent?',
        a: `Scope the move before you set a date. CLEAR Talent supports API access plus CSV and Excel bulk import for structured data such as goals, ratings, and organisation structure including departments, locations, roles and reporting lines, and a guided onboarding wizard is tailored to organisation size, industry and performance-management maturity. Ask Leapsome which review, goal and feedback data it can export and in what format, and decide what stays where — surveys or learning, for example. What transfers cleanly depends on how your data is shaped, so migration scope is confirmed during discovery, and there is a ${TRIAL_POLICY.toLowerCase()} to run one team through a cycle first.`,
      },
      {
        q: 'How do the two compare on price?',
        a: `${CLEAR_PRICE_FAQ_OPENING} For Leapsome, read their current pricing page and write down exactly which modules the plan you are quoted includes. If it bundles surveys and learning, you are comparing a wider purchase with a performance-only one, so compare the performance scope like for like and convert both to a monthly cost at your own headcount and term.`,
      },
    ],
    sources: [
      { label: 'Leapsome (vendor site)', href: 'https://www.leapsome.com/' },
      { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
      { label: 'CLEAR Talent pricing', href: 'https://www.clear-talent.com/pricing' },
      { label: 'CLEAR Talent trust centre', href: 'https://www.clear-talent.com/trust' },
      { label: 'CLEAR Talent integrations', href: 'https://www.clear-talent.com/integrations' },
    ],
    internalLinks: [
      { label: 'Performance management for Ireland and Europe', href: '/solutions/performance-management-software-europe' },
      { label: 'Competency management software', href: '/solutions/competency-management-software' },
      { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
      { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
      { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
      { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
      { label: 'CLEAR Talent vs ClearCompany', href: '/clear-talent-vs-clearcompany' },
      { label: 'CLEAR Talent vs Personio', href: '/clear-talent-vs-personio' },
      { label: 'CLEAR Talent vs HiBob', href: '/clear-talent-vs-hibob' },
      { label: 'CLEAR Talent vs BambooHR', href: '/clear-talent-vs-bamboohr' },
      { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
      { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
      { label: 'Best OKR software for HR teams', href: OKR_SOFTWARE_GUIDE_PATH },
      { label: 'CLEAR Talent pricing', href: '/pricing' },
    ],
  },

  // ─────────────────────────────────────────────
  // 9. Culture Amp — engagement first, performance alongside
  // ─────────────────────────────────────────────
  {
    slug: 'clear-talent-vs-culture-amp',
    competitor: 'Culture Amp',
    seoTitle: 'CLEAR Talent vs Culture Amp: Performance Software Compared',
    metaDescription:
      'Culture Amp leads with engagement surveys; CLEAR Talent with evidence-backed ratings. Compare reviews, 360°, goals, AI, languages and price, and what to ask.',
    primaryKeyword: 'CLEAR Talent vs Culture Amp',
    keywords: [
      'Culture Amp comparison',
      'Culture Amp vs CLEAR Talent',
      'Culture Amp performance management',
      'performance and engagement software',
      'performance management software comparison',
    ],
    eyebrow: 'Head-to-head',
    h1: 'CLEAR Talent vs Culture Amp',
    excerpt:
      'Culture Amp is best known for engagement surveys and people analytics, with performance and development alongside. CLEAR Talent has no survey product and puts its depth into the rating itself. That split usually decides the shortlist.',
    heroImage: '/screenshots/07-reports-main.webp',
    heroImageAlt: 'Reporting workspace in CLEAR Talent',
    reviewed: PEOPLE_PLATFORM_REVIEWED,
    intro: [
      'Culture Amp and CLEAR Talent overlap on performance — review cycles, 360° feedback, development — but they start from opposite ends. Culture Amp is best known for employee engagement surveys and people analytics, and its performance and development products sit alongside them. CLEAR Talent has no survey product at all; it starts from the competency framework and the evidence behind each rating.',
      'That makes the first question unusually clear-cut. If engagement surveys are part of this purchase, CLEAR Talent cannot cover them, and you are either buying a platform that does or running a survey tool beside a performance layer. If surveys are already handled, or out of scope, the comparison becomes how much structure you want behind a rating, how goals feed it, and whether the people being reviewed can complete the cycle in their own language and on a phone.',
    ],
    rivalPositioning:
      'Culture Amp positions itself as an employee experience platform, best known for engagement surveys and people analytics, with performance management — including reviews and 360° feedback — and development products alongside. Products and packaging change, so confirm which ones your quote includes, and how they are priced together, directly with Culture Amp.',
    chooseClear: [
      'Performance and development is the purchase, and surveys are handled elsewhere or not needed. CLEAR Talent is a performance layer built to run beside the tools you keep.',
      'Competencies are the framework, not a tag: behavioural indicators at every proficiency level, scales from three to seven levels that can differ by job family, and role profiles carrying required competencies and target levels.',
      'A rating has to hold up to a sceptical reader later. Self, assessor and final ratings are stored separately with their own comments, and competency ratings can be configured to require written evidence before submission.',
      'Goals carry weight in the rating. Strategic Linked Goals requires every individual goal to connect to a parent objective, a KPI library holds role-aware metric templates with performance bands, and the weighting between goals and competencies is configurable per level.',
      'Reviews run in more than one language: 16 languages, with AI-generated summaries and recommendations delivered in each employee’s preferred language, and native iOS and Android apps with offline support from the Pro plan.',
    ],
    chooseRival: [
      'Engagement surveys are part of this purchase. CLEAR Talent has no survey product, and surveys are what Culture Amp is best known for.',
      'You want survey results and performance data analysed together by one supplier, rather than read side by side from two tools.',
      'You already run Culture Amp surveys, and adding its performance product is simpler for your team than introducing a second vendor. Familiarity is a legitimate adoption advantage.',
      'Your competency model is deliberately light, and gating ratings behind written evidence would be more process than your organisation wants.',
    ],
    rows: [
      sharedRow('goals', {
        status: 'confirm',
        detail:
          'Ask how goals are set and tracked in the performance product, whether an individual goal can be required to link to a company objective, and whether weighted KPIs with performance bands can be modelled.',
      }),
      sharedRow('reviews', {
        status: 'strong',
        detail:
          'Performance reviews are part of the performance product Culture Amp presents. Ask to see a multi-stage cycle built with your own stages, templates and rating scale, and which product it requires.',
      }),
      sharedRow('competencies', {
        status: 'confirm',
        detail:
          'Ask whether competencies carry behavioural indicators at each proficiency level, how many levels a scale supports, whether scales differ by job family, and whether evidence can be required before a rating is submitted.',
      }),
      sharedRow('feedback360', {
        status: 'strong',
        detail:
          '360° feedback is part of what Culture Amp presents alongside its performance product. Ask how rater groups are configured, what the anonymity and minimum-rater rules are, and whether input is structured against your competency model.',
      }),
      sharedRow('development', {
        status: 'confirm',
        detail:
          'Ask which of development planning, calibration, 9-box and succession sit in the products you are quoted, which are separate, and what a completed report looks like at team and organisation level.',
      }),
      sharedRow('ai', {
        status: 'confirm',
        detail:
          'Ask what any AI writes or summarises, what it reads to do so, whether its output is labelled and editable, whether it can be disabled per team or cycle, and what is logged when someone changes it.',
      }),
      sharedRow('global', {
        status: 'confirm',
        detail:
          'Ask which languages employees can complete a review and a 360° in, and have them complete a full review on a phone in front of you rather than describing it.',
      }),
      {
        requirement: 'Employee engagement surveys and people analytics',
        group: 'Scope boundaries',
        clear: {
          status: 'none',
          detail:
            'Not a CLEAR Talent capability. Reporting covers performance data at individual, team and organisation level; if engagement or pulse surveys are a required part of this purchase, you would need a second tool.',
        },
        rival: {
          status: 'strong',
          detail:
            'Engagement surveys and people analytics are what Culture Amp is best known for. Confirm which survey products your quote includes and how survey results connect to the performance record.',
        },
      },
      sharedRow('coreHr', {
        status: 'confirm',
        detail:
          'Confirm what sits inside Culture Amp and what you would still need from your HRIS or payroll platform, so the two quotes cover the same ground.',
      }),
    ],
    pricing: {
      clear: CLEAR_PRICING_STATEMENT,
      rival:
        'Read Culture Amp’s current pricing information, then confirm which products — engagement, performance, development — your quote covers. A quote that bundles surveys with performance and one for performance alone are not the same purchase, so compare the performance scope like for like.',
      questions: PRICING_QUESTIONS,
    },
    boundaries: [
      'Employee engagement or pulse surveys, and the survey analytics built on them.',
      'A learning management system. Development plans link to learning; delivering courses is not a CLEAR Talent capability.',
      'Payroll, time and attendance, or leave administration.',
      'Recruitment and applicant tracking.',
    ],
    faqs: [
      {
        q: 'Does CLEAR Talent run engagement surveys like Culture Amp?',
        a: 'No. CLEAR Talent has no engagement or pulse survey product. It covers the performance side — goals and KPIs, review cycles, 360° feedback, competency assessment, development and calibration. If surveys are part of this purchase, you need either a platform that bundles both or a dedicated survey tool beside CLEAR Talent, and we would rather you knew that here than in the third demo.',
      },
      {
        q: 'Can we keep Culture Amp for surveys and use CLEAR Talent for performance?',
        a: 'Yes. CLEAR Talent is built to run beside the tools you keep, and the two answer different questions: a survey describes how a group feels about the organisation, while the performance record describes how one person is doing and what happens next. There is no prebuilt connector between them, and survey results are not imported into CLEAR Talent. People and organisation structure come in by CSV or Excel bulk import or API access, and performance records go out as CSV, Excel or PDF. Confirm with Culture Amp whether its surveys can be licensed without its performance product.',
      },
      {
        q: 'We review people in several countries and languages. Which handles that better?',
        a: 'Test it with the people who will use it rather than taking either vendor’s word. CLEAR Talent supports 16 languages, with AI-generated summaries and recommendations delivered in each employee’s preferred language, and native iOS and Android apps with biometric sign-in, push notifications and offline support from the Pro plan. For data protection it provides DSAR exports, right-to-be-forgotten workflows and a data processing agreement you can request. Ask Culture Amp which languages employees can complete a review in, and have both vendors show the same review completed on a phone.',
      },
      {
        q: 'How would we move our performance process from Culture Amp?',
        a: `Scope the move before you set a date. CLEAR Talent supports API access plus CSV and Excel bulk import for structured data such as goals, ratings, and organisation structure including departments, locations, roles and reporting lines, and a guided onboarding wizard is tailored to organisation size, industry and performance-management maturity. Ask Culture Amp which review, goal and feedback data it can export and in what format, and decide whether surveys stay with Culture Amp. What transfers cleanly depends on how your data is shaped, so migration scope is confirmed during discovery, and there is a ${TRIAL_POLICY.toLowerCase()} to run one team through a cycle first.`,
      },
      {
        q: 'How do the two compare on price?',
        a: `${CLEAR_PRICE_FAQ_OPENING} For Culture Amp, ask for a written quote and write down exactly which products it covers. If it bundles engagement surveys with performance, it is a wider purchase than CLEAR Talent alone, so compare the performance scope like for like and convert both to a monthly cost at your own headcount and term.`,
      },
    ],
    sources: [
      { label: 'Culture Amp (vendor site)', href: 'https://www.cultureamp.com/' },
      { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
      { label: 'CLEAR Talent pricing', href: 'https://www.clear-talent.com/pricing' },
      {
        label: 'CLEAR Talent and engagement surveys',
        href: 'https://www.clear-talent.com/solutions/employee-engagement-survey-software',
      },
    ],
    internalLinks: [
      { label: 'Employee engagement and pulse survey software', href: '/solutions/employee-engagement-survey-software' },
      { label: '360 degree feedback software', href: '/solutions/360-degree-feedback-software' },
      { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
      { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
      { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
      { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
      { label: 'CLEAR Talent vs ClearCompany', href: '/clear-talent-vs-clearcompany' },
      { label: 'CLEAR Talent vs Personio', href: '/clear-talent-vs-personio' },
      { label: 'CLEAR Talent vs HiBob', href: '/clear-talent-vs-hibob' },
      { label: 'CLEAR Talent vs BambooHR', href: '/clear-talent-vs-bamboohr' },
      { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
      { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
      { label: 'Best 360 feedback tools', href: FEEDBACK_360_GUIDE_PATH },
      { label: 'CLEAR Talent pricing', href: '/pricing' },
    ],
  },
];

export const HEAD_TO_HEAD_BY_SLUG: Record<string, HeadToHead> = HEAD_TO_HEAD_COMPARISONS.reduce(
  (acc, entry) => ({ ...acc, [entry.slug]: entry }),
  {} as Record<string, HeadToHead>,
);

export const HEAD_TO_HEAD_PATHS = HEAD_TO_HEAD_COMPARISONS.map(headToHeadPath);

/** Structural match for `RouteSeo['schemas']`, declared locally to avoid a cycle. */
type HeadToHeadJsonLd = Record<string, unknown>;

/**
 * Home > Software comparison > this page, for the visible trail and the
 * BreadcrumbList alike. The buyer's guide is the cluster's hub, so it is the
 * parent; the page is named by its H1 ("CLEAR Talent vs Lattice") rather than
 * the bare competitor name, which on its own reads as a page about that vendor.
 */
export const getHeadToHeadBreadcrumbs = (entry: HeadToHead): BreadcrumbItem[] => [
  { name: 'Home', path: '/' },
  { name: 'Software comparison', path: HEAD_TO_HEAD_HUB_PATH },
  { name: entry.h1, path: headToHeadPath(entry) },
];

/**
 * WebPage + Article + FAQPage + BreadcrumbList for one head-to-head. `siteSeo`
 * prepends the Organization, WebSite and SoftwareApplication nodes so the `@id`
 * references resolve — the SoftwareApplication node is the only product markup
 * on the page and it describes CLEAR Talent, not the competitor.
 *
 * Deliberately no `Review` or `AggregateRating`: this is a vendor-authored
 * comparison, not testimony, and marking it up as a rating of another company's
 * product would be false. There is also no schema.org `ComparisonTable` type to
 * emit — the visible table carries a caption and row headers instead.
 */
export const getSchemaForHeadToHead = (entry: HeadToHead): HeadToHeadJsonLd[] => {
  const url = `${SITE_ORIGIN}${headToHeadPath(entry)}`;
  const reviewedIso = headToHeadReviewed(entry).iso;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: entry.seoTitle,
      description: entry.metaDescription,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': SOFTWARE_ID },
      inLanguage: 'en',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${url}#article`,
      headline: entry.h1,
      description: entry.excerpt,
      url,
      mainEntityOfPage: { '@id': `${url}#webpage` },
      image: `${SITE_ORIGIN}${entry.heroImage}`,
      datePublished: reviewedIso,
      dateModified: reviewedIso,
      author: { '@id': ORG_ID },
      publisher: { '@id': ORG_ID },
      about: { '@id': SOFTWARE_ID },
      inLanguage: 'en',
      articleSection: 'Software comparison',
      keywords: [entry.primaryKeyword, ...entry.keywords],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: entry.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
    breadcrumbListSchema(getHeadToHeadBreadcrumbs(entry)),
  ];
};

// ─────────────────────────────────────────────
// Segment guide: choosing by company size
// ─────────────────────────────────────────────

export const SEGMENT_GUIDE_REVIEWED_DATE = 'September 24, 2026';
export const SEGMENT_GUIDE_REVIEWED_DATE_ISO = '2026-09-24';

/** The head-to-head disclaimer, restated for a table with six other columns rather than one. */
export const SEGMENT_GUIDE_DISCLAIMER =
  'We can describe CLEAR Talent precisely, because we build it. We cannot describe other vendors’ current packaging with the same authority, and pretending otherwise would make this table useless to you. So the CLEAR Talent column states what the platform does today. The other columns say “Strong” only where that vendor presents the capability in its own public positioning, and “Confirm” everywhere else — which means ask them, never that the capability is missing.';

/**
 * Every vendor any guide in this file compares, under one id each, so a vendor
 * appearing in two guides is the same key in both. A guide's table only renders
 * the vendors that guide lists; `seo/siteSeo.test.ts` checks that every row
 * carries a status for each of them.
 */
export type SegmentVendorId =
  | 'clear'
  | 'lattice'
  | 'fifteenFive'
  | 'betterworks'
  | 'cultureAmp'
  | 'performYard'
  | 'bambooHr'
  | 'leapsome'
  | 'perdoo'
  | 'quantive'
  | 'workBoard'
  | 'profitCo'
  | 'qualtrics'
  | 'spidergap'
  | 'workday'
  | 'sapSuccessFactors'
  | 'personio'
  | 'hiBob';

export interface SegmentVendor {
  id: SegmentVendorId;
  name: string;
  /** How the vendor describes itself, stated neutrally. */
  positioning: string;
  /** The situation in which this vendor belongs on the shortlist the guide is written for. */
  worthALook: string;
  /** The question that settles the most in a demo. Empty for CLEAR Talent. */
  askThem: string;
  href: string;
  /** Our named head-to-head against this vendor, where one exists. */
  headToHead?: string;
}

export interface SegmentCriterion {
  criterion: string;
  /** Why the criterion matters at this company size. */
  why: string;
  /** What CLEAR Talent does, in one line — the cell text beside its badge. */
  clearNote: string;
  /** One status for each vendor the guide lists, and for no other. */
  statuses: Partial<Record<SegmentVendorId, ComparisonStatus>>;
}

/** The section copy that differs between guides; everything else on the template is shared. */
export interface SegmentGuideCopy {
  criteriaEyebrow: string;
  criteriaTitle: string;
  criteriaSub: string;
  /** What the table compares, for its accessible name: "Scrollable comparison of …". */
  tableSubject: string;
  /** Follows the vendor names in the table caption. */
  tableCaptionSuffix: string;
  faqTitle: string;
  faqSub: string;
  checklistSub: string;
  ctaTitle: string;
  ctaSub: string;
  /** `source` on the demo link, so a demo request can be traced to the guide. */
  demoSource: string;
}

export interface SegmentPricePoint {
  staff: number;
  tier: PublishedTier;
  /** Published monthly list price, or null where the plan does not reach the headcount. */
  monthlyUsd: number | null;
}

export interface SegmentGuide {
  path: string;
  /** <title> and og:title — under 60 characters. */
  seoTitle: string;
  /** 150–160 characters. */
  metaDescription: string;
  primaryKeyword: string;
  keywords: string[];
  eyebrow: string;
  h1: string;
  /** The page's name in the breadcrumb trail. */
  breadcrumbName: string;
  excerpt: string;
  heroImage: string;
  heroImageAlt: string;
  /**
   * When the vendor evidence was last checked: the visible "Last reviewed" line,
   * the Article dates and the sitemap lastmod. Move it only when the guide is
   * actually rechecked.
   */
  reviewed: { label: string; iso: string };
  /** The `name` of the route's ItemList of compared tools. */
  itemListName: string;
  copy: SegmentGuideCopy;
  intro: string[];
  selectionCriteria: { title: string; text: string }[];
  vendors: SegmentVendor[];
  criteria: SegmentCriterion[];
  clearFit: string[];
  clearNotFit: string[];
  pricing: {
    points: SegmentPricePoint[];
    /** Row label for the headcount past every published plan, where Enterprise is quoted. */
    aboveCapLabel: string;
    summary: string;
    rivals: string;
    questions: string[];
  };
  checklist: { stage: string; items: string[] }[];
  faqs: HeadToHeadFaq[];
  sources: { label: string; href: string }[];
  internalLinks: { label: string; href: string }[];
}

/** The two ends of the range the published plans cover: a 50-person company, and the cap. */
const SEGMENT_PRICE_STAFF = [50, PUBLISHED_STAFF_CAP];

/** Basic and Pro list prices at each headcount, for a guide's price table. */
const pricePointsAt = (staffCounts: number[]): SegmentPricePoint[] =>
  staffCounts.flatMap((staff) =>
    [BASIC_TIER, PRO_TIER].map((tier) => ({ staff, tier, monthlyUsd: monthlyListPrice(tier, staff) })),
  );

const segmentPricePoints: SegmentPricePoint[] = pricePointsAt(SEGMENT_PRICE_STAFF);

const priceAt = (tier: PublishedTier, staff: number) => {
  const price = monthlyListPrice(tier, staff);
  return price === null ? 'quoted' : `USD ${price}`;
};

/** Succession planning is the Enterprise module a buyer at this size is most likely to assume is included. */
const SUCCESSION_MODULE = ENTERPRISE_ONLY_MODULES.find((name) => /succession/i.test(name));

const SEGMENT_PRICE_SUMMARY =
  `${STARTING_PRICE_SENTENCE} At 50 staff users that is ${priceAt(BASIC_TIER, 50)} a month on Basic or ${priceAt(PRO_TIER, 50)} on Pro; at ${PUBLISHED_STAFF_CAP} staff users, ${priceAt(BASIC_TIER, PUBLISHED_STAFF_CAP)} on Basic or ${priceAt(PRO_TIER, PUBLISHED_STAFF_CAP)} on Pro. Annual billing takes ${ANNUAL_DISCOUNT_PERCENT}% off the plan price, and there is a ${TRIAL_POLICY.toLowerCase()}. Between ${PUBLISHED_STAFF_CAP} and 500 employees there is no published figure: the Enterprise plan is quoted to your scope.`;

export const SEGMENT_GUIDE: SegmentGuide = {
  path: SEGMENT_GUIDE_PATH,
  seoTitle: 'Best Performance Management Software for Small Business',
  metaDescription:
    'Compare seven performance management tools for small and mid-size businesses: what to look for, a fair capability table, pricing context and a buyer checklist.',
  primaryKeyword: 'best performance management software for small business',
  keywords: [
    'performance management software for mid-size companies',
    'performance management software for small business',
    'performance review software for small business',
    'performance management software comparison',
  ],
  eyebrow: 'Buyer’s guide · 50–500 employees',
  h1: 'Performance management software for small and mid-size businesses',
  breadcrumbName: 'Small and mid-size businesses',
  excerpt:
    'Seven tools a 50–500 employee company is likely to shortlist, the criteria that actually separate them at this size, and a checklist for running the evaluation with an HR team of one to five.',
  heroImage: '/screenshots/02-onboarding-main.webp',
  heroImageAlt: 'Guided onboarding wizard in CLEAR Talent',
  reviewed: { label: SEGMENT_GUIDE_REVIEWED_DATE, iso: SEGMENT_GUIDE_REVIEWED_DATE_ISO },
  itemListName: 'Performance management tools compared for 50–500 employee companies',
  copy: {
    criteriaEyebrow: 'What to look for at this size',
    criteriaTitle: 'What separates one tool from another at 50–500 employees.',
    criteriaSub:
      'Feature lists converge. These are the questions that decide whether a small HR team can run the tool and managers will actually finish a cycle in it.',
    tableSubject: 'performance management tools for small and mid-size businesses',
    tableCaptionSuffix: 'compared for 50–500 employee companies',
    faqTitle: 'Questions small and mid-size teams ask.',
    faqSub: 'The answers a shortlist at this size actually turns on.',
    checklistSub:
      'A short, repeatable process a small HR team can actually follow — and the one that stops the best demo from winning by default.',
    ctaTitle: 'Bring your own review cycle to the demo.',
    ctaSub:
      'Show us your stages, your competencies and your headcount, and judge CLEAR Talent on how it handles them — with the same scenarios you give every other vendor.',
    demoSource: 'small-business-guide',
  },
  intro: [
    'Most “best performance management software” lists are written for no company in particular. At 50 to 500 employees the decision is narrower and more practical than they suggest. The person running the evaluation is usually also the person who will administer the tool, often alongside payroll queries and hiring. Many of the managers who will complete reviews are doing it for the first or second time. And the price has to make sense at today’s headcount and at the headcount you expect in three years, not at an enterprise volume discount.',
    'This guide compares seven tools that come up on shortlists at this size: CLEAR Talent, Lattice, 15Five, Betterworks, Culture Amp, PerformYard and BambooHR. We build CLEAR Talent, so we describe it precisely and describe the others only as they describe themselves. Where we cannot speak for another vendor, the table says “Confirm” and the vendor notes give you the question to ask — never a claimed gap.',
  ],
  selectionCriteria: [
    {
      title: 'Who will administer it',
      text: 'At this size HR is often one to five people. Ask each vendor to let your administrator build a review template, a rating scale and a reminder schedule in the demo, without their consultant driving. What needs vendor services to change will need them every cycle.',
    },
    {
      title: 'The price at your real headcount',
      text: 'Pricing models differ — a plan fee with per-user add-ons, a per-seat rate, a minimum seat count, modules sold separately. Get every quote at today’s headcount and your three-year headcount, over the same term, covering the same modules.',
    },
    {
      title: 'A performance layer, or a whole HR suite',
      text: 'Decide this before the shortlist. If you already run an HRIS or payroll system you are keeping, you are buying a performance layer beside it. If you have no system of record yet, an HRIS with performance built in may be the first purchase. Mixing the two decisions produces a shortlist that cannot be compared.',
    },
    {
      title: 'Something managers will finish',
      text: 'Completion is the outcome, and first-time managers are the constraint. Look at reminders, how long a review takes on a phone, and whether AI drafting helps without writing the review for them.',
    },
    {
      title: 'Structure you can grow into',
      text: 'The competency model and goal structure you set up at 80 people should still work at 400 without re-implementation. Check whether competencies can differ by role, and whether individual goals connect to company objectives.',
    },
    {
      title: 'A rollout measured in weeks',
      text: 'A small HR team cannot absorb a transformation programme. Ask what onboarding involves, what can be imported from spreadsheets, and who does the work — you or the vendor.',
    },
  ],
  vendors: [
    {
      id: 'clear',
      name: 'CLEAR Talent',
      positioning:
        'A performance and development layer: goals and KPIs, review cycles, 360° feedback, competency assessment and development, with Ava AI assistance that a person reviews and approves. Built to run alongside the HRIS or payroll system you already have rather than replace it.',
      worthALook:
        'Performance and development are the priority, you are keeping your HRIS, and you want ratings backed by competencies and evidence rather than a single score.',
      askThem: '',
      href: '/features',
    },
    {
      id: 'lattice',
      name: 'Lattice',
      positioning:
        'A people platform covering performance reviews, goals and OKRs, 1:1s, engagement surveys, career and development tracks and analytics, and it has extended beyond performance into core HR. Widely adopted by fast-growing companies that want one supplier across those areas.',
      worthALook:
        'You want performance and engagement surveys from one supplier and are comfortable consolidating more of the HR stack with it.',
      askThem:
        'Which modules sit in the tier you are quoted at your headcount, and whether a minimum seat count applies.',
      href: 'https://lattice.com/',
      headToHead: '/clear-talent-vs-lattice',
    },
    {
      id: 'fifteenFive',
      name: '15Five',
      positioning:
        'Continuous performance and manager enablement: weekly check-ins, 1-on-1s, OKRs, performance reviews, engagement surveys, and manager coaching and training content.',
      worthALook:
        'Manager capability is the real problem, and you want a weekly check-in habit and training content as much as a review workflow.',
      askThem: 'Whether manager training is inside the tier you are quoted or priced separately.',
      href: 'https://www.15five.com/',
      headToHead: '/clear-talent-vs-15five',
    },
    {
      id: 'betterworks',
      name: 'Betterworks',
      positioning:
        'Enterprise goal and OKR management, with conversations, continuous feedback, performance reviews and calibration alongside, typically deployed in large organisations next to an existing HCM.',
      worthALook:
        'Its positioning is aimed at large organisations, so at this size it belongs on the list mainly if an enterprise-scale goal programme is the outcome you are buying, or one you expect to grow into soon.',
      askThem: 'What the minimum contract looks like at your headcount, and what implementation involves.',
      href: 'https://www.betterworks.com/',
      headToHead: '/clear-talent-vs-betterworks',
    },
    {
      id: 'cultureAmp',
      name: 'Culture Amp',
      positioning:
        'An employee experience platform best known for engagement surveys and people analytics, with performance and development products alongside.',
      worthALook:
        'Measuring engagement matters at least as much as running reviews, and you want survey results and performance data from one supplier.',
      askThem:
        'Which of engagement, performance and development are separate products, and how they are priced when bought together.',
      href: 'https://www.cultureamp.com/',
      headToHead: '/clear-talent-vs-culture-amp',
    },
    {
      id: 'performYard',
      name: 'PerformYard',
      positioning:
        'Performance management software for HR teams, built around flexible review processes — annual, quarterly or continuous — with goals and feedback alongside.',
      worthALook:
        'The main need is a configurable review process that HR runs, and engagement surveys and core HR are not part of this purchase.',
      askThem:
        'How competencies are modelled, and whether a rating can be required to carry written evidence.',
      href: 'https://www.performyard.com/',
    },
    {
      id: 'bambooHr',
      name: 'BambooHR',
      positioning:
        'An HRIS for small and medium-sized businesses — the employee record, hiring, onboarding and time off — with performance management as one part of the platform.',
      worthALook:
        'You do not yet have an HRIS and would rather buy the system of record first, with performance reviews inside it.',
      askThem: 'Which plan includes the performance features you need, and how far review configuration goes.',
      href: 'https://www.bamboohr.com/',
      headToHead: '/clear-talent-vs-bamboohr',
    },
  ],
  criteria: [
    {
      criterion: 'Review cycles a small HR team can configure',
      why: 'Your administrator changes templates, stages and deadlines without vendor services.',
      clearNote: 'Configurable self, manager, peer and multi-stage cycles, with per-stage deadlines and automated reminders.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        betterworks: 'strong',
        cultureAmp: 'strong',
        performYard: 'strong',
        bambooHr: 'strong',
      },
    },
    {
      criterion: 'Goals and OKRs linked to company objectives',
      why: 'Individual goals stay tied to what the company is actually trying to do.',
      clearNote: 'Strategic Linked Goals requires every individual goal to connect to a parent; weighted KPIs sit alongside.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        betterworks: 'strong',
        cultureAmp: 'confirm',
        performYard: 'confirm',
        bambooHr: 'confirm',
      },
    },
    {
      criterion: 'Competency frameworks with behavioural indicators',
      why: 'First-time managers rate against a written standard instead of a feeling.',
      clearNote: 'Indicators at every proficiency level, scales of three to seven levels, and ratings that can require evidence.',
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        betterworks: 'confirm',
        cultureAmp: 'confirm',
        performYard: 'confirm',
        bambooHr: 'confirm',
      },
    },
    {
      criterion: '360° feedback with anonymity rules',
      why: 'In small teams, anonymity is easy to break unless minimum-rater rules exist.',
      clearNote: 'Configurable rater groups, anonymity within each group, and minimum-rater thresholds.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'confirm',
        betterworks: 'confirm',
        cultureAmp: 'confirm',
        performYard: 'confirm',
        bambooHr: 'confirm',
      },
    },
    {
      criterion: 'Check-ins and feedback between cycles',
      why: 'The annual review stops being the only conversation.',
      clearNote: 'Continuous feedback and check-ins between formal cycles, with quarterly and annual cadences side by side.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        betterworks: 'strong',
        cultureAmp: 'confirm',
        performYard: 'confirm',
        bambooHr: 'confirm',
      },
    },
    {
      criterion: 'AI assistance with human approval',
      why: 'Drafting help for busy managers, without output nobody has checked.',
      clearNote: 'Ava drafts and summarises; output is labelled, editable, approved by a person and can be switched off. AI scope varies by plan.',
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        betterworks: 'confirm',
        cultureAmp: 'confirm',
        performYard: 'confirm',
        bambooHr: 'confirm',
      },
    },
    {
      criterion: 'Mobile and multilingual participation',
      why: 'Staff away from a desk, or not working in English, still complete the cycle.',
      clearNote: '16 languages; native iOS and Android apps with offline support are included from the Pro plan.',
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        betterworks: 'confirm',
        cultureAmp: 'confirm',
        performYard: 'confirm',
        bambooHr: 'confirm',
      },
    },
    {
      criterion: 'Employee engagement surveys',
      why: 'Only matters if surveys are part of this purchase — then it matters a lot.',
      clearNote: 'Not a CLEAR Talent capability. If surveys are required, you would need a second tool.',
      statuses: {
        clear: 'none',
        lattice: 'strong',
        fifteenFive: 'strong',
        betterworks: 'confirm',
        cultureAmp: 'strong',
        performYard: 'confirm',
        bambooHr: 'confirm',
      },
    },
    {
      criterion: 'Core HR record, payroll and time off',
      why: 'Decides whether you are buying a performance layer or a system of record.',
      clearNote: 'Not a CLEAR Talent capability, deliberately: it runs alongside the HRIS or payroll system you keep.',
      statuses: {
        clear: 'none',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        betterworks: 'confirm',
        cultureAmp: 'confirm',
        performYard: 'confirm',
        bambooHr: 'strong',
      },
    },
  ],
  clearFit: [
    'Performance and development are the priority, and you already have an HRIS or payroll system you are keeping. CLEAR Talent is built to sit beside it.',
    'Ratings need to hold up. Competencies carry behavioural indicators at every level, and ratings can be configured to require written evidence before submission.',
    `You want a price to plan against before the first sales call: the Basic plan is USD ${BASIC_TIER.monthlyUsd} a month, with published per-user rates up to ${PUBLISHED_STAFF_CAP} staff users, and a ${TRIAL_POLICY.toLowerCase()}.`,
    'A small HR team will run it. A guided onboarding wizard is tailored to organisation size, industry and performance-management maturity, and goals, ratings and organisation structure can be bulk-imported from CSV or Excel.',
    'Your people work across languages or away from desks: 16 languages, and native iOS and Android apps with offline support from the Pro plan.',
  ],
  clearNotFit: [
    'Engagement or pulse surveys are part of this purchase. CLEAR Talent has no survey product.',
    'You need payroll, time and attendance, or recruitment. CLEAR Talent does not do those, and an HRIS with performance built in may be the better first purchase.',
    'A manager training curriculum is what you are really buying. CLEAR Talent gives managers coaching context, not a course library.',
    `${SUCCESSION_MODULE ?? 'Talent and succession planning'} has to be in scope at a published price. The pricing page lists it as an Enterprise module, and Enterprise is quoted.`,
    `You are past ${PUBLISHED_STAFF_CAP} staff users and want a list price rather than a quote. Above that headcount CLEAR Talent is priced on the Enterprise plan.`,
  ],
  pricing: {
    points: segmentPricePoints,
    aboveCapLabel: `${PUBLISHED_STAFF_CAP + 1}–500`,
    summary: SEGMENT_PRICE_SUMMARY,
    rivals:
      'We do not quote other vendors’ prices here. Published pricing changes, some vendors quote rather than publish, and a figure repeated on this page would be out of date before you read it. Read each vendor’s current pricing page, then get a written quote at the same headcount, term and module list as every other vendor on the shortlist.',
    questions: PRICING_QUESTIONS,
  },
  checklist: [
    {
      stage: 'Before the demos',
      items: [
        'Write down today’s headcount and your expected headcount in three years, and ask for every quote at both.',
        'Decide whether you are buying a performance layer beside your HRIS or replacing the HRIS — and shortlist accordingly.',
        'Name who will administer the tool and roughly how many hours a month they have for it.',
        'Write three to five scenarios every vendor must show live: a review cycle with your stages, a goal linked to a company objective, a 360° with your rater rules, and a manager completing a review on a phone.',
      ],
    },
    {
      stage: 'In the demo',
      items: [
        'Have your administrator build a review template and rating scale themselves, not watch the vendor do it.',
        'Ask to see how a rating is evidenced once the cycle has closed.',
        'Ask what the AI writes, whether its output is labelled, and whether it can be switched off per team or cycle.',
        'Put at least one manager who will actually complete reviews in the room.',
      ],
    },
    {
      stage: 'Before you sign',
      items: [
        'Get the module list, how a user or seat is counted, any minimum seat count, the implementation fee and the renewal terms in writing.',
        'Confirm how your data comes out — formats, and what happens at the end of the contract.',
        'Run a trial or a pilot with one team through a real check-in or review before rolling out to everyone.',
      ],
    },
  ],
  faqs: [
    {
      q: 'What is the best performance management software for a small business?',
      a: 'There is no single winner, and a list that names one without asking about your situation is guessing. At 50 to 500 employees the decision usually turns on three questions: whether you are keeping your current HRIS or buying one, whether engagement surveys are part of the purchase, and how much structure you want behind a rating. If you are keeping your HRIS and want evidence-backed competency ratings, CLEAR Talent is built for that. If surveys matter as much as reviews, Culture Amp, Lattice and 15Five all position around them. If you have no HRIS yet, an HRIS with performance built in, such as BambooHR, may be the first purchase.',
    },
    {
      q: 'How much does performance management software cost for 50 to 500 employees?',
      a: `Pricing models differ, which is why headline figures mislead. CLEAR Talent publishes a plan fee with per-user add-ons: at 50 staff users that comes to ${priceAt(BASIC_TIER, 50)} a month on the Basic plan or ${priceAt(PRO_TIER, 50)} on Pro, and at ${PUBLISHED_STAFF_CAP} staff users ${priceAt(BASIC_TIER, PUBLISHED_STAFF_CAP)} on Basic or ${priceAt(PRO_TIER, PUBLISHED_STAFF_CAP)} on Pro, with ${ANNUAL_DISCOUNT_PERCENT}% off the plan price for annual billing. Past ${PUBLISHED_STAFF_CAP} staff users the Enterprise plan is quoted. For other vendors, read their current pricing page and ask whether they price per seat, whether a minimum seat count applies, and which modules the quote includes — then compare every quote at your own headcount and term.`,
    },
    {
      q: 'Do we need an HRIS before we buy performance management software?',
      a: 'No, but you do need to know which of the two you are buying. A performance layer such as CLEAR Talent runs alongside an existing HRIS or payroll system and does not replace the employee record. If you have no system of record at all, an HRIS that includes performance features may be the better first step, and a specialist layer can be added once the review process outgrows it.',
    },
    {
      q: 'How long does it take a small HR team to roll out?',
      a: 'With CLEAR Talent, rollout is measured in weeks. A guided onboarding wizard is tailored to organisation size, industry and performance-management maturity, and goals, ratings and organisation structure — departments, locations, roles and reporting lines — can be brought in through CSV and Excel bulk import or the API. What transfers cleanly depends on how your current data is shaped, so migration scope is confirmed during discovery rather than promised up front.',
    },
    {
      q: 'Is CLEAR Talent suitable for a company of around 50 people?',
      a: `Yes. The Basic plan is USD ${BASIC_TIER.monthlyUsd} a month for ${BASIC_TIER.includedAdmins} admin user and ${BASIC_TIER.includedStaff} staff users, with each additional staff user at USD ${BASIC_TIER.extraStaffUsd} a month, and covers competency frameworks, goals and OKRs, KPIs, review cycles and continuous feedback. The mobile apps and individual development plans start on the Pro plan, at USD ${PRO_TIER.monthlyUsd} a month for ${PRO_TIER.includedAdmins} admin users and ${PRO_TIER.includedStaff} staff users. Both come with a ${TRIAL_POLICY.toLowerCase()}, with no credit card required.`,
    },
  ],
  sources: [
    { label: 'CLEAR Talent pricing', href: 'https://www.clear-talent.com/pricing' },
    { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
    { label: 'Lattice', href: 'https://lattice.com/' },
    { label: '15Five', href: 'https://www.15five.com/' },
    { label: 'Betterworks', href: 'https://www.betterworks.com/' },
    { label: 'Culture Amp', href: 'https://www.cultureamp.com/' },
    { label: 'PerformYard', href: 'https://www.performyard.com/' },
    { label: 'BambooHR', href: 'https://www.bamboohr.com/' },
  ],
  internalLinks: [
    { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
    { label: 'Choosing for a startup', href: STARTUP_GUIDE_PATH },
    { label: 'Choosing for 500+ employees', href: ENTERPRISE_GUIDE_PATH },
    { label: 'Choosing for a remote team', href: REMOTE_TEAMS_GUIDE_PATH },
    { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
    { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
    { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
    { label: 'CLEAR Talent vs ClearCompany', href: '/clear-talent-vs-clearcompany' },
    { label: 'CLEAR Talent vs Personio', href: '/clear-talent-vs-personio' },
    { label: 'CLEAR Talent vs HiBob', href: '/clear-talent-vs-hibob' },
    { label: 'CLEAR Talent vs BambooHR', href: '/clear-talent-vs-bamboohr' },
    { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
    { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
    { label: 'Performance management software', href: '/solutions/performance-management-software' },
    { label: 'Best OKR software for HR teams', href: OKR_SOFTWARE_GUIDE_PATH },
    { label: 'Best 360 feedback tools', href: FEEDBACK_360_GUIDE_PATH },
    { label: 'CLEAR Talent pricing', href: '/pricing' },
  ],
};

// ─────────────────────────────────────────────
// Category roundups: choosing by capability
// ─────────────────────────────────────────────

/**
 * Two roundups by category rather than by company size — OKR software and 360
 * feedback tools — built on the segment guide's shape and rendered by the same
 * page, under the same rules: the CLEAR Talent column states what the product
 * does, competitor cells are `strong` only where the vendor presents the
 * capability itself and `confirm` everywhere else, no competitor price, no
 * rating markup, no ranking and no link to a campaign page. In particular the
 * 360 roundup never links /360-feedback-software, which is a paid destination.
 *
 * They take adjacent queries ("best OKR software for HR teams", "best 360
 * feedback tools") that the hub's "best performance management software" does
 * not answer, and each feeds the solution page that owns the head term: its
 * CLEAR Talent card links there, and that page links back.
 */
export const CATEGORY_GUIDE_REVIEWED = { label: 'October 5, 2026', iso: '2026-10-05' };

/** Our own price, at the same two headcounts the segment guide uses, for a guide whose subject is one capability. */
const categoryPriceSummary = (planNote: string) =>
  `${STARTING_PRICE_SENTENCE} ${planNote} At 50 staff users that is ${priceAt(BASIC_TIER, 50)} a month on Basic or ${priceAt(PRO_TIER, 50)} on Pro; at ${PUBLISHED_STAFF_CAP} staff users, ${priceAt(BASIC_TIER, PUBLISHED_STAFF_CAP)} on Basic or ${priceAt(PRO_TIER, PUBLISHED_STAFF_CAP)} on Pro. Annual billing takes ${ANNUAL_DISCOUNT_PERCENT}% off the plan price, and there is a ${TRIAL_POLICY.toLowerCase()}. Past ${PUBLISHED_STAFF_CAP} staff users the Enterprise plan is quoted to your scope.`;

const CATEGORY_SIGN_OFF = [
  'Get the module list, how a user or seat is counted, any minimum seat count, the implementation fee and the renewal terms in writing.',
];

export const OKR_SOFTWARE_GUIDE: SegmentGuide = {
  path: OKR_SOFTWARE_GUIDE_PATH,
  seoTitle: 'Best OKR Software for HR Teams: 9 Tools Compared',
  metaDescription:
    'Compare nine OKR tools for HR teams on goal linkage, weighted KPIs, check-ins and how goal scores reach the review, with honest fit notes and a buyer checklist.',
  primaryKeyword: 'best OKR software for HR teams',
  keywords: [
    'OKR software comparison',
    'OKR tools for HR',
    'goal setting software comparison',
    'OKR and KPI software',
  ],
  eyebrow: 'Buyer’s guide · OKR software',
  h1: 'The best OKR software for HR teams, compared',
  breadcrumbName: 'Best OKR software',
  excerpt:
    'Nine OKR tools an HR team is likely to shortlist, the criteria that matter when goals feed the performance review, an honest fit check and a checklist for the evaluation.',
  heroImage: '/screenshots/04-organizational-goals-main.webp',
  heroImageAlt: 'Organisational goal cascade in CLEAR Talent',
  reviewed: CATEGORY_GUIDE_REVIEWED,
  itemListName: 'OKR software compared for HR teams',
  copy: {
    criteriaEyebrow: 'What to look for',
    criteriaTitle: 'What separates one OKR tool from another when HR owns the outcome.',
    criteriaSub:
      'Every tool on this list can record an objective and its key results. These are the questions that decide whether goals stay connected to strategy all year and reach the review intact.',
    tableSubject: 'OKR software for HR teams',
    tableCaptionSuffix: 'compared as OKR software for HR teams',
    faqTitle: 'Questions HR teams ask about OKR software.',
    faqSub: 'The answers an OKR shortlist usually turns on.',
    checklistSub:
      'A repeatable process for an OKR evaluation — and the one that stops the most polished goal dashboard from winning by default.',
    ctaTitle: 'Bring your own goal cascade to the demo.',
    ctaSub:
      'Show us one company objective, the teams that serve it and the way goals reach your reviews, and judge CLEAR Talent on how it handles them — with the same scenarios you give every other vendor.',
    demoSource: 'okr-software-guide',
  },
  intro: [
    'Most lists of the best OKR software are written for whoever runs strategy. HR teams buy OKR software for a slightly different reason: the goals people set at the start of a cycle are what they will be assessed against at the end of it, so the tool has to keep those goals connected to company objectives all year and then carry them into the review. That narrows the shortlist and changes what matters in a demo.',
    'This guide compares nine tools that come up when HR teams shortlist OKR software: CLEAR Talent, Betterworks, Lattice, 15Five, Leapsome, Perdoo, Quantive, WorkBoard and Profit.co. Some are dedicated OKR and strategy-execution platforms; others manage OKRs inside a wider performance product. We build CLEAR Talent, so we describe it precisely and describe the others only as they describe themselves. Where we cannot speak for another vendor, the table says “Confirm” and the vendor notes give you the question to ask — never a claimed gap.',
  ],
  selectionCriteria: [
    {
      title: 'Linkage that holds',
      text: 'An OKR tool earns its place by keeping individual goals tied to the objectives they serve. Ask whether a goal can exist without a parent, and what happens to the cascade when a parent objective changes mid-quarter.',
    },
    {
      title: 'OKRs and KPIs in one place',
      text: 'Most organisations run both: objectives that change each quarter, and measures a role carries all year. Check whether KPIs can be weighted, banded and linked to goals, or whether they live in a second tool.',
    },
    {
      title: 'What happens at review time',
      text: 'For an HR team the question is not only whether goals are tracked but whether goal results reach the performance review without being exported and retyped — and how much of the rating they carry.',
    },
    {
      title: 'Check-ins people keep up',
      text: 'A goal set at the start of the year and next opened at the end of it is documentation, not management. Look at check-in prompts, at-risk flags and how much effort a progress update takes on a phone.',
    },
    {
      title: 'Priorities stated as weights',
      text: 'Once someone holds more than two goals, the weights are the clearest priority signal they get. Ask how weighting works, whether weights can total more than 100%, and what happens when a new priority lands mid-cycle.',
    },
    {
      title: 'Who will own it',
      text: 'OKR programmes are often run by strategy or operations while HR runs reviews. Decide who will administer the tool, and ask each vendor to let that person build a goal cycle in the demo, without their consultant driving.',
    },
  ],
  vendors: [
    {
      id: 'clear',
      name: 'CLEAR Talent',
      positioning:
        'A performance and development platform that manages OKRs, SMART goals and weighted KPIs through Strategic Linked Goals, then carries goal scores into the performance review beside competency assessment and 360° feedback. Built to run alongside the HRIS you already have.',
      worthALook:
        'Goals are meant to feed the review, you want individual goals that cannot exist without a parent objective, and you want OKRs and KPIs in the same tool as your review cycles.',
      askThem: '',
      href: '/solutions/okr-goal-setting-software',
    },
    {
      id: 'betterworks',
      name: 'Betterworks',
      positioning:
        'Enterprise goal and OKR management, with conversations, continuous feedback, performance reviews and calibration alongside, typically deployed in large organisations next to an existing HCM.',
      worthALook:
        'An enterprise-scale goal programme across a large, structurally complex organisation is the outcome you are buying.',
      askThem:
        'Whether an individual goal can be required to link to a parent objective, and what implementation involves at your headcount.',
      href: 'https://www.betterworks.com/',
      headToHead: '/clear-talent-vs-betterworks',
    },
    {
      id: 'lattice',
      name: 'Lattice',
      positioning:
        'A people platform covering performance reviews, goals and OKRs, 1:1s, engagement surveys, career and development tracks and analytics, and it has extended beyond performance into core HR.',
      worthALook:
        'You want OKRs, reviews and engagement surveys from one supplier and are comfortable consolidating more of the HR stack with it.',
      askThem:
        'How an individual goal is tied to a parent objective, and whether weighted KPIs with performance bands can be modelled.',
      href: 'https://lattice.com/',
      headToHead: '/clear-talent-vs-lattice',
    },
    {
      id: 'fifteenFive',
      name: '15Five',
      positioning:
        'Continuous performance and manager enablement: weekly check-ins, 1-on-1s, OKRs, performance reviews, engagement surveys, and manager coaching and training content.',
      worthALook:
        'You want OKRs to sit inside a weekly check-in habit, and manager training content matters as much as the goal tracker.',
      askThem:
        'Whether the link from an individual objective to its parent can be enforced, and how goal progress is used in a review.',
      href: 'https://www.15five.com/',
      headToHead: '/clear-talent-vs-15five',
    },
    {
      id: 'leapsome',
      name: 'Leapsome',
      positioning:
        'A people enablement platform covering performance reviews, goals and OKRs, 360° feedback, engagement surveys, 1:1 meetings, and learning and onboarding.',
      worthALook:
        'You want goals and OKRs inside a broader people platform, with engagement surveys and learning from the same supplier.',
      askThem: 'Which modules sit in the tier you are quoted, and how goal progress is used in a review cycle.',
      href: 'https://www.leapsome.com/',
      headToHead: '/clear-talent-vs-leapsome',
    },
    {
      id: 'perdoo',
      name: 'Perdoo',
      positioning:
        'Goal and strategy-execution software built around OKRs and KPIs, with a strategy map that shows how goals connect and regular check-ins on progress.',
      worthALook:
        'The main need is an organisation-wide OKR and KPI programme, and performance reviews are run elsewhere or are a secondary concern.',
      askThem:
        'Whether performance reviews are part of the product you are quoted, and how goal results would reach the system that runs your reviews.',
      href: 'https://www.perdoo.com/',
    },
    {
      id: 'quantive',
      name: 'Quantive',
      positioning:
        'A strategy-execution platform with OKRs at its core, positioned around connecting objectives to business data, planning and progress reporting.',
      worthALook:
        'Strategy execution driven by business data is the priority, and the goal programme is owned by strategy or operations rather than HR.',
      askThem: 'How individual goals and performance reviews are handled, or which system they would stay in.',
      href: 'https://quantive.com/',
    },
    {
      id: 'workBoard',
      name: 'WorkBoard',
      positioning:
        'A strategy-execution platform built around OKRs, aimed at aligning large organisations on their objectives and the operating rhythm around them.',
      worthALook:
        'You are a large organisation running OKRs as the operating cadence for leadership and teams, with reviews handled in a separate HR system.',
      askThem:
        'What the minimum contract and implementation look like at your headcount, and how goal data would reach your review process.',
      href: 'https://www.workboard.com/',
    },
    {
      id: 'profitCo',
      name: 'Profit.co',
      positioning:
        'OKR software with KPI tracking alongside, and further modules offered around the OKR core.',
      worthALook:
        'You want a dedicated OKR and KPI tool now, with the option of adding further modules from the same supplier later.',
      askThem: 'Which modules your quote includes, and how OKR results are used in performance reviews.',
      href: 'https://www.profit.co/',
    },
  ],
  criteria: [
    {
      criterion: 'OKRs cascaded from organisation objectives',
      why: 'Team and individual goals visibly serve what the company is trying to do.',
      clearNote: 'Organisation objectives cascade to department, team and individual goals, with a visual cascade map.',
      statuses: {
        clear: 'strong',
        betterworks: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'strong',
        perdoo: 'strong',
        quantive: 'strong',
        workBoard: 'strong',
        profitCo: 'strong',
      },
    },
    {
      criterion: 'Individual goals that must link to a parent',
      why: 'Orphaned goals cannot quietly accumulate between planning and review.',
      clearNote: 'Strategic Linked Goals will not accept an individual goal without a parent objective.',
      statuses: {
        clear: 'strong',
        betterworks: 'confirm',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        perdoo: 'confirm',
        quantive: 'confirm',
        workBoard: 'confirm',
        profitCo: 'confirm',
      },
    },
    {
      criterion: 'Weighted KPIs alongside OKRs',
      why: 'Ongoing measures and quarterly objectives are scored on the same terms.',
      clearNote: 'A KPI library of reusable, role-aware templates with performance bands, weightings and guardrails.',
      statuses: {
        clear: 'strong',
        betterworks: 'confirm',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        perdoo: 'strong',
        quantive: 'confirm',
        workBoard: 'confirm',
        profitCo: 'strong',
      },
    },
    {
      criterion: 'Check-ins and at-risk flags during the cycle',
      why: 'A drifting goal shows up in month two, not at review time.',
      clearNote: 'Check-in prompts per goal, at-risk flags from pace and check-in signals, and end-of-cycle score projections.',
      statuses: {
        clear: 'strong',
        betterworks: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'confirm',
        perdoo: 'confirm',
        quantive: 'confirm',
        workBoard: 'confirm',
        profitCo: 'confirm',
      },
    },
    {
      criterion: 'Goals and performance reviews in one platform',
      why: 'Goal results reach the review without being exported and retyped.',
      clearNote: 'Goal scores roll into the review automatically; the weight goals carry against competencies is set per level.',
      statuses: {
        clear: 'strong',
        betterworks: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'strong',
        perdoo: 'confirm',
        quantive: 'confirm',
        workBoard: 'confirm',
        profitCo: 'confirm',
      },
    },
    {
      criterion: 'Competency assessment beside goal results',
      why: 'A rating reflects how someone worked, not only what they delivered.',
      clearNote: 'Competency ratings against behavioural indicators form a second stream of the rating and can require written evidence.',
      statuses: {
        clear: 'strong',
        betterworks: 'confirm',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        perdoo: 'confirm',
        quantive: 'confirm',
        workBoard: 'confirm',
        profitCo: 'confirm',
      },
    },
    {
      criterion: 'AI drafting with human approval',
      why: 'Help past the blank page, without goals nobody has checked.',
      clearNote: 'Ava drafts goals, KPIs and weightings for a role; output is labelled, editable and can be switched off. AI scope varies by plan.',
      statuses: {
        clear: 'strong',
        betterworks: 'confirm',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        perdoo: 'confirm',
        quantive: 'confirm',
        workBoard: 'confirm',
        profitCo: 'confirm',
      },
    },
    {
      criterion: 'Mobile and multilingual check-ins',
      why: 'People away from a desk, or not working in English, keep their goals current.',
      clearNote: 'Check-ins on the web or the iOS and Android apps, in 16 languages; the mobile apps are included from the Pro plan.',
      statuses: {
        clear: 'strong',
        betterworks: 'confirm',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        perdoo: 'confirm',
        quantive: 'confirm',
        workBoard: 'confirm',
        profitCo: 'confirm',
      },
    },
    {
      criterion: 'Strategy execution beyond people goals',
      why: 'Matters if OKRs run the business cadence as well as the people process.',
      clearNote: 'Goals, OKRs and KPIs are managed as part of performance management. CLEAR Talent is not a standalone strategy-execution suite.',
      statuses: {
        clear: 'limited',
        betterworks: 'confirm',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        perdoo: 'strong',
        quantive: 'strong',
        workBoard: 'strong',
        profitCo: 'confirm',
      },
    },
  ],
  clearFit: [
    'Goals are meant to feed the performance review. Goal scores roll in automatically and sit beside competency ratings and 360° feedback in one composite rating whose parts stay visible.',
    'You want linkage enforced rather than requested. Strategic Linked Goals will not accept an individual goal without a parent objective, and the goal alignment view opens on cascade gaps, unlinked KPIs and goals without an owner.',
    'You run OKRs and KPIs together. A central KPI library holds reusable, role-aware templates with performance bands, weightings and guardrails, and a person’s goal weights for a period cannot total more than 100%.',
    'Different teams work to different rhythms. Quarterly OKRs and annual goals run side by side, with each quarter’s goal scores rolling up into the annual review.',
    `You want a price to plan against before the first sales call. Goal and OKR management and KPI management are on every plan, from the Basic plan at USD ${BASIC_TIER.monthlyUsd} a month, and there is a ${TRIAL_POLICY.toLowerCase()}.`,
  ],
  clearNotFit: [
    'You only need to track objectives and will never assess anyone against them. A dedicated OKR tool may be all you need — our own OKR page says the same.',
    'OKRs mainly run the business cadence — strategy reporting and operating reviews for leadership — rather than the people process. Dedicated strategy-execution platforms are positioned around that.',
    'Engagement or pulse surveys are part of this purchase. CLEAR Talent has no survey product.',
    'You need goals to sync live with your HRIS. There is no prebuilt connector today: people and structure come in by CSV or Excel import or API access.',
    `You are past ${PUBLISHED_STAFF_CAP} staff users and want a list price rather than a quote. Above that headcount CLEAR Talent is priced on the Enterprise plan.`,
  ],
  pricing: {
    points: segmentPricePoints,
    aboveCapLabel: `${PUBLISHED_STAFF_CAP + 1}+`,
    summary: categoryPriceSummary(
      'Goal and OKR management and KPI management are included on every plan, Basic included.',
    ),
    rivals: SEGMENT_GUIDE.pricing.rivals,
    questions: PRICING_QUESTIONS,
  },
  checklist: [
    {
      stage: 'Before the demos',
      items: [
        'Decide who owns the goal programme — HR, strategy or operations — and whether goals will feed performance reviews. The answer changes the shortlist.',
        'Write down your cadences: which levels work to quarterly OKRs, which to annual goals, and which KPIs roles carry all year.',
        'Write three to five scenarios every vendor must show live: a company objective cascaded to one individual, a goal re-weighted when a new priority lands mid-quarter, a check-in on a phone, and goal results reaching a review.',
        'Ask for every quote at today’s headcount and your expected headcount in three years.',
      ],
    },
    {
      stage: 'In the demo',
      items: [
        'Try to create an individual goal with no parent objective, and see what happens.',
        'Ask how progress on a parent objective is calculated from its child goals, KPIs and key results.',
        'Ask what the AI drafts, whether its output is labelled, and whether it can be switched off per team or cycle.',
        'Put a manager who will actually run check-ins in the room.',
      ],
    },
    {
      stage: 'Before you sign',
      items: [
        ...CATEGORY_SIGN_OFF,
        'Confirm how goal data comes out — formats, and what happens at the end of the contract.',
        'Pilot one quarter with one department before rolling out to everyone.',
      ],
    },
  ],
  faqs: [
    {
      q: 'What is the best OKR software for HR teams?',
      a: 'There is no single winner, and the answer depends on what the goals are for. If goals are meant to feed performance reviews, look for a tool where reviews live on the same platform and goal results reach the review without being retyped — CLEAR Talent, Betterworks, Lattice, 15Five and Leapsome all present reviews alongside goals. If OKRs are mainly a strategy and operating cadence owned outside HR, strategy-execution platforms such as Perdoo, Quantive and WorkBoard are positioned around that. CLEAR Talent is built for the first case: it will not accept an individual goal without a parent objective, and goal scores sit beside competency ratings in the review.',
    },
    {
      q: 'Should OKR scores decide performance ratings?',
      a: 'They usually inform the rating rather than make up all of it. A goal score says what was delivered; it says nothing about how. In CLEAR Talent goal scores roll into the review automatically and form one stream of a composite rating, competency assessment forms the other, and the weighting between them is configurable per level — so you decide how much of the rating goals carry, and a calibration session can question a goal score without disturbing the rest of the rating.',
    },
    {
      q: 'What is the difference between OKR software and KPI software?',
      a: 'OKRs describe change: an objective and the measurable key results that show it was achieved, usually on a quarterly rhythm. KPIs describe the health of ongoing work and are carried by a role all year. Some tools handle one far better than the other. CLEAR Talent holds both on one platform: OKRs, SMART goals and weighted KPIs, with KPIs drawn from a central library with performance bands and guardrails, all rolling up to the same organisation objectives.',
    },
    {
      q: 'How much does OKR software cost?',
      a: `Pricing models differ — a plan fee with per-user add-ons, a per-seat rate, modules sold separately — so headline figures mislead. CLEAR Talent includes goal, OKR and KPI management on every plan: at 50 staff users that comes to ${priceAt(BASIC_TIER, 50)} a month on the Basic plan or ${priceAt(PRO_TIER, 50)} on Pro, with ${ANNUAL_DISCOUNT_PERCENT}% off the plan price for annual billing, and past ${PUBLISHED_STAFF_CAP} staff users the Enterprise plan is quoted. For other vendors, read their current pricing page and get a written quote at the same headcount, term and module list before comparing.`,
    },
    {
      q: 'Is CLEAR Talent a dedicated OKR tool?',
      a: 'No. It manages OKRs, goals and KPIs as part of a performance management platform, so the same goals connect to reviews, 360° feedback, competency assessment and development plans. If you only need to track objectives and never assess anyone against them, a dedicated OKR tool may be all you need.',
    },
  ],
  sources: [
    { label: 'CLEAR Talent OKR & goal setting software', href: 'https://www.clear-talent.com/solutions/okr-goal-setting-software' },
    { label: 'CLEAR Talent pricing', href: 'https://www.clear-talent.com/pricing' },
    { label: 'Betterworks', href: 'https://www.betterworks.com/' },
    { label: 'Lattice', href: 'https://lattice.com/' },
    { label: '15Five', href: 'https://www.15five.com/' },
    { label: 'Leapsome', href: 'https://www.leapsome.com/' },
    { label: 'Perdoo', href: 'https://www.perdoo.com/' },
    { label: 'Quantive', href: 'https://quantive.com/' },
    { label: 'WorkBoard', href: 'https://www.workboard.com/' },
    { label: 'Profit.co', href: 'https://www.profit.co/' },
  ],
  internalLinks: [
    { label: 'OKR & goal setting software', href: '/solutions/okr-goal-setting-software' },
    { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
    { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
    { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
    { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
    { label: 'Glossary: OKR vs KPI', href: '/glossary/okr-vs-kpi' },
    { label: 'Guide: cascading company goals', href: '/blog/how-to-cascade-company-goals' },
    { label: 'Best 360 feedback tools', href: FEEDBACK_360_GUIDE_PATH },
    { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
    { label: 'CLEAR Talent pricing', href: '/pricing' },
  ],
};

export const FEEDBACK_360_GUIDE: SegmentGuide = {
  path: FEEDBACK_360_GUIDE_PATH,
  seoTitle: 'Best 360 Feedback Tools: 9 Platforms Compared',
  metaDescription:
    'Compare nine 360 feedback tools on rater groups, anonymity thresholds, competency-linked questions and what happens after the report, with an honest fit check.',
  primaryKeyword: 'best 360 feedback tools',
  keywords: [
    '360 feedback tools comparison',
    '360 review software comparison',
    'multi-rater feedback tools',
    '360 degree feedback platforms',
  ],
  eyebrow: 'Buyer’s guide · 360 feedback',
  h1: 'The best 360 feedback tools, compared',
  breadcrumbName: 'Best 360 feedback tools',
  excerpt:
    'Nine tools buyers shortlist for 360 feedback, compared on rater rules, anonymity, competency-linked questions and what happens after the report — with an honest fit check and a checklist.',
  heroImage: '/screenshots/12-feedback-main.webp',
  heroImageAlt: 'Continuous feedback and check-in view in CLEAR Talent',
  reviewed: CATEGORY_GUIDE_REVIEWED,
  itemListName: '360 feedback tools compared',
  copy: {
    criteriaEyebrow: 'What to look for',
    criteriaTitle: 'What separates a 360 people trust from one they rush through.',
    criteriaSub:
      'Every tool on this list can send a questionnaire to several raters. These are the questions that decide whether the answers are candid, comparable and acted on.',
    tableSubject: '360 feedback tools',
    tableCaptionSuffix: 'compared as 360 feedback tools',
    faqTitle: 'Questions buyers ask about 360 feedback tools.',
    faqSub: 'The answers a 360 shortlist usually turns on.',
    checklistSub:
      'A repeatable process for a 360 evaluation — and the one that stops the best-looking sample report from winning by default.',
    ctaTitle: 'Bring your own rater rules to the demo.',
    ctaSub:
      'Show us the competencies a 360 should measure, who should rate whom and what the minimum group size is, and judge CLEAR Talent on how it handles them — with the same scenarios you give every other vendor.',
    demoSource: '360-feedback-guide',
  },
  intro: [
    'A 360 feedback tool is easy to buy and hard to get value from. Collecting ratings from a manager, peers and direct reports is the simple part. Whether raters trust the anonymity, whether the questions measure the behaviours the role actually needs, and whether anything happens after the report is delivered are what separate a 360 that changes how someone works from a report nobody opens twice.',
    'This guide compares nine tools buyers shortlist for 360 feedback: CLEAR Talent, Culture Amp, Lattice, Leapsome, 15Five, Qualtrics, Spidergap, PerformYard and Betterworks. Some run 360s inside a performance or employee-experience platform; Spidergap is a dedicated 360 tool. We build CLEAR Talent, so we describe it precisely and describe the others only as they describe themselves. Where we cannot speak for another vendor, the table says “Confirm” and the vendor notes give you the question to ask — never a claimed gap.',
  ],
  selectionCriteria: [
    {
      title: 'Anonymity people believe',
      text: 'Raters are candid when they trust that they cannot be identified. Ask how raters are grouped, what the minimum number of raters per group is, and exactly what a manager can and cannot see.',
    },
    {
      title: 'Questions tied to the role',
      text: 'Open-text surveys produce quotes; structured questions produce something comparable. Check whether questions come from your own competency model and whether a rating can carry written evidence.',
    },
    {
      title: 'Rater selection with a check',
      text: 'Who nominates raters, and who approves the list, decides whether a 360 is balanced. Look for configurable rater groups across managers, peers, direct reports and other stakeholders.',
    },
    {
      title: 'A cadence that fits',
      text: 'Some organisations run 360s with the annual review; others after a project or before a promotion. Confirm the tool can run a 360 on its own schedule, not only inside a review cycle.',
    },
    {
      title: 'Something happens next',
      text: 'A 360 earns its cost when it changes what someone works on. Ask what the report leads to — a development plan, a coaching conversation, or nothing — and who owns that step.',
    },
    {
      title: 'Effort for raters',
      text: 'A rater asked for five 360s in a fortnight will rush the last three. Look at how long a 360 takes to complete, whether it works on a phone and in the rater’s language, and how reminders are handled.',
    },
  ],
  vendors: [
    {
      id: 'clear',
      name: 'CLEAR Talent',
      positioning:
        'A performance and development platform whose 360° feedback runs against your competency framework, with configurable rater groups, anonymity within each group, minimum-rater thresholds and a suggested development plan from the results.',
      worthALook:
        'You want 360 input structured against the competencies in each role profile, and you want the report to lead into a development plan rather than a filed PDF.',
      askThem: '',
      href: '/solutions/360-degree-feedback-software',
    },
    {
      id: 'cultureAmp',
      name: 'Culture Amp',
      positioning:
        'An employee experience platform best known for engagement surveys and people analytics, with performance and development products alongside, including 360 feedback.',
      worthALook:
        'Engagement surveys matter at least as much as 360s, and you want both, with people analytics, from one supplier.',
      askThem: 'Whether 360 feedback is priced with the performance product or separately, and what its anonymity rules are.',
      href: 'https://www.cultureamp.com/',
      headToHead: '/clear-talent-vs-culture-amp',
    },
    {
      id: 'lattice',
      name: 'Lattice',
      positioning:
        'A people platform covering performance reviews with multi-rater input, goals and OKRs, 1:1s, engagement surveys, career and development tracks and analytics.',
      worthALook:
        'You want 360 input inside review cycles on a broader people platform, with engagement surveys from the same supplier.',
      askThem:
        'How rater groups are configured, what the minimum-rater rules are, and whether input is structured against a competency model.',
      href: 'https://lattice.com/',
      headToHead: '/clear-talent-vs-lattice',
    },
    {
      id: 'leapsome',
      name: 'Leapsome',
      positioning:
        'A people enablement platform covering performance reviews, 360° feedback, goals and OKRs, engagement surveys, 1:1 meetings, and learning and onboarding.',
      worthALook:
        'You want 360 feedback inside a broader people platform that also covers engagement surveys, learning and onboarding.',
      askThem: 'How rater anonymity and thresholds are configured, and whether questions can be built from your competency framework.',
      href: 'https://www.leapsome.com/',
      headToHead: '/clear-talent-vs-leapsome',
    },
    {
      id: 'fifteenFive',
      name: '15Five',
      positioning:
        'Continuous performance and manager enablement: weekly check-ins, 1-on-1s, OKRs, performance reviews, engagement surveys, and manager coaching and training content.',
      worthALook:
        'Manager capability and a weekly check-in habit are the main goal, and multi-rater input is one part of that.',
      askThem: 'How multi-rater feedback is configured, and whether it can run outside a review cycle.',
      href: 'https://www.15five.com/',
      headToHead: '/clear-talent-vs-15five',
    },
    {
      id: 'qualtrics',
      name: 'Qualtrics',
      positioning:
        'An experience management company whose employee experience products include engagement and lifecycle surveys and 360 feedback, with survey analytics across them.',
      worthALook:
        'You already run employee surveys on Qualtrics, or want research-grade survey design and analytics behind a 360 programme.',
      askThem:
        'Which products the 360 requires, and how its results reach the system that runs your reviews and development plans.',
      href: 'https://www.qualtrics.com/',
    },
    {
      id: 'spidergap',
      name: 'Spidergap',
      positioning:
        'A dedicated 360 feedback tool focused on running 360 reviews and producing feedback reports, rather than a full performance management suite.',
      worthALook:
        'You want 360s and nothing else — run as a standalone exercise, perhaps beside a leadership programme — and reviews and goals live elsewhere.',
      askThem: 'How the questionnaire can be adapted to your competency framework, and how results move into your development process.',
      href: 'https://www.spidergap.com/',
    },
    {
      id: 'performYard',
      name: 'PerformYard',
      positioning:
        'Performance management software for HR teams, built around flexible review processes — annual, quarterly or continuous — with goals and feedback alongside.',
      worthALook: 'You want a configurable review process run by HR, with multi-rater input as one part of it.',
      askThem: 'How 360 rater groups and anonymity are set up, and whether input maps to a competency model.',
      href: 'https://www.performyard.com/',
    },
    {
      id: 'betterworks',
      name: 'Betterworks',
      positioning:
        'Enterprise goal and OKR management, with conversations, continuous feedback, performance reviews and calibration alongside, typically deployed in large organisations next to an existing HCM.',
      worthALook:
        'Multi-rater input is one part of an enterprise goal and review programme in a large organisation, rather than the main purchase.',
      askThem: 'How multi-rater feedback is configured and what the anonymity rules are.',
      href: 'https://www.betterworks.com/',
      headToHead: '/clear-talent-vs-betterworks',
    },
  ],
  criteria: [
    {
      criterion: '360° feedback as part of the product',
      why: 'The baseline: multi-rater input collected and reported in the tool.',
      clearNote: 'Reports cover strengths, blind spots and development priorities, as a PDF or a shareable link.',
      statuses: {
        clear: 'strong',
        cultureAmp: 'strong',
        lattice: 'strong',
        leapsome: 'strong',
        fifteenFive: 'confirm',
        qualtrics: 'strong',
        spidergap: 'strong',
        performYard: 'confirm',
        betterworks: 'confirm',
      },
    },
    {
      criterion: 'Rater groups across managers, peers, reports and stakeholders',
      why: 'A balanced 360 hears from every direction the person works in.',
      clearNote: 'Configurable rater groups; employees nominate their raters and managers approve the list.',
      statuses: {
        clear: 'strong',
        cultureAmp: 'confirm',
        lattice: 'confirm',
        leapsome: 'confirm',
        fifteenFive: 'confirm',
        qualtrics: 'confirm',
        spidergap: 'confirm',
        performYard: 'confirm',
        betterworks: 'confirm',
      },
    },
    {
      criterion: 'Anonymity within groups and minimum-rater thresholds',
      why: 'In small teams, anonymity breaks easily unless thresholds are enforced.',
      clearNote: 'Raters are anonymised within their group and thresholds are enforced; managers see aggregated group data only.',
      statuses: {
        clear: 'strong',
        cultureAmp: 'confirm',
        lattice: 'confirm',
        leapsome: 'confirm',
        fifteenFive: 'confirm',
        qualtrics: 'confirm',
        spidergap: 'confirm',
        performYard: 'confirm',
        betterworks: 'confirm',
      },
    },
    {
      criterion: 'Questions structured against your competency model',
      why: 'Feedback maps to the behaviours the role actually needs.',
      clearNote: 'Each rater scores the competencies in the role profile, on a scale you control, with optional written evidence.',
      statuses: {
        clear: 'strong',
        cultureAmp: 'confirm',
        lattice: 'confirm',
        leapsome: 'confirm',
        fifteenFive: 'confirm',
        qualtrics: 'confirm',
        spidergap: 'confirm',
        performYard: 'confirm',
        betterworks: 'confirm',
      },
    },
    {
      criterion: '360s on their own cadence',
      why: 'Run after a project or before a promotion, not only at year end.',
      clearNote: 'Any cadence — quarterly, after a project, on promotion — independently of the annual review.',
      statuses: {
        clear: 'strong',
        cultureAmp: 'confirm',
        lattice: 'confirm',
        leapsome: 'confirm',
        fifteenFive: 'confirm',
        qualtrics: 'confirm',
        spidergap: 'strong',
        performYard: 'confirm',
        betterworks: 'confirm',
      },
    },
    {
      criterion: 'From report to development plan',
      why: 'The 360 changes what someone works on next.',
      clearNote: 'A suggested IDP from the lowest-scoring, highest-importance competencies. IDPs are included from the Pro plan.',
      statuses: {
        clear: 'strong',
        cultureAmp: 'confirm',
        lattice: 'confirm',
        leapsome: 'confirm',
        fifteenFive: 'confirm',
        qualtrics: 'confirm',
        spidergap: 'confirm',
        performYard: 'confirm',
        betterworks: 'confirm',
      },
    },
    {
      criterion: 'AI summaries a person approves',
      why: 'Faster synthesis of rater input, without output nobody has checked.',
      clearNote: 'Ava summarises multi-rater input into themes; output is labelled, editable and approved by a person. AI scope varies by plan.',
      statuses: {
        clear: 'strong',
        cultureAmp: 'confirm',
        lattice: 'confirm',
        leapsome: 'confirm',
        fifteenFive: 'confirm',
        qualtrics: 'confirm',
        spidergap: 'confirm',
        performYard: 'confirm',
        betterworks: 'confirm',
      },
    },
    {
      criterion: 'Mobile and multilingual raters',
      why: 'Raters away from a desk, or not working in English, still respond.',
      clearNote: '16 languages; native iOS and Android apps with offline support are included from the Pro plan.',
      statuses: {
        clear: 'strong',
        cultureAmp: 'confirm',
        lattice: 'confirm',
        leapsome: 'confirm',
        fifteenFive: 'confirm',
        qualtrics: 'confirm',
        spidergap: 'confirm',
        performYard: 'confirm',
        betterworks: 'confirm',
      },
    },
    {
      criterion: 'Employee engagement surveys',
      why: 'Only matters if surveys are part of this purchase — then it matters a lot.',
      clearNote: 'Not a CLEAR Talent capability. If surveys are required, you would need a second tool.',
      statuses: {
        clear: 'none',
        cultureAmp: 'strong',
        lattice: 'strong',
        leapsome: 'strong',
        fifteenFive: 'strong',
        qualtrics: 'strong',
        spidergap: 'confirm',
        performYard: 'confirm',
        betterworks: 'confirm',
      },
    },
  ],
  clearFit: [
    'You want 360 input to measure the behaviours each role needs. Raters score the competencies in the role profile, on a scale you control, with optional written evidence.',
    'Anonymity has to hold in small teams. Raters are anonymised within their group, minimum-rater thresholds are enforced, and managers see only aggregated, anonymised group data.',
    'The 360 should lead somewhere. Each one generates a suggested development plan from the lowest-scoring, highest-importance competencies, on the same record as reviews, goals and calibration.',
    'Your raters work across languages or away from desks: 16 languages, and native iOS and Android apps from the Pro plan.',
    `You want a price to plan against before the first sales call: the Basic plan is USD ${BASIC_TIER.monthlyUsd} a month, with published per-user rates up to ${PUBLISHED_STAFF_CAP} staff users, and a ${TRIAL_POLICY.toLowerCase()}.`,
  ],
  clearNotFit: [
    'Engagement or pulse surveys are part of this purchase. CLEAR Talent has no survey product, and several tools on this list combine the two.',
    'You want a one-off 360 for a leadership programme and nothing else. A dedicated 360 tool may be simpler than adopting a performance platform.',
    'You want a validated psychometric instrument. CLEAR Talent structures 360 input against the competency framework you define; it is not sold as a validated assessment.',
    'You need results to sync live with your HRIS. There is no prebuilt connector today: people and structure come in by CSV or Excel import or API access, and results go out as CSV, Excel or PDF.',
    `You are past ${PUBLISHED_STAFF_CAP} staff users and want a list price rather than a quote. Above that headcount CLEAR Talent is priced on the Enterprise plan.`,
  ],
  pricing: {
    points: segmentPricePoints,
    aboveCapLabel: `${PUBLISHED_STAFF_CAP + 1}+`,
    summary: categoryPriceSummary(
      'The pricing page lists performance review cycles and continuous feedback on every plan, and individual development plans — where a 360° becomes a suggested development plan — from Pro.',
    ),
    rivals: SEGMENT_GUIDE.pricing.rivals,
    questions: PRICING_QUESTIONS,
  },
  checklist: [
    {
      stage: 'Before the demos',
      items: [
        'Decide what the 360 is for — development only, or input to ratings — and tell raters before the first one runs.',
        'Write down your rater rules: which groups, how many raters per group, who nominates and who approves.',
        'Pick the competencies the 360 will measure for two or three real roles, and ask every vendor to build those questionnaires live.',
        'Ask for every quote at today’s headcount and your expected headcount in three years.',
      ],
    },
    {
      stage: 'In the demo',
      items: [
        'Ask to see exactly what a manager sees when a rater group falls below the minimum.',
        'Complete a 360 as a rater on a phone, in front of the vendor.',
        'Ask what happens after the report: who sees it, and what it turns into.',
        'Ask what the AI summarises, whether its output is labelled, and whether it can be switched off.',
      ],
    },
    {
      stage: 'Before you sign',
      items: [
        ...CATEGORY_SIGN_OFF,
        'Confirm how 360 results come out — formats, and what happens at the end of the contract.',
        'Pilot with one team, and ask the raters afterwards whether they trusted the anonymity.',
      ],
    },
  ],
  faqs: [
    {
      q: 'What is the best 360 feedback tool?',
      a: 'It depends on what the 360 is for. If you want 360s as a standalone exercise, a dedicated tool such as Spidergap is positioned around exactly that. If engagement surveys matter as much as feedback, Culture Amp, Lattice, Leapsome, 15Five and Qualtrics all present both. If you want 360 input structured against role competencies and turned into a development plan on the same record as reviews and goals, CLEAR Talent is built for that. Whichever you shortlist, judge it on anonymity rules, question structure and what happens after the report rather than on the number of question templates.',
    },
    {
      q: 'How many raters should a 360 have?',
      a: 'Enough in each group that no individual can be identified, which is why a minimum per group matters more than a target total. Most 360s draw on the manager, several peers, direct reports where the person manages people, and sometimes cross-functional stakeholders. In CLEAR Talent rater groups are configurable, employees nominate and managers approve, and minimum-rater thresholds per group prevent identification.',
    },
    {
      q: 'Should 360 feedback be anonymous?',
      a: 'For peers and direct reports, usually yes: upward feedback in particular is rarely candid when the person being rated can see who said what. In CLEAR Talent raters are anonymised within their group, managers see only aggregated, anonymised group data, and raw rater identities are never displayed.',
    },
    {
      q: 'Is a 360 the same as a performance review?',
      a: 'No. A performance review records a judgement about performance over a period, usually by the manager; a 360 gathers perspectives from the people around someone. Many organisations keep 360s for development so that raters feel free to be candid. CLEAR Talent supports both: a 360 can run on its own cadence — quarterly, after a project, on promotion — or feed a review cycle, where 360 input sits beside goal scores and competency ratings.',
    },
    {
      q: 'How much do 360 feedback tools cost?',
      a: `Pricing models differ — a plan fee with per-user add-ons, a per-seat rate, a 360 sold as a separate product — so headline figures mislead. CLEAR Talent publishes its plans: at 50 staff users that comes to ${priceAt(BASIC_TIER, 50)} a month on the Basic plan or ${priceAt(PRO_TIER, 50)} on Pro, where individual development plans start, with ${ANNUAL_DISCOUNT_PERCENT}% off the plan price for annual billing, and past ${PUBLISHED_STAFF_CAP} staff users the Enterprise plan is quoted. For other vendors, read their current pricing page and get a written quote at the same headcount, term and module list before comparing.`,
    },
  ],
  sources: [
    { label: 'CLEAR Talent 360 degree feedback software', href: 'https://www.clear-talent.com/solutions/360-degree-feedback-software' },
    { label: 'CLEAR Talent pricing', href: 'https://www.clear-talent.com/pricing' },
    { label: 'Culture Amp', href: 'https://www.cultureamp.com/' },
    { label: 'Lattice', href: 'https://lattice.com/' },
    { label: 'Leapsome', href: 'https://www.leapsome.com/' },
    { label: '15Five', href: 'https://www.15five.com/' },
    { label: 'Qualtrics', href: 'https://www.qualtrics.com/' },
    { label: 'Spidergap', href: 'https://www.spidergap.com/' },
    { label: 'PerformYard', href: 'https://www.performyard.com/' },
    { label: 'Betterworks', href: 'https://www.betterworks.com/' },
  ],
  internalLinks: [
    { label: '360 degree feedback software', href: '/solutions/360-degree-feedback-software' },
    { label: 'Competency management software', href: '/solutions/competency-management-software' },
    { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
    { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
    { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
    { label: 'Guide: 100 example 360 feedback questions', href: '/blog/360-feedback-questions' },
    { label: 'Glossary: what is a 360 review?', href: '/glossary/360-degree-review' },
    { label: 'Best OKR software for HR teams', href: OKR_SOFTWARE_GUIDE_PATH },
    { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
    { label: 'CLEAR Talent pricing', href: '/pricing' },
  ],
};

export const CATEGORY_GUIDES: SegmentGuide[] = [OKR_SOFTWARE_GUIDE, FEEDBACK_360_GUIDE];

// ─────────────────────────────────────────────
// Segment siblings: startups, enterprises and remote teams
// ─────────────────────────────────────────────

/**
 * The small-business guide is the one "best … software" page in this cluster
 * whose clicks per impression are worth copying: a segment-qualified query is
 * narrower and less contested than the hub's head term. These three take the
 * same template, the same title shape and the same rules for three other kinds
 * of company, each with criteria of its own — price per seat and setup time for
 * a startup; SSO, audit trail and calibration for an enterprise; async check-ins
 * and languages for a remote team. CLEAR Talent's column says plainly where it
 * falls short (no SAML SSO or SCIM, no Slack or Teams app, no survey product,
 * mobile apps from Pro), competitor cells are `strong` only where the site
 * already publishes that vendor's positioning — the Workday and SAP cells follow
 * the hub's capability table — and no competitor price appears.
 *
 * "16 languages" is the AI-generated feedback, summaries and recommendations, as
 * the Europe page states it; the remote guide does not claim every screen.
 */
export const SEGMENT_SIBLINGS_REVIEWED = { label: 'October 17, 2026', iso: '2026-10-17' };

/** The year in the titles is the year of the review, so it cannot run ahead of the evidence. */
const SEGMENT_SIBLINGS_YEAR = SEGMENT_SIBLINGS_REVIEWED.iso.slice(0, 4);

const SIBLING_GUIDE_LINKS = {
  smallBusiness: { label: 'Choosing for 50–500 employees', href: SEGMENT_GUIDE_PATH },
  startups: { label: 'Choosing for a startup', href: STARTUP_GUIDE_PATH },
  enterprise: { label: 'Choosing for 500+ employees', href: ENTERPRISE_GUIDE_PATH },
  remote: { label: 'Choosing for a remote team', href: REMOTE_TEAMS_GUIDE_PATH },
};

export const STARTUP_GUIDE: SegmentGuide = {
  path: STARTUP_GUIDE_PATH,
  seoTitle: `Best Performance Management Software for Startups (${SEGMENT_SIBLINGS_YEAR})`,
  metaDescription:
    'Seven performance management tools for startups compared on price per seat, setup time, check-ins and room to grow — plus a fit check and a buyer checklist.',
  primaryKeyword: 'best performance management software for startups',
  keywords: [
    'performance management software for startups',
    'performance review software for startups',
    'startup performance management tools',
    'OKR and review software for startups',
  ],
  eyebrow: 'Buyer’s guide · Startups',
  h1: 'Performance management software for startups',
  breadcrumbName: 'Startups',
  excerpt:
    'Seven tools a startup is likely to shortlist, the criteria that matter when cash is tight and nobody does HR full-time, and a checklist for choosing before the first formal review cycle.',
  heroImage: '/screenshots/08-me-goals-main.webp',
  heroImageAlt: 'Personal goals view in CLEAR Talent',
  reviewed: SEGMENT_SIBLINGS_REVIEWED,
  itemListName: 'Performance management tools compared for startups',
  copy: {
    criteriaEyebrow: 'What to look for at a startup',
    criteriaTitle: 'What separates one tool from another before you have an HR team.',
    criteriaSub:
      'Most tools on this list can run a review. These are the questions that decide whether a founder or a first HR hire can set one up, afford it at next year’s headcount and keep using it once the company has doubled.',
    tableSubject: 'performance management tools for startups',
    tableCaptionSuffix: 'compared for startups',
    faqTitle: 'Questions founders and first HR hires ask.',
    faqSub: 'The answers a startup shortlist usually turns on.',
    checklistSub:
      'A process short enough to run alongside hiring and everything else — and the one that stops the slickest demo from winning by default.',
    ctaTitle: 'Bring your first review cycle to the demo.',
    ctaSub:
      'Show us your headcount, your goals and how you want your first managers to give feedback, and judge CLEAR Talent on how it handles them — with the same scenarios you give every other vendor.',
    demoSource: 'startups-guide',
  },
  intro: [
    'Startups tend to go looking for performance management software at the same moment. The founders can no longer give everyone feedback in person, the first people managers have just been hired, and the first conversation about pay or promotion is in the diary with nothing written down to support it. The question then is not which tool has the longest feature list. It is which one a founder or a first HR hire can set up without an implementation project, afford at next year’s headcount, and keep using as the company doubles.',
    'This guide compares seven tools that come up on startup shortlists: CLEAR Talent, Lattice, 15Five, Leapsome, Culture Amp, BambooHR and Personio. Some are performance and people platforms; BambooHR and Personio are HR systems with performance inside them, which is often the right first purchase for a company that has no HR system yet. We build CLEAR Talent, so we describe it precisely and describe the others only as they describe themselves. Where we cannot speak for another vendor, the table says “Confirm” and the vendor notes give you the question to ask — never a claimed gap.',
  ],
  selectionCriteria: [
    {
      title: 'The price at next year’s headcount',
      text: 'A startup can double in a year. Get every quote at today’s headcount and at the one in your hiring plan, ask whether a minimum seat count or an annual commitment applies, and convert plan fees and per-seat rates to the same monthly figure before comparing them.',
    },
    {
      title: 'Set-up without a project',
      text: 'Whoever sets the tool up — a founder, an operations lead or a first HR hire — is doing it alongside a day job. Ask each vendor to let that person build a review template and invite a team in the demo, and ask what can be imported from the spreadsheets you already keep.',
    },
    {
      title: 'A first cycle you can keep simple',
      text: 'The first review should be short: a self-review, a manager review and a conversation. Check that you can start there and add peer feedback, competencies and calibration later without rebuilding the process.',
    },
    {
      title: 'Goals that follow the company plan',
      text: 'At a startup, priorities move every quarter. Look for goals and OKRs that link to company objectives, can be reweighted when the plan changes, and reach the review without being retyped.',
    },
    {
      title: 'A career framework before the first promotion round',
      text: 'The first promotion debates arrive with the first managers. A competency framework that writes down what each level looks like gives those conversations a standard, and it is far easier to set up at 30 people than to retrofit at 300.',
    },
    {
      title: 'An HR system first, or a performance layer',
      text: 'If you have no HR system of record yet, an HRIS with performance built in may be the right first purchase. If you already run one, or run payroll through a provider you are keeping, you are buying a performance layer beside it. Decide which before you build the shortlist.',
    },
  ],
  vendors: [
    {
      id: 'clear',
      name: 'CLEAR Talent',
      positioning:
        'A performance and development layer: goals and KPIs, review cycles, 360° feedback, competency assessment and development, with Ava AI assistance that a person reviews and approves. Built to run alongside the HRIS or payroll system you already have rather than replace it.',
      worthALook:
        'You want a published price you can plan against, a competency framework in place before the first promotion round and goals tied to company objectives — beside the HR or payroll system you already use.',
      askThem: '',
      href: '/features',
    },
    {
      id: 'lattice',
      name: 'Lattice',
      positioning:
        'A people platform covering performance reviews, goals and OKRs, 1:1s, engagement surveys, career and development tracks and analytics, and it has extended beyond performance into core HR. Widely adopted by fast-growing companies that want one supplier across those areas.',
      worthALook:
        'You want reviews, 1:1s and engagement surveys from one supplier, and expect to consolidate more of the HR stack with it as you grow.',
      askThem:
        'Which modules the tier you are quoted includes at your headcount, and whether a minimum seat count or an annual commitment applies.',
      href: 'https://lattice.com/',
      headToHead: '/clear-talent-vs-lattice',
    },
    {
      id: 'fifteenFive',
      name: '15Five',
      positioning:
        'Continuous performance and manager enablement: weekly check-ins, 1-on-1s, OKRs, performance reviews, engagement surveys, and manager coaching and training content.',
      worthALook:
        'Your first managers are new to managing, and a weekly check-in habit and training content matter as much as the review itself.',
      askThem: 'Whether manager training is inside the tier you are quoted, and how the price changes as headcount grows.',
      href: 'https://www.15five.com/',
      headToHead: '/clear-talent-vs-15five',
    },
    {
      id: 'leapsome',
      name: 'Leapsome',
      positioning:
        'A people enablement platform covering performance reviews, goals and OKRs, 360° feedback, engagement surveys, 1:1 meetings, and learning and onboarding.',
      worthALook:
        'You want reviews, goals, engagement surveys, learning and onboarding on one platform from the start.',
      askThem: 'Which modules sit in the tier you are quoted, and which you would need to add as the company grows.',
      href: 'https://www.leapsome.com/',
      headToHead: '/clear-talent-vs-leapsome',
    },
    {
      id: 'cultureAmp',
      name: 'Culture Amp',
      positioning:
        'An employee experience platform best known for engagement surveys and people analytics, with performance and development products alongside.',
      worthALook:
        'Measuring engagement matters as much as running reviews, and you want survey results and performance data from one supplier.',
      askThem:
        'Which of engagement, performance and development are separate products, and how they are priced together at your headcount.',
      href: 'https://www.cultureamp.com/',
      headToHead: '/clear-talent-vs-culture-amp',
    },
    {
      id: 'bambooHr',
      name: 'BambooHR',
      positioning:
        'An HRIS for small and medium-sized businesses — the employee record, hiring, onboarding and time off — with performance management as one part of the platform.',
      worthALook:
        'You have no HR system yet and would rather buy the employee record, hiring and time off first, with performance reviews inside it.',
      askThem: 'Which plan includes the performance features you need, and how far review configuration goes.',
      href: 'https://www.bamboohr.com/',
      headToHead: '/clear-talent-vs-bamboohr',
    },
    {
      id: 'personio',
      name: 'Personio',
      positioning:
        'An all-in-one HR platform for small and medium-sized companies, with a strong European presence: the employee record, recruiting, onboarding, absence and time tracking, payroll-related workflows, and performance and development on one system.',
      worthALook:
        'You have no HR system yet, want the employee record, absences, onboarding and performance on one platform, and value a supplier with a strong European presence.',
      askThem: 'Which plan includes performance and development, and how far its review cycles can be configured.',
      href: 'https://www.personio.com/',
      headToHead: '/clear-talent-vs-personio',
    },
  ],
  criteria: [
    {
      criterion: 'A price you can model per seat',
      why: 'Runway is the constraint: you need the cost at today’s headcount and next year’s before a sales call.',
      clearNote: `Basic is USD ${BASIC_TIER.monthlyUsd} a month for ${BASIC_TIER.includedAdmins} admin and ${BASIC_TIER.includedStaff} staff users, then USD ${BASIC_TIER.extraStaffUsd} per extra staff user; Pro is USD ${PRO_TIER.monthlyUsd} for ${PRO_TIER.includedStaff}.`,
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        bambooHr: 'confirm',
        personio: 'confirm',
      },
    },
    {
      criterion: 'Set up by a founder or first HR hire',
      why: 'Nobody at a startup has time to run an implementation project.',
      clearNote: `A guided onboarding wizard tailored to size, industry and maturity, CSV and Excel bulk import, and a ${TRIAL_POLICY.toLowerCase()}.`,
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        bambooHr: 'confirm',
        personio: 'confirm',
      },
    },
    {
      criterion: 'Review cycles you can start small',
      why: 'The first cycle stays short; the structure is there when you need more.',
      clearNote: 'Self, manager, peer and multi-stage cycles from configurable templates, with per-stage deadlines and automated reminders.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'strong',
        cultureAmp: 'strong',
        bambooHr: 'strong',
        personio: 'strong',
      },
    },
    {
      criterion: 'Check-ins and 1:1s between reviews',
      why: 'Feedback keeps pace with a company that changes every quarter.',
      clearNote: '1-on-1 scheduling, goal check-ins, kudos and development notes between formal cycles.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'strong',
        cultureAmp: 'confirm',
        bambooHr: 'confirm',
        personio: 'confirm',
      },
    },
    {
      criterion: 'Goals and OKRs linked to company objectives',
      why: 'Individual goals move when the company plan moves.',
      clearNote: 'Strategic Linked Goals requires every individual goal to connect to a parent; quarterly goals run beside an annual review.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'strong',
        cultureAmp: 'confirm',
        bambooHr: 'confirm',
        personio: 'confirm',
      },
    },
    {
      criterion: 'A career framework before the first promotion round',
      why: 'First-time managers argue promotions against a written standard.',
      clearNote: 'Behavioural indicators at every level, scales of three to seven levels, and AI that extracts competencies from job descriptions.',
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        bambooHr: 'confirm',
        personio: 'confirm',
      },
    },
    {
      criterion: 'AI assistance with human approval',
      why: 'Help past the blank page, without output nobody has checked.',
      clearNote: 'Every plan suggests goals and KPIs and generates behavioural indicators, with wider AI from Pro. Output is labelled and editable.',
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        bambooHr: 'confirm',
        personio: 'confirm',
      },
    },
    {
      criterion: 'Employee engagement surveys',
      why: 'Only matters if surveys are part of this purchase — then it matters a lot.',
      clearNote: 'Not a CLEAR Talent capability. If surveys are required, you would need a second tool.',
      statuses: {
        clear: 'none',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'strong',
        cultureAmp: 'strong',
        bambooHr: 'confirm',
        personio: 'confirm',
      },
    },
    {
      criterion: 'Core HR record, payroll and time off',
      why: 'Decides whether you are buying an HR system or a performance layer beside one.',
      clearNote: 'Not a CLEAR Talent capability, deliberately: it runs alongside the HR or payroll system you use.',
      statuses: {
        clear: 'none',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        bambooHr: 'strong',
        personio: 'strong',
      },
    },
  ],
  clearFit: [
    `You want a price before the first sales call. The Basic plan is USD ${BASIC_TIER.monthlyUsd} a month for ${BASIC_TIER.includedAdmins} admin user and ${BASIC_TIER.includedStaff} staff users, with each additional staff user at USD ${BASIC_TIER.extraStaffUsd} a month, and there is a ${TRIAL_POLICY.toLowerCase()} with no credit card required.`,
    'The first promotion round is coming. On every plan, Ava can extract competencies from your job descriptions and generate behavioural indicators, so a framework exists before managers start debating levels.',
    'Priorities change every quarter. Quarterly OKRs and an annual review run side by side, each quarter’s goal scores roll up into the annual review, and every individual goal has to connect to a company objective.',
    'Nobody does HR full-time. A guided onboarding wizard is tailored to organisation size, industry and performance-management maturity, and people, goals and organisation structure load from CSV or Excel.',
    'You run payroll or an HR system you are keeping. CLEAR Talent sits beside it rather than replacing it, which keeps the rollout narrow.',
  ],
  clearNotFit: [
    'You have no HR system at all and want one product for the employee record, time off and reviews. An HRIS with performance built in, such as BambooHR or Personio, is the better first purchase.',
    'Engagement or pulse surveys are part of this purchase. CLEAR Talent has no survey product.',
    `You want a plan that stays free. There is a ${TRIAL_POLICY.toLowerCase()}, after which plans start at USD ${BASIC_TIER.monthlyUsd} a month.`,
    `Everyone needs the mobile apps on the cheapest plan. The native iOS and Android apps start on Pro, at USD ${PRO_TIER.monthlyUsd} a month for ${PRO_TIER.includedAdmins} admin and ${PRO_TIER.includedStaff} staff users.`,
    'A manager training curriculum is what you are really buying. CLEAR Talent gives managers coaching context, not a course library.',
  ],
  pricing: {
    points: pricePointsAt([10, 25, 50, PUBLISHED_STAFF_CAP]),
    aboveCapLabel: `${PUBLISHED_STAFF_CAP + 1}+`,
    summary: `${STARTING_PRICE_SENTENCE} At 10 staff users that is ${priceAt(BASIC_TIER, 10)} a month on Basic, at 25 it is ${priceAt(BASIC_TIER, 25)}, and at 50 it is ${priceAt(BASIC_TIER, 50)} on Basic or ${priceAt(PRO_TIER, 50)} on Pro, which adds the mobile apps and individual development plans. Annual billing takes ${ANNUAL_DISCOUNT_PERCENT}% off the plan price, there is a ${TRIAL_POLICY.toLowerCase()} with no credit card required, and past ${PUBLISHED_STAFF_CAP} staff users the Enterprise plan is quoted.`,
    rivals: SEGMENT_GUIDE.pricing.rivals,
    questions: PRICING_QUESTIONS,
  },
  checklist: [
    {
      stage: 'Before the demos',
      items: [
        'Write down today’s headcount and the one in your hiring plan for the next twelve months, and ask for every quote at both.',
        'Decide whether you need an HR system of record or a performance layer beside the one you have.',
        'Name the person who will set the tool up and run the first cycle, and how many hours a month they really have for it.',
        'Write three scenarios every vendor must show live: a first review cycle with your stages, a company objective linked to one person’s goal, and a manager’s 1:1 notes reaching the review.',
      ],
    },
    {
      stage: 'In the demo',
      items: [
        'Have the person who will run it build a review template and invite a team themselves.',
        'Ask what happens to the price, the plan and your data when you pass the next headcount threshold.',
        'Ask what the AI writes, whether its output is labelled, and whether it can be switched off.',
        'Put one of your first-time managers in the room.',
      ],
    },
    {
      stage: 'Before you sign',
      items: [
        'Get the module list, how a user or seat is counted, any minimum seat count or annual commitment, and the renewal terms in writing.',
        'Confirm how your data comes out — formats, and what happens at the end of the contract.',
        'Run a real check-in or review with one team in a trial or pilot before inviting everyone.',
      ],
    },
  ],
  faqs: [
    {
      q: 'What is the best performance management software for a startup?',
      a: 'There is no single winner, and the answer depends on what you already run. If you have no HR system yet, an HRIS with performance built in, such as BambooHR or Personio, may be the right first purchase. If engagement surveys matter as much as reviews, Lattice, 15Five, Leapsome and Culture Amp all position around both. If you already run payroll or an HR system you are keeping, and want a published price, a competency framework before the first promotion round and goals tied to company objectives, CLEAR Talent is built for that. Whichever you shortlist, judge it on how easily the person who will run it can set it up, and on the price at next year’s headcount.',
    },
    {
      q: 'When should a startup start formal performance reviews?',
      a: 'Usually when the founders can no longer give everyone feedback personally — often around the time the first people managers are hired, or when the first conversation about pay or promotion has nothing written down behind it. Start small: a self-review, a manager review and a conversation, with goals linked to the company plan. Peer feedback, competency assessment and calibration can follow, as long as the tool lets you add them without rebuilding the process.',
    },
    {
      q: 'How much does performance management software cost for a startup?',
      a: `Pricing models differ — a plan fee with per-user add-ons, a per-seat rate, a minimum seat count, an annual commitment — so headline figures mislead. CLEAR Talent publishes a plan fee with per-user add-ons: at 10 staff users that is ${priceAt(BASIC_TIER, 10)} a month on the Basic plan, at 25 staff users ${priceAt(BASIC_TIER, 25)}, and at 50 staff users ${priceAt(BASIC_TIER, 50)} on Basic or ${priceAt(PRO_TIER, 50)} on Pro, with ${ANNUAL_DISCOUNT_PERCENT}% off the plan price for annual billing. For other vendors, read their current pricing page and get a written quote at today’s headcount and next year’s, over the same term and module list.`,
    },
    {
      q: 'How quickly can a startup get set up?',
      a: `With CLEAR Talent, rollout is measured in weeks. A guided onboarding wizard is tailored to organisation size, industry and performance-management maturity, people and organisation structure load from CSV or Excel, and Ava can extract competencies from your job descriptions so the framework does not start from a blank page. There is a ${TRIAL_POLICY.toLowerCase()} with no credit card required, so you can try it with one team before committing.`,
    },
    {
      q: 'Will it still fit when we have 100 or 500 people?',
      a: `The structure should. Competency scales can differ by job family, role profiles carry their own required competencies, goals cascade from company objectives to teams and individuals, and staff transfers move people between teams without rebuilding the organisation chart. The price changes shape: the published plans cover up to ${PUBLISHED_STAFF_CAP} staff users, and past that CLEAR Talent is priced on the Enterprise plan, which is quoted. Ask every vendor the same question — what changes at your next headcount threshold — before you sign.`,
    },
  ],
  sources: [
    { label: 'CLEAR Talent pricing', href: 'https://www.clear-talent.com/pricing' },
    { label: 'CLEAR Talent platform capabilities', href: 'https://www.clear-talent.com/features' },
    { label: 'Lattice', href: 'https://lattice.com/' },
    { label: '15Five', href: 'https://www.15five.com/' },
    { label: 'Leapsome', href: 'https://www.leapsome.com/' },
    { label: 'Culture Amp', href: 'https://www.cultureamp.com/' },
    { label: 'BambooHR', href: 'https://www.bamboohr.com/' },
    { label: 'Personio', href: 'https://www.personio.com/' },
  ],
  internalLinks: [
    { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
    SIBLING_GUIDE_LINKS.smallBusiness,
    SIBLING_GUIDE_LINKS.enterprise,
    SIBLING_GUIDE_LINKS.remote,
    { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
    { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
    { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
    { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
    { label: 'CLEAR Talent vs BambooHR', href: '/clear-talent-vs-bamboohr' },
    { label: 'CLEAR Talent vs Personio', href: '/clear-talent-vs-personio' },
    { label: 'Performance management for technology companies', href: '/industries/performance-management-technology-companies' },
    { label: 'Guide: performance management software pricing', href: '/blog/performance-management-software-pricing' },
    { label: 'Buyer’s checklist: 25 questions to ask vendors', href: '/resources/performance-management-software-buyers-checklist' },
    { label: 'CLEAR Talent pricing', href: '/pricing' },
  ],
};

export const ENTERPRISE_GUIDE: SegmentGuide = {
  path: ENTERPRISE_GUIDE_PATH,
  seoTitle: `Best Performance Management Software for Enterprise (${SEGMENT_SIBLINGS_YEAR})`,
  metaDescription:
    'Six performance management platforms for 500+ employee organisations compared on SSO, audit trails, calibration, succession, AI governance and data residency.',
  primaryKeyword: 'best performance management software for enterprise',
  keywords: [
    'enterprise performance management software',
    'performance management software for large organisations',
    'enterprise performance review software',
    'performance calibration software for enterprises',
  ],
  eyebrow: 'Buyer’s guide · 500+ employees',
  h1: 'Performance management software for enterprises',
  breadcrumbName: 'Enterprises (500+ employees)',
  excerpt:
    'Six platforms an organisation of 500 or more employees is likely to shortlist, compared on the governance, calibration and integration questions HR, IT and employee representatives will ask — including where CLEAR Talent falls short.',
  heroImage: '/screenshots/07-reports-tab-organization.webp',
  heroImageAlt: 'Department and organisation reporting in CLEAR Talent Reports & Analytics',
  reviewed: SEGMENT_SIBLINGS_REVIEWED,
  itemListName: 'Performance management platforms compared for organisations of 500 or more employees',
  copy: {
    criteriaEyebrow: 'What to look for at enterprise scale',
    criteriaTitle: 'What separates one platform from another past 500 employees.',
    criteriaSub:
      'At this size the review form is rarely the problem. These are the questions that decide whether IT will approve the tool, whether ratings hold up across divisions, and whether the record survives an audit or a dispute.',
    tableSubject: 'performance management platforms for enterprises',
    tableCaptionSuffix: 'compared for organisations of 500 or more employees',
    faqTitle: 'Questions enterprise HR and IT teams ask.',
    faqSub: 'The answers an enterprise shortlist usually turns on.',
    checklistSub:
      'An evaluation that gives HR, IT, procurement and employee representatives the same evidence — and stops the best-rehearsed demo from winning by default.',
    ctaTitle: 'Bring your security questionnaire to the demo.',
    ctaSub:
      'Show us your review stages, your calibration process and your IT requirements, and judge CLEAR Talent on how it handles them — including the ones it does not meet today.',
    demoSource: 'enterprise-guide',
  },
  intro: [
    'Past 500 employees, performance management software stops being a question of whether managers can complete a review form. HR is now answering to IT about single sign-on and access control, to legal about the audit trail behind a rating that moved in calibration, to employee representatives about how AI is used, and to finance about a contract that has to make sense over its whole term. The tool has to survive all of those reviews before anyone sees a performance cycle.',
    'This guide compares six platforms that come up on enterprise shortlists: CLEAR Talent, Workday HCM, SAP SuccessFactors, Betterworks, Lattice and Culture Amp. Workday and SAP SuccessFactors are HCM suites with performance inside them; the others are performance or people platforms that usually sit beside an HCM. We build CLEAR Talent, so we describe it precisely — including what it does not do today, such as SAML single sign-on — and describe the others only as they describe themselves. Where we cannot speak for another vendor, the table says “Confirm” and the vendor notes give you the question to ask — never a claimed gap.',
  ],
  selectionCriteria: [
    {
      title: 'Identity and access first',
      text: 'Most enterprise IT policies require single sign-on through the identity provider, and many require automatic provisioning too. Ask about SAML SSO and SCIM in the first conversation, before anyone spends time on review templates.',
    },
    {
      title: 'An audit trail that answers “who changed this?”',
      text: 'A rating that moved in calibration is the one most likely to be questioned later. Ask to see the log entry for a changed rating — who made it, when, and the before-and-after values — and when the cycle closes to further amendment.',
    },
    {
      title: 'One standard across divisions',
      text: 'The same rating should mean the same thing in every business unit and country. Look for competency scales that can differ by job family, a calibration stage before ratings go final, and views that compare managers rating similar roles.',
    },
    {
      title: 'AI your works council can review',
      text: 'The EU AI Act treats AI used to evaluate workers’ performance as high-risk. Ask what the AI writes and reads, whether its output is labelled and editable, whether every edit is logged, and whether it can be switched off for a team, a cycle or a module.',
    },
    {
      title: 'Replace the HCM, or add a layer beside it',
      text: 'If you are consolidating HR, payroll and talent onto one suite, an HCM such as Workday or SAP SuccessFactors is the comparison. If you are keeping your HCM and the gap is performance depth, you are buying a layer beside it — and how data moves between the two matters as much as any feature.',
    },
    {
      title: 'Residency and the paper trail',
      text: 'Get the hosting region, the DPA, the subprocessor list and any certification you require confirmed in writing during the evaluation, not after signature. A vendor web page — including this one — is not a contract.',
    },
  ],
  vendors: [
    {
      id: 'clear',
      name: 'CLEAR Talent',
      positioning:
        'A performance and development layer that runs beside the HCM you keep: goals and KPIs, review cycles with a calibration stage, 360° feedback, competency assessment, development and succession, with Ava AI assistance that a person reviews and approves.',
      worthALook:
        'You are keeping your HCM, the gap is the evidence behind ratings and calibration across divisions, and you need AI governance you can show a works council or a DPO.',
      askThem: '',
      href: '/features',
    },
    {
      id: 'workday',
      name: 'Workday HCM',
      positioning:
        'An enterprise HCM suite for large multinationals consolidating HR, finance, workforce and talent on one platform, with talent and performance management inside it.',
      worthALook:
        'You are consolidating HR, payroll and talent onto one suite, or already run Workday and want performance on the same platform.',
      askThem:
        'Which talent and performance capabilities your subscription includes, and what implementing them involves at your scale.',
      href: 'https://www.workday.com/en-us/products/human-capital-management/overview.html',
    },
    {
      id: 'sapSuccessFactors',
      name: 'SAP SuccessFactors',
      positioning:
        'A global enterprise HCM suite whose Performance & Goals module covers performance reviews and goal management, particularly suited to organisations already invested in the SAP ecosystem.',
      worthALook:
        'You already run SAP, or are consolidating HR onto a global HCM suite, and want performance on the same platform.',
      askThem:
        'Which modules your contract includes for performance, calibration and succession, and what implementing them involves.',
      href: 'https://help.sap.com/docs/successfactors-performance-and-goals',
    },
    {
      id: 'betterworks',
      name: 'Betterworks',
      positioning:
        'Enterprise goal and OKR management, with conversations, continuous feedback, performance reviews and calibration alongside, typically deployed in large organisations next to an existing HCM.',
      worthALook: 'An enterprise-wide goal and OKR programme, run beside an existing HCM, is the outcome you are buying.',
      askThem:
        'How competencies are modelled beside goals, and what the minimum contract and implementation look like at your headcount.',
      href: 'https://www.betterworks.com/',
      headToHead: '/clear-talent-vs-betterworks',
    },
    {
      id: 'lattice',
      name: 'Lattice',
      positioning:
        'A people platform covering performance reviews, goals and OKRs, 1:1s, engagement surveys, career and development tracks and analytics, and it has extended beyond performance into core HR.',
      worthALook:
        'You want reviews, goals, 1:1s and engagement surveys from one people platform, and its packaging fits an organisation of your size.',
      askThem:
        'Which identity-provider sign-in, provisioning and audit logging come with the tier you are quoted, and which modules it includes.',
      href: 'https://lattice.com/',
      headToHead: '/clear-talent-vs-lattice',
    },
    {
      id: 'cultureAmp',
      name: 'Culture Amp',
      positioning:
        'An employee experience platform best known for engagement surveys and people analytics, with performance and development products alongside.',
      worthALook: 'Engagement surveys and people analytics are as central to the purchase as performance reviews.',
      askThem:
        'Which of engagement, performance and development are separate products, and how identity-provider sign-in and data exports work at your scale.',
      href: 'https://www.cultureamp.com/',
      headToHead: '/clear-talent-vs-culture-amp',
    },
  ],
  criteria: [
    {
      criterion: 'SAML single sign-on and SCIM provisioning',
      why: 'Most enterprise IT policies require both for every business application.',
      clearNote: 'Not available today. People sign in with CLEAR Talent credentials, with biometric sign-in in the mobile apps.',
      statuses: {
        clear: 'none',
        workday: 'confirm',
        sapSuccessFactors: 'confirm',
        betterworks: 'confirm',
        lattice: 'confirm',
        cultureAmp: 'confirm',
      },
    },
    {
      criterion: 'Audit trail and role-based access',
      why: 'You can show who changed a rating, when and from what — and who could see it.',
      clearNote: 'Sign-ins, exports, approvals and rating changes logged with before-and-after values; role-based access with ownership checks.',
      statuses: {
        clear: 'strong',
        workday: 'confirm',
        sapSuccessFactors: 'confirm',
        betterworks: 'confirm',
        lattice: 'confirm',
        cultureAmp: 'confirm',
      },
    },
    {
      criterion: 'Calibration before ratings go final',
      why: 'One rating standard across divisions, agreed before pay and promotion decisions.',
      clearNote: 'A calibration stage in every review cycle, with heatmaps, outlier flags and an optional forced-distribution simulation.',
      statuses: {
        clear: 'strong',
        workday: 'strong',
        sapSuccessFactors: 'strong',
        betterworks: 'strong',
        lattice: 'confirm',
        cultureAmp: 'confirm',
      },
    },
    {
      criterion: 'Succession and the 9-box on the calibrated record',
      why: 'Talent reviews start from performance that has already been calibrated.',
      clearNote: `9-box, readiness and succession in the ${SUCCESSION_MODULE ?? 'Talent & Succession Planning'} module, on the Enterprise plan.`,
      statuses: {
        clear: 'strong',
        workday: 'strong',
        sapSuccessFactors: 'strong',
        betterworks: 'confirm',
        lattice: 'confirm',
        cultureAmp: 'confirm',
      },
    },
    {
      criterion: 'Goals cascaded across divisions',
      why: 'Business-unit, team and individual goals stay tied to the corporate plan.',
      clearNote: 'Strategic Linked Goals requires every individual goal to link to a parent; weighted KPIs come from a central library.',
      statuses: {
        clear: 'strong',
        workday: 'strong',
        sapSuccessFactors: 'strong',
        betterworks: 'strong',
        lattice: 'strong',
        cultureAmp: 'confirm',
      },
    },
    {
      criterion: 'Competency frameworks and assessment',
      why: 'One written standard per job family, so a rating means the same thing everywhere.',
      clearNote: 'Scales of three to seven levels that can differ by job family, indicators at every level, and ratings that can require evidence.',
      statuses: {
        clear: 'strong',
        workday: 'strong',
        sapSuccessFactors: 'strong',
        betterworks: 'confirm',
        lattice: 'confirm',
        cultureAmp: 'confirm',
      },
    },
    {
      criterion: 'AI governance you can evidence',
      why: 'Works councils and DPOs ask what the AI does before it goes live.',
      clearNote: 'Ava’s output is labelled, editable and approved by a person; every edit is logged, and it can be switched off per cycle, team or module.',
      statuses: {
        clear: 'strong',
        workday: 'confirm',
        sapSuccessFactors: 'confirm',
        betterworks: 'confirm',
        lattice: 'confirm',
        cultureAmp: 'confirm',
      },
    },
    {
      criterion: 'Multilingual and mobile participation',
      why: 'Every country and every frontline site completes the same cycle.',
      clearNote: 'AI-generated feedback and summaries in 16 languages; native iOS and Android apps with offline support.',
      statuses: {
        clear: 'strong',
        workday: 'strong',
        sapSuccessFactors: 'strong',
        betterworks: 'confirm',
        lattice: 'confirm',
        cultureAmp: 'confirm',
      },
    },
    {
      criterion: 'GDPR tooling and regional hosting',
      why: 'The DPO signs off before any employee data is loaded.',
      clearNote: 'DSAR exports, erasure workflows and a DPA; Google Cloud hosting, with regional requirements agreed per deployment.',
      statuses: {
        clear: 'available',
        workday: 'confirm',
        sapSuccessFactors: 'confirm',
        betterworks: 'confirm',
        lattice: 'confirm',
        cultureAmp: 'confirm',
      },
    },
    {
      criterion: 'Data exchange with the HCM you keep',
      why: 'People and structure stay current without anyone re-keying records.',
      clearNote: 'No prebuilt HCM connector: API access and CSV or Excel import in, CSV, Excel or PDF out. Enterprise integrations are scoped per rollout.',
      statuses: {
        clear: 'limited',
        workday: 'confirm',
        sapSuccessFactors: 'confirm',
        betterworks: 'confirm',
        lattice: 'confirm',
        cultureAmp: 'confirm',
      },
    },
    {
      criterion: 'Core HR, payroll and the employee record',
      why: 'Decides whether you are replacing the HCM or adding a layer beside it.',
      clearNote: 'Not a CLEAR Talent capability, deliberately: it runs beside the HCM you keep.',
      statuses: {
        clear: 'none',
        workday: 'strong',
        sapSuccessFactors: 'strong',
        betterworks: 'confirm',
        lattice: 'confirm',
        cultureAmp: 'confirm',
      },
    },
  ],
  clearFit: [
    'You are keeping Workday, SAP SuccessFactors or another HCM, and the gap is performance depth: evidence behind ratings, competency assessment and calibration on one record.',
    'Ratings have to survive scrutiny. Every rating change is logged against the user who made it, with before-and-after values, and each stage closes on the date you set.',
    'You need AI governance you can evidence. Ava’s output is labelled, editable and approved by a person, every edit is logged, it can be switched off per cycle, team or module, and your employees’ data is never used to train models for other customers.',
    'The organisation spans entities, countries and languages: several legal entities on one platform, AI-generated feedback and summaries in 16 languages, and native iOS and Android apps for people away from a desk.',
    `Talent reviews should start from calibrated ratings. The ${SUCCESSION_MODULE ?? 'Talent & Succession Planning'} module puts the 9-box, readiness and succession on the same record as the review, on the Enterprise plan.`,
  ],
  clearNotFit: [
    'Your IT policy requires SAML single sign-on or SCIM provisioning for every application. CLEAR Talent offers neither today, so raise it before you shortlist, not after.',
    'You are consolidating HR, payroll and talent onto one suite. An HCM such as Workday or SAP SuccessFactors is the comparison; CLEAR Talent has no payroll, time and attendance or employee record.',
    'A live, automatically syncing HCM connector is a hard requirement. There is no prebuilt connector today: data moves by import, API access and export, and enterprise integrations are scoped per rollout.',
    'Procurement requires a named security certification or third-party audit report before shortlisting. Our trust centre names none, so ask at the start what can be provided.',
    'Engagement or pulse surveys are part of this purchase. CLEAR Talent has no survey product.',
  ],
  pricing: {
    points: segmentPricePoints,
    aboveCapLabel: `${PUBLISHED_STAFF_CAP + 1}+`,
    summary: `The published plans stop at ${PUBLISHED_STAFF_CAP} staff users, so an organisation of 500 or more employees is priced on the Enterprise plan, quoted to your scope. Enterprise includes everything in Pro and adds ${ENTERPRISE_ONLY_MODULES.join(', ')}. The published figures are still a useful reference for a pilot: ${PUBLISHED_STAFF_CAP} staff users cost ${priceAt(BASIC_TIER, PUBLISHED_STAFF_CAP)} a month on Basic or ${priceAt(PRO_TIER, PUBLISHED_STAFF_CAP)} on Pro, with ${ANNUAL_DISCOUNT_PERCENT}% off the plan price for annual billing and a ${TRIAL_POLICY.toLowerCase()}.`,
    rivals: SEGMENT_GUIDE.pricing.rivals,
    questions: PRICING_QUESTIONS,
  },
  checklist: [
    {
      stage: 'Before the demos',
      items: [
        'Send your security questionnaire and your SSO, provisioning and data-residency requirements to every vendor before the first demo, and drop any that cannot meet a hard requirement.',
        'Decide whether you are replacing the HCM or adding a performance layer beside it, and shortlist accordingly.',
        'Agree who signs off: HR, IT, the DPO, procurement and, where one exists, the works council or employee representatives.',
        'Write scenarios every vendor must show live: a multi-stage cycle with your stages, a calibration session across two divisions, a rating change traced in the audit log, and a review completed on a phone.',
      ],
    },
    {
      stage: 'In the demo',
      items: [
        'Ask to see the audit log entry for a rating changed in calibration.',
        'Ask what the AI writes and reads, whether its output is labelled, and have them switch it off for one team in front of you.',
        'Have your administrator change a review template and a rating scale without the vendor’s consultant driving.',
        'Ask how people, structure and ratings move between the platform and your HCM, and who re-imports after a reorganisation.',
      ],
    },
    {
      stage: 'Before you sign',
      items: [
        'Get the module list, how a user is counted, the implementation scope and fees, the contract term and the renewal uplift in writing.',
        'Get the hosting region, the DPA, the subprocessor list and any certification you require confirmed in writing.',
        'Pilot with one division through a real cycle, calibration included, before committing the whole organisation.',
      ],
    },
  ],
  faqs: [
    {
      q: 'What is the best performance management software for an enterprise?',
      a: 'It depends first on whether you are replacing your HCM. If you are consolidating HR, payroll and talent onto one suite, Workday HCM and SAP SuccessFactors are built for that, with performance inside them. If you are keeping your HCM and the gap is performance depth — evidence behind ratings, calibration across divisions, AI you can govern — a performance or people platform such as CLEAR Talent, Betterworks or Lattice can sit beside it. Betterworks positions itself around enterprise goal programmes; CLEAR Talent around competency evidence and calibration. Check identity and access requirements before anything else: CLEAR Talent does not offer SAML single sign-on or SCIM today.',
    },
    {
      q: 'Does CLEAR Talent support single sign-on (SAML) or SCIM?',
      a: 'Not currently. People sign in with their own CLEAR Talent credentials, with biometric sign-in in the iOS and Android apps, and access is role-based with ownership checks and audit logging. If SSO is mandatory under your IT policy, raise it at the start of the evaluation rather than after the shortlist.',
    },
    {
      q: 'How does calibration work across a large organisation?',
      a: 'In CLEAR Talent, calibration is a stage of every review cycle, between manager review and the final rating. Heatmaps and outlier flags show where ratings are inconsistent across managers and teams before anything is final, an optional forced-distribution simulation shows where ratings cluster, and every change agreed in the session is written to the audit log with before-and-after values. Sessions usually run by level or function, so the people in the room know enough about each other’s teams to contribute.',
    },
    {
      q: 'Can CLEAR Talent run alongside Workday or SAP SuccessFactors?',
      a: 'Yes — it is designed to sit beside the HCM you keep, which remains the system of record for the employee. There is no prebuilt connector to Workday, SAP SuccessFactors or any other named HCM, so nothing syncs automatically: people and organisation structure come in through CSV or Excel bulk import or API access, and performance records go out as CSV, Excel or PDF. Enterprise integrations are scoped per rollout, so raise a live connection in the first conversation if you need one.',
    },
    {
      q: 'How is performance management software priced at enterprise scale?',
      a: `Usually by quote. CLEAR Talent publishes plans up to ${PUBLISHED_STAFF_CAP} staff users; past that, every organisation is priced on the Enterprise plan, which adds ${ENTERPRISE_ONLY_MODULES.join(', ')} to everything in Pro. Workday HCM and SAP SuccessFactors are quoted too. Compare quotes on the same module list, user definition, implementation scope and term, and ask what the renewal uplift will be before you sign.`,
    },
  ],
  sources: [
    { label: 'CLEAR Talent trust centre', href: 'https://www.clear-talent.com/trust' },
    { label: 'CLEAR Talent integrations', href: 'https://www.clear-talent.com/integrations' },
    { label: 'CLEAR Talent pricing', href: 'https://www.clear-talent.com/pricing' },
    { label: 'Workday Human Capital Management', href: 'https://www.workday.com/en-us/products/human-capital-management/overview.html' },
    { label: 'Workday Talent Management', href: 'https://www.workday.com/en-us/products/talent-management/overview.html' },
    { label: 'SAP SuccessFactors Performance & Goals', href: 'https://help.sap.com/docs/successfactors-performance-and-goals' },
    { label: 'Betterworks', href: 'https://www.betterworks.com/' },
    { label: 'Lattice', href: 'https://lattice.com/' },
    { label: 'Culture Amp', href: 'https://www.cultureamp.com/' },
  ],
  internalLinks: [
    { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
    SIBLING_GUIDE_LINKS.smallBusiness,
    SIBLING_GUIDE_LINKS.startups,
    SIBLING_GUIDE_LINKS.remote,
    { label: 'CLEAR Talent vs Betterworks', href: '/clear-talent-vs-betterworks' },
    { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
    { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
    { label: 'Performance calibration software', href: '/solutions/performance-calibration-software' },
    { label: 'Succession planning software', href: '/solutions/succession-planning-software' },
    { label: 'Trust centre: GDPR, hosting and AI governance', href: '/trust' },
    { label: 'Integrations: how data gets in and out', href: '/integrations' },
    { label: 'RFP template and vendor evaluation scorecard', href: '/resources/performance-management-software-rfp' },
    { label: 'Performance management for Ireland and Europe', href: '/solutions/performance-management-software-europe' },
    { label: 'CLEAR Talent pricing', href: '/pricing' },
  ],
};

export const REMOTE_TEAMS_GUIDE: SegmentGuide = {
  path: REMOTE_TEAMS_GUIDE_PATH,
  seoTitle: `Best Remote Team Performance Management Software (${SEGMENT_SIBLINGS_YEAR})`,
  metaDescription:
    'Compare six performance management tools for remote teams: async check-ins, 1:1 records, AI summaries in 16 languages, mobile apps and fair calibration.',
  primaryKeyword: 'best performance management software for remote teams',
  keywords: [
    'remote team performance management software',
    'performance management software for distributed teams',
    'remote performance review software',
    'async performance check-ins',
  ],
  eyebrow: 'Buyer’s guide · Remote teams',
  h1: 'Performance management software for remote teams',
  breadcrumbName: 'Remote and distributed teams',
  excerpt:
    'Six tools a remote or distributed team is likely to shortlist, compared on what keeps performance fair when nobody shares an office: async check-ins, written 1:1 records, languages, mobile access and calibration.',
  heroImage: '/screenshots/12-feedback-main.webp',
  heroImageAlt: 'Continuous feedback and check-in view in CLEAR Talent',
  reviewed: SEGMENT_SIBLINGS_REVIEWED,
  itemListName: 'Performance management tools compared for remote and distributed teams',
  copy: {
    criteriaEyebrow: 'What to look for in a remote team',
    criteriaTitle: 'What separates one tool from another when nobody shares an office.',
    criteriaSub:
      'In an office, a manager learns a great deal without trying. In a remote team, only what is written down reaches the review. These are the questions that decide whether it does.',
    tableSubject: 'performance management tools for remote teams',
    tableCaptionSuffix: 'compared for remote and distributed teams',
    faqTitle: 'Questions remote and distributed teams ask.',
    faqSub: 'The answers a remote-team shortlist usually turns on.',
    checklistSub:
      'An evaluation you can run across time zones — and the one that stops a tool designed around the office from winning by default.',
    ctaTitle: 'Bring a distributed team to the demo.',
    ctaSub:
      'Show us your time zones, your languages and how your managers run 1:1s today, and judge CLEAR Talent on how it handles them — with the same scenarios you give every other vendor.',
    demoSource: 'remote-teams-guide',
  },
  intro: [
    'Performance management in a remote team has the same aims as in an office — clear objectives, regular feedback, a fair assessment and a development plan — but a different source of evidence. In an office, much of what a manager knows arrives without anyone writing it down. In a distributed team it arrives only if someone records it: in a check-in, a 1:1 note, a piece of feedback or a goal update. A tool for remote teams is judged on whether that record gets written, in whose language and on which device, and whether it is still there when the review opens.',
    'This guide compares six tools that come up when remote and distributed teams shortlist: CLEAR Talent, Lattice, 15Five, Leapsome, Culture Amp and HiBob. We build CLEAR Talent, so we describe it precisely — including what it does not do, such as run inside Slack or Microsoft Teams — and describe the others only as they describe themselves. Where we cannot speak for another vendor, the table says “Confirm” and the vendor notes give you the question to ask — never a claimed gap.',
  ],
  selectionCriteria: [
    {
      title: 'Async by default',
      text: 'Progress should be recordable without a meeting that suits every time zone. Look for written check-ins against goals, self-reviews with clear deadlines and reminders, and feedback anyone can give at any time.',
    },
    {
      title: 'A record of every 1:1',
      text: 'A 1:1 that leaves no trace is forgotten by the next one. Check whether meetings are scheduled and summarised in the tool, whether agreed next steps carry forward, and whether coaching notes can stay private.',
    },
    {
      title: 'Evidence that reaches the review',
      text: 'Remote feedback is scattered across chat threads, documents and calls. The tool should give it one home, tied to the competencies people are assessed against, so the year-end review is not rebuilt from memory.',
    },
    {
      title: 'Fair to people the manager rarely sees',
      text: 'Remote staff are often rated on thinner evidence than the people a manager sees every day. Look for ratings that have to cite the record behind them, and for calibration across managers before ratings are final.',
    },
    {
      title: 'Every language, every device',
      text: 'A review written in someone’s second language is a weaker record of what they think. Check which languages the review screens and the AI output support, and complete one review on a phone, with the connection off, in front of the vendor.',
    },
    {
      title: 'Prompts that reach people',
      text: 'If your team lives in Slack or Microsoft Teams, ask whether reviews and feedback can be completed there or only prompted from there — and test the reminder and mobile journey with a few managers during a trial.',
    },
  ],
  vendors: [
    {
      id: 'clear',
      name: 'CLEAR Talent',
      positioning:
        'A performance and development layer: goals and check-ins, 1:1s, continuous feedback, review cycles with calibration, 360° feedback and competency assessment, with Ava AI assistance that a person reviews and approves. Built to run alongside the HRIS you already have.',
      worthALook:
        'Your team spans time zones and languages, you want 1:1s, check-ins and feedback written down where the review will find them, and you want calibration before ratings are final.',
      askThem: '',
      href: '/solutions/continuous-performance-management',
    },
    {
      id: 'lattice',
      name: 'Lattice',
      positioning:
        'A people platform covering performance reviews, goals and OKRs, 1:1s, engagement surveys, career and development tracks and analytics, and it has extended beyond performance into core HR.',
      worthALook:
        'You want 1:1s, goals, reviews and engagement surveys on one people platform, and are comfortable consolidating more of the HR stack with it.',
      askThem: 'Which collaboration tools reviews and feedback can be completed in, and which modules your tier includes.',
      href: 'https://lattice.com/',
      headToHead: '/clear-talent-vs-lattice',
    },
    {
      id: 'fifteenFive',
      name: '15Five',
      positioning:
        'Continuous performance and manager enablement: weekly check-ins, 1-on-1s, OKRs, performance reviews, engagement surveys, and manager coaching and training content.',
      worthALook:
        'A weekly check-in habit is the core of how you want a distributed team to work, and manager training content matters as much as the review.',
      askThem: 'How check-ins and 1-on-1s work across time zones, and whether manager training is inside the tier you are quoted.',
      href: 'https://www.15five.com/',
      headToHead: '/clear-talent-vs-15five',
    },
    {
      id: 'leapsome',
      name: 'Leapsome',
      positioning:
        'A people enablement platform covering performance reviews, goals and OKRs, 360° feedback, engagement surveys, 1:1 meetings, and learning and onboarding.',
      worthALook:
        'You want 1:1 meetings, reviews, goals, engagement surveys, learning and onboarding on one platform.',
      askThem: 'Which languages its review screens support, and which modules the tier you are quoted includes.',
      href: 'https://www.leapsome.com/',
      headToHead: '/clear-talent-vs-leapsome',
    },
    {
      id: 'cultureAmp',
      name: 'Culture Amp',
      positioning:
        'An employee experience platform best known for engagement surveys and people analytics, with performance management — including reviews and 360° feedback — and development products alongside.',
      worthALook:
        'Measuring how a distributed team feels — engagement surveys and people analytics — matters as much as running reviews.',
      askThem:
        'Which of engagement, performance and development are separate products, and how 360 rater groups work across locations.',
      href: 'https://www.cultureamp.com/',
      headToHead: '/clear-talent-vs-culture-amp',
    },
    {
      id: 'hiBob',
      name: 'HiBob',
      positioning:
        'An HR platform for modern, mid-sized and multinational companies: core HR and the employee record, onboarding, time and attendance, compensation and performance management on one system, with an emphasis on culture and employee experience.',
      worthALook:
        'You employ people in several countries and want the employee record, time and attendance, compensation and performance on one HR platform.',
      askThem: 'Which languages review forms support, and how far the performance module’s cycles and feedback can be configured.',
      href: 'https://www.hibob.com/',
      headToHead: '/clear-talent-vs-hibob',
    },
  ],
  criteria: [
    {
      criterion: 'Async check-ins against goals',
      why: 'Progress is recorded without a meeting that suits every time zone.',
      clearNote: 'Written check-ins on each goal, on the web or in the mobile apps from Pro, with at-risk flags from pace and check-in signals.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        hiBob: 'confirm',
      },
    },
    {
      criterion: '1:1s that leave a written record',
      why: 'The next conversation starts where the last one ended.',
      clearNote: '1-on-1 scheduling with a combined summary and suggested next steps per person; coaching notes can stay private.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'strong',
        cultureAmp: 'confirm',
        hiBob: 'confirm',
      },
    },
    {
      criterion: 'Feedback that is still findable at review time',
      why: 'Praise and coaching do not vanish into chat threads.',
      clearNote: 'Kudos and development notes tagged to competencies, plus a performance journal managers keep as they go.',
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        hiBob: 'confirm',
      },
    },
    {
      criterion: 'Company goals visible from every location',
      why: 'People who never meet can see how their work connects.',
      clearNote: 'Objectives cascade to teams and individuals, and every individual goal must link to a parent.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'strong',
        cultureAmp: 'confirm',
        hiBob: 'confirm',
      },
    },
    {
      criterion: '360° input from colleagues in other locations',
      why: 'Peers see work the manager does not.',
      clearNote: 'Rater groups include cross-functional stakeholders, anonymised within each group, with minimum-rater thresholds.',
      statuses: {
        clear: 'strong',
        lattice: 'strong',
        fifteenFive: 'confirm',
        leapsome: 'strong',
        cultureAmp: 'strong',
        hiBob: 'confirm',
      },
    },
    {
      criterion: 'Calibration before ratings are final',
      why: 'Catches remote staff rated on thinner evidence than office colleagues.',
      clearNote: 'A calibration stage in every review cycle, with heatmaps and outlier flags across managers and teams.',
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        hiBob: 'confirm',
      },
    },
    {
      criterion: 'AI feedback and summaries in each person’s language',
      why: 'Nobody’s record is weaker because it was written in a second language.',
      clearNote: 'AI-generated feedback, summaries and recommendations in 16 languages, in the language each employee prefers.',
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        hiBob: 'confirm',
      },
    },
    {
      criterion: 'Mobile apps with push and offline support',
      why: 'People away from a laptop, or on a weak connection, still take part.',
      clearNote: 'Native iOS and Android apps with push notifications, biometric sign-in and offline support, from the Pro plan.',
      statuses: {
        clear: 'strong',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        hiBob: 'confirm',
      },
    },
    {
      criterion: 'Reviews and feedback inside Slack or Microsoft Teams',
      why: 'Matters most if your team rarely leaves chat.',
      clearNote: 'No Slack or Teams app today. Automated stage reminders and mobile push notifications do the prompting.',
      statuses: {
        clear: 'none',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        hiBob: 'confirm',
      },
    },
    {
      criterion: 'Employee engagement surveys',
      why: 'Only matters if surveys are part of this purchase — then it matters a lot.',
      clearNote: 'Not a CLEAR Talent capability. If surveys are required, you would need a second tool.',
      statuses: {
        clear: 'none',
        lattice: 'strong',
        fifteenFive: 'strong',
        leapsome: 'strong',
        cultureAmp: 'strong',
        hiBob: 'confirm',
      },
    },
    {
      criterion: 'Core HR record across countries',
      why: 'Decides whether you are buying a performance layer or a system of record.',
      clearNote: 'Not a CLEAR Talent capability, deliberately: it runs alongside the HRIS or payroll system you already use.',
      statuses: {
        clear: 'none',
        lattice: 'confirm',
        fifteenFive: 'confirm',
        leapsome: 'confirm',
        cultureAmp: 'confirm',
        hiBob: 'strong',
      },
    },
  ],
  clearFit: [
    'Your team spans time zones. Goal check-ins, self-reviews and feedback are written in the platform rather than held in a meeting, and every review stage has its own deadline and automated reminders.',
    '1:1s need a memory. Managers schedule them in CLEAR Talent, each person carries a combined summary and suggested next steps, and coaching agreed in the meeting is kept as a development note that can stay private.',
    'People work in different languages. AI-generated feedback, summaries and recommendations are delivered in 16 languages, including English, Spanish, French, German and Portuguese, in the language each employee prefers.',
    'Fairness to people the manager rarely sees matters. Competency ratings can be configured to require written evidence, and a calibration stage compares ratings across managers and teams before they are final.',
    `Part of the team works from a phone. The native iOS and Android apps carry check-ins, feedback and 1:1 preparation, with push notifications and offline support, from the Pro plan at USD ${PRO_TIER.monthlyUsd} a month.`,
  ],
  clearNotFit: [
    'Your team works almost entirely inside Slack or Microsoft Teams and expects to complete reviews there. There is no CLEAR Talent app for either today, so test the reminder and mobile journey during the trial.',
    'Engagement or pulse surveys are part of this purchase. CLEAR Talent has no survey product.',
    'You want one HR system for the employee record, payroll and time off across countries. CLEAR Talent runs beside that system rather than replacing it.',
    'Every screen, not only the AI-generated content, has to appear in a particular language. Ask to see those screens in your languages in the demo before relying on them.',
    'Activity monitoring is what you are after. CLEAR Talent is built around goals, check-ins, feedback and competency evidence, not screenshots, keystrokes or online status.',
  ],
  pricing: {
    points: pricePointsAt([25, 50, PUBLISHED_STAFF_CAP]),
    aboveCapLabel: `${PUBLISHED_STAFF_CAP + 1}+`,
    summary: `${STARTING_PRICE_SENTENCE} For a distributed team the plan matters as much as the price: the native iOS and Android apps, with push notifications and offline support, start on Pro. At 25 staff users that is ${priceAt(BASIC_TIER, 25)} a month on Basic or ${priceAt(PRO_TIER, 25)} on Pro; at 50, ${priceAt(BASIC_TIER, 50)} or ${priceAt(PRO_TIER, 50)}; at ${PUBLISHED_STAFF_CAP}, ${priceAt(BASIC_TIER, PUBLISHED_STAFF_CAP)} or ${priceAt(PRO_TIER, PUBLISHED_STAFF_CAP)}. Annual billing takes ${ANNUAL_DISCOUNT_PERCENT}% off the plan price, there is a ${TRIAL_POLICY.toLowerCase()}, and past ${PUBLISHED_STAFF_CAP} staff users the Enterprise plan is quoted.`,
    rivals: SEGMENT_GUIDE.pricing.rivals,
    questions: PRICING_QUESTIONS,
  },
  checklist: [
    {
      stage: 'Before the demos',
      items: [
        'List your time zones and languages, and ask every vendor which languages its review screens and AI output support.',
        'Decide how your managers will run 1:1s and check-ins — weekly, fortnightly or monthly — and what has to be written down from each.',
        'Write scenarios every vendor must show live: a check-in on a phone, a 1:1 summarised and followed up, feedback from a colleague in another country, and a calibration session comparing two managers.',
        'Ask for every quote at today’s headcount and your expected headcount in three years.',
      ],
    },
    {
      stage: 'In the demo',
      items: [
        'Complete a self-review on a phone with the connection switched off, then back on.',
        'Ask whether reviews and feedback can be completed inside Slack or Microsoft Teams, or only prompted from there.',
        'Ask what the AI writes, in which languages, whether its output is labelled, and whether it can be switched off.',
        'Put a manager from your most distant time zone in the room.',
      ],
    },
    {
      stage: 'Before you sign',
      items: [
        ...CATEGORY_SIGN_OFF,
        'Confirm how your data comes out — formats, and what happens at the end of the contract.',
        'Run a trial with managers in at least two time zones before rolling out to everyone.',
      ],
    },
  ],
  faqs: [
    {
      q: 'What is the best performance management software for remote teams?',
      a: 'There is no single winner. If a weekly check-in habit is the heart of how your team works, 15Five positions itself around exactly that. If you want 1:1s, reviews and engagement surveys on one people platform, Lattice and Leapsome both present all three. If you employ people in several countries and want the employee record and performance on one HR system, HiBob positions itself there. If you want 1:1s, check-ins and feedback written down where the review will find them, AI-generated summaries in 16 languages and calibration before ratings are final — beside the HRIS you keep — CLEAR Talent is built for that. Whatever you shortlist, test it with managers in more than one time zone.',
    },
    {
      q: 'How do you run performance reviews asynchronously?',
      a: 'Put everything that does not need a live conversation into writing, on a timetable. Self-reviews, 360° input and manager drafts are completed in the tool against fixed stage deadlines, with automated reminders, so nobody has to be online at the same time. The review conversation itself, and any difficult message about underperformance, still belongs on a call, with the written record following it. In CLEAR Talent each stage carries its own deadline and reminders, and on plans that include it Ava drafts the review narrative from the check-ins, feedback and goal scores already recorded, for the manager to edit and approve.',
    },
    {
      q: 'Does CLEAR Talent work inside Slack or Microsoft Teams?',
      a: 'No. There is no CLEAR Talent app for Slack or Microsoft Teams today, so reviews, feedback and approvals are completed in CLEAR Talent on the web or in the native iOS and Android apps. Automated reminders on every review stage and push notifications in the apps are how people are prompted. If your managers work almost entirely inside Slack or Teams, test the reminder and mobile journey with a few of them during the free trial.',
    },
    {
      q: 'How do we stop remote employees being rated lower than office-based colleagues?',
      a: 'Start with the evidence. Proximity bias usually shows up as a thinner record for the people a manager sees least, so ask managers to cite check-in notes, goal updates and feedback for every rating, and treat a thin record as a gap in the process rather than weaker performance. Before ratings are final, compare remote, hybrid and office-based staff in comparable roles during calibration. In CLEAR Talent, competency ratings can be configured to require written evidence, and the calibration stage shows heatmaps and outlier flags across managers and teams before anything is final.',
    },
    {
      q: 'How much does performance management software cost for a remote team?',
      a: `Pricing models differ — a plan fee with per-user add-ons, a per-seat rate, modules sold separately — so compare quotes at your own headcount. CLEAR Talent publishes its plans: at 25 staff users that is ${priceAt(BASIC_TIER, 25)} a month on Basic or ${priceAt(PRO_TIER, 25)} on Pro, and at 50 staff users ${priceAt(BASIC_TIER, 50)} or ${priceAt(PRO_TIER, 50)}, with ${ANNUAL_DISCOUNT_PERCENT}% off the plan price for annual billing. The mobile apps start on Pro, which matters if part of the team works from a phone. Past ${PUBLISHED_STAFF_CAP} staff users the Enterprise plan is quoted.`,
    },
  ],
  sources: [
    { label: 'CLEAR Talent continuous performance management', href: 'https://www.clear-talent.com/solutions/continuous-performance-management' },
    { label: 'CLEAR Talent integrations', href: 'https://www.clear-talent.com/integrations' },
    { label: 'CLEAR Talent pricing', href: 'https://www.clear-talent.com/pricing' },
    { label: 'Lattice', href: 'https://lattice.com/' },
    { label: '15Five', href: 'https://www.15five.com/' },
    { label: 'Leapsome', href: 'https://www.leapsome.com/' },
    { label: 'Culture Amp', href: 'https://www.cultureamp.com/' },
    { label: 'HiBob', href: 'https://www.hibob.com/' },
  ],
  internalLinks: [
    { label: 'Compare six platforms side by side', href: HEAD_TO_HEAD_HUB_PATH },
    SIBLING_GUIDE_LINKS.smallBusiness,
    SIBLING_GUIDE_LINKS.startups,
    SIBLING_GUIDE_LINKS.enterprise,
    { label: 'CLEAR Talent vs Lattice', href: '/clear-talent-vs-lattice' },
    { label: 'CLEAR Talent vs 15Five', href: '/clear-talent-vs-15five' },
    { label: 'CLEAR Talent vs Leapsome', href: '/clear-talent-vs-leapsome' },
    { label: 'CLEAR Talent vs Culture Amp', href: '/clear-talent-vs-culture-amp' },
    { label: 'CLEAR Talent vs HiBob', href: '/clear-talent-vs-hibob' },
    { label: 'Continuous performance management', href: '/solutions/continuous-performance-management' },
    { label: 'Guide: managing remote and hybrid teams', href: '/blog/remote-hybrid-performance-management' },
    { label: 'Glossary: what is continuous feedback?', href: '/glossary/continuous-feedback' },
    { label: 'Integrations: how data gets in and out', href: '/integrations' },
    { label: 'CLEAR Talent pricing', href: '/pricing' },
  ],
};

/** The startup, enterprise and remote-team guides, in the order the hub shows them. */
export const SEGMENT_SIBLING_GUIDES: SegmentGuide[] = [STARTUP_GUIDE, ENTERPRISE_GUIDE, REMOTE_TEAMS_GUIDE];

/** Every guide `pages/SegmentGuidePage.tsx` renders: by kind of company, then by category. */
export const SEGMENT_GUIDES: SegmentGuide[] = [SEGMENT_GUIDE, ...SEGMENT_SIBLING_GUIDES, ...CATEGORY_GUIDES];

export const SEGMENT_GUIDE_BY_PATH: Record<string, SegmentGuide> = Object.fromEntries(
  SEGMENT_GUIDES.map((guide) => [guide.path, guide]),
);

export const SEGMENT_GUIDE_PATHS = SEGMENT_GUIDES.map((guide) => guide.path);

/** Home > Software comparison > this page — the same hub as the head-to-heads. */
export const getSegmentGuideBreadcrumbs = (guide: SegmentGuide = SEGMENT_GUIDE): BreadcrumbItem[] => [
  { name: 'Home', path: '/' },
  { name: 'Software comparison', path: HEAD_TO_HEAD_HUB_PATH },
  { name: guide.breadcrumbName, path: guide.path },
];

/**
 * WebPage + Article + ItemList + FAQPage + BreadcrumbList for one guide — the
 * segment guide or a category roundup. `siteSeo` prepends the Organization,
 * WebSite and SoftwareApplication nodes.
 *
 * The ItemList is the set of tools the page compares, in the order the table
 * shows them, and is declared unordered: the page ranks nobody, so the markup
 * must not imply that it does. Only CLEAR Talent resolves to a product node; the
 * others appear by name only. No `Review`, `AggregateRating` or competitor
 * `Offer` — the same rules as the head-to-heads.
 */
export const getSchemaForSegmentGuide = (guide: SegmentGuide = SEGMENT_GUIDE): HeadToHeadJsonLd[] => {
  const url = `${SITE_ORIGIN}${guide.path}`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: guide.seoTitle,
      description: guide.metaDescription,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': SOFTWARE_ID },
      mainEntity: { '@id': `${url}#tools` },
      inLanguage: 'en',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${url}#article`,
      headline: guide.h1,
      description: guide.excerpt,
      url,
      mainEntityOfPage: { '@id': `${url}#webpage` },
      image: `${SITE_ORIGIN}${guide.heroImage}`,
      datePublished: guide.reviewed.iso,
      dateModified: guide.reviewed.iso,
      author: { '@id': ORG_ID },
      publisher: { '@id': ORG_ID },
      about: { '@id': SOFTWARE_ID },
      inLanguage: 'en',
      articleSection: 'Software comparison',
      keywords: [guide.primaryKeyword, ...guide.keywords],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${url}#tools`,
      url: `${url}#tool-comparison`,
      name: guide.itemListName,
      description:
        'The tools this guide compares, in the order its table lists them. The list is not a ranking.',
      itemListOrder: 'https://schema.org/ItemListUnordered',
      numberOfItems: guide.vendors.length,
      itemListElement: guide.vendors.map((vendor, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: vendor.name,
        ...(vendor.id === 'clear' ? { item: { '@id': SOFTWARE_ID } } : {}),
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: guide.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
    breadcrumbListSchema(getSegmentGuideBreadcrumbs(guide)),
  ];
};
